/**
 * WidevinePlayers.jsx · the Widevine (Shaka) players, moved as is (punchlist Stage 1).
 *
 * Each player below was moved word for word from its page file, together with every helper it uses
 * and every comment. Nothing is combined: each player keeps its own copies. Names that would clash
 * inside this one file carry a Desktop, Tablet or Phone suffix; nothing else changed.
 *
 *   Desktop ← VideoViewPage.jsx (VideoPlayer)
 *   Tablet  ← VideoViewPageTablet.jsx (MobilePlayer)
 *   Phone   ← VideoViewPageMobile.jsx (MobilePlayer)
 *
 * The chooser at the bottom picks the player for the device.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import EndOfLessonCard from './EndOfLessonCard';
import shaka from 'shaka-player/dist/shaka-player.compiled';
import { useAuth } from './AuthContext';
import { useHeartbeat } from './useHeartbeat';
import { isLocked, isLightTheme, tierRank } from './config';
import { cdnTokenSecondsLeft, CDN_TOKEN_VIDEO_MAX_SECONDS } from './useCDNToken';
import { LinkOverlay } from './LinkOverlay';

// play/pause events only REPORT what the video is doing: they copy its real state (paused or not), never the
// event's name. A late event (a 'pause' that arrives while the video plays, or a 'play' while it is paused) then
// changes nothing, so it can't make the play effect send the opposite command (the play/pause loop, October 11).
const reportPlaying = (setIsPlaying) => (e) => setIsPlaying(!e.currentTarget.paused);

// "Tap for sound" shows big and centred for this long after a lesson has to start muted, then shrinks to the
// small corner pill until the sound is back on. Change the number here.
const SOUND_PROMPT_BIG_MS = 4000;

// ════════════════ Desktop player · moved as is from VideoViewPage.jsx (VideoPlayer) ════════════════

const PLAYBACK_SPEEDSDesktop = [0.5, 0.75, 1, 1.25, 1.5, 2];

// Deterministic per-lesson gradient. The API no longer stores gradient colors;
// instead we derive a stable pair from the videoId so every lesson keeps a
// consistent look across sessions without any stored data. Hues are spread
// across the wheel; saturation/lightness kept muted so it sits behind the
// player without clashing with the gold/cream theme. Returns [from, to] hex.
// ── Request guard (link refresh, step 3 · October 15, 2026) ────────────────────────────────────────────────────
// A player must never ask the CDN for anything in a loop. Shaka's own recovery (disable the failing variant for
// 30 s, switch to the next, re-enable, repeat) turns ONE dead link into an endless stream of refused requests,
// paused or not, hidden or not. Two rules stop a player cold:
//   · a request on a link that has already run out is never sent (checked here, before shaka sends it), and
//   · CDN_GUARD_MAX_FAILURES failed CDN requests (any refusal, error or timeout, shaka's retried ones included)
//     inside CDN_GUARD_WINDOW_SECONDS: the player is unloaded and the error screen shows.
// A stopped player holds its spot and starts again from it, by ONE load each time, on: a fresher link arriving
// (useCDNToken keeps one coming), a play press, the browser coming back online, the page becoming visible, and a
// short ladder of timed tries (CDN_GUARD_RETRY_SECONDS; only a link or a press rewinds it, a load that merely
// succeeds does not, so a half-working link cannot cycle). So the most a broken link can cost is a handful of
// loads, never a stream.
// Clock: "run out" is judged by the device clock, with useCDNToken's sanity rule: a link that arrives with less
// than CDN_TOKEN_VIDEO_MAX_SECONDS left means the clock is wrong (or the TTL is short), and then only the CDN's
// answers count, as before this change.
export const CDN_GUARD_MAX_FAILURES = 6;
export const CDN_GUARD_WINDOW_SECONDS = 60;
export const CDN_GUARD_RETRY_SECONDS = [30, 60, 120, 300];
// The step 2 overlay (LinkOverlay.jsx, the same one Safari's player shows): "Welcome back · Reloading your lesson…"
// from the stop until a load has succeeded, then "Resume lesson · Picks up at m:ss"; that tap plays inside the
// gesture, with sound. A lesson that was never played (spot under 1 s) gets no overlay, only the play button, or
// the error screen if its load failed. A load that fails falls through to the error screen (step 2's rule), and
// the next try brings the overlay back. "Reloading…" with nothing left to try (no fresher link, the ladder spent or
// the link run out) becomes the error screen after CDN_GUARD_OVERLAY_WAIT_SECONDS, instead of a spinner forever.
export const CDN_GUARD_OVERLAY_WAIT_SECONDS = 25;
// Has the token of a signed link (its ?t= value) run out? Unreadable tokens are left to the CDN to judge.
export function cdnTokenExpired(t) {
  if (!t) return false;
  const left = cdnTokenSecondsLeft('?t=' + t);
  return left !== null && left <= 0;
}
const freshGuard = () => ({ failures: [], stopped: false, pos: 0, key: null, tries: 0, clockOk: true, playAfter: false, loading: false });

function gradientFor(id) {
  const s = String(id || '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; // djb2
  const hue1 = h % 360;
  const hue2 = (hue1 + 40) % 360;               // nearby hue for a smooth blend
  const hsl = (deg, sat, light) => {
    // minimal HSL→hex (no deps)
    const a = sat * Math.min(light, 1 - light);
    const f = (n) => {
      const k = (n + deg / 30) % 12;
      const c = light - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
      return Math.round(255 * c).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };
  return [hsl(hue1, 0.45, 0.45), hsl(hue2, 0.5, 0.35)];
}

// ============================================================================
// TIER-SPECIFIC STYLE HELPERS
// ============================================================================
const getSkipButtonStylesDesktop = (currentTier, isHovered, isMobile) => {
  const base = { position:'relative', borderRadius:isMobile?'16px':'20px', cursor:'pointer', display:'flex', alignItems:'center', gap:isMobile?'5px':'8px', padding:isMobile?'7px 12px':'12px 20px', transition:'all 0.4s cubic-bezier(0.4,0,0.2,1)', overflow:'hidden', border:'none', fontFamily:'"DM Sans",sans-serif', WebkitTapHighlightColor:'transparent' };
  if (tierRank(currentTier) === 0) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(26,26,26,0.5),rgba(42,42,42,0.45))':'linear-gradient(135deg,rgba(26,26,26,0.4),rgba(42,42,42,0.35))', border:`1px solid ${isHovered?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.2)'}`, boxShadow:isHovered?'0 4px 16px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.2)':'0 2px 8px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.15)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  if (tierRank(currentTier) === 1) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(201,169,98,0.35),rgba(201,169,98,0.25))':'linear-gradient(135deg,rgba(201,169,98,0.25),rgba(201,169,98,0.15))', border:`1px solid ${isHovered?'rgba(201,169,98,0.6)':'rgba(201,169,98,0.4)'}`, boxShadow:isHovered?'0 6px 24px rgba(0,0,0,0.4),0 0 36px rgba(201,169,98,0.4),inset 0 1px 0 rgba(201,169,98,0.5)':'0 3px 12px rgba(0,0,0,0.3),0 0 20px rgba(201,169,98,0.18),inset 0 1px 0 rgba(201,169,98,0.3)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  return { ...base, background:isHovered?'linear-gradient(135deg,rgba(232,213,163,0.4),rgba(232,213,163,0.3))':'linear-gradient(135deg,rgba(232,213,163,0.3),rgba(232,213,163,0.2))', border:`1px solid ${isHovered?'rgba(232,213,163,0.7)':'rgba(232,213,163,0.5)'}`, boxShadow:isHovered?'0 8px 32px rgba(0,0,0,0.5),0 0 44px rgba(232,213,163,0.45),inset 0 1px 0 rgba(232,213,163,0.6)':'0 4px 16px rgba(0,0,0,0.4),0 0 24px rgba(232,213,163,0.24),inset 0 1px 0 rgba(232,213,163,0.4)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
};
const getSkipIconColorDesktop = (t,h) => tierRank(t)===0?'#FFF':tierRank(t)===1?(h?'#E8D5A3':'#C9A962'):(h?'#FFF8E7':'#E8D5A3');
const getSkipNumberColorDesktop = (t) => tierRank(t)===0?'#FFF':tierRank(t)===1?'#C9A962':'#E8D5A3';
const getProgressStylesDesktop = (t) => {
  if (tierRank(t)===0) return { played:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.4)'}, thumb:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.5)'} };
  if (tierRank(t)===1) return { played:{background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 12px rgba(201,169,98,0.5)'}, thumb:{background:'linear-gradient(135deg,#E8D5A3,#C9A962)',boxShadow:'0 0 12px rgba(201,169,98,0.6)'} };
  return { played:{background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 16px rgba(232,213,163,0.6)'}, thumb:{background:'linear-gradient(135deg,#FFF8E7,#E8D5A3)',boxShadow:'0 0 16px rgba(232,213,163,0.7)'} };
};
const getVolumeStyles = (t) => {
  if (tierRank(t)===0) return {background:'#8B8B7A'};
  if (tierRank(t)===1) return {background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 8px rgba(201,169,98,0.4)'};
  return {background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 10px rgba(232,213,163,0.5)'};
};

// ============================================================================
// SKIP BUTTON - Pill-shaped with tier styling
// ============================================================================
function SkipButtonDesktop({ direction, onClick, currentTier, isMobile, seconds=15 }) {
  const [isHovered, setIsHovered] = useState(false);
  const styles = getSkipButtonStylesDesktop(currentTier, isHovered, isMobile);
  const iconColor = getSkipIconColorDesktop(currentTier, isHovered);
  const numberColor = getSkipNumberColorDesktop(currentTier);
  const iconSize = isMobile?13:18, numberSize = isMobile?12:16, unitSize = isMobile?9:12;
  const iconFilter = tierRank(currentTier)===0?'drop-shadow(0 1px 3px rgba(0,0,0,0.5))':tierRank(currentTier)===1?(isHovered?'drop-shadow(0 0 8px rgba(201,169,98,0.5))':'drop-shadow(0 0 4px rgba(201,169,98,0.3))'):(isHovered?'drop-shadow(0 0 12px rgba(232,213,163,0.6))':'drop-shadow(0 0 6px rgba(232,213,163,0.4))');
  return (
    <button onClick={onClick} onMouseEnter={()=>setIsHovered(true)} onMouseLeave={()=>setIsHovered(false)} style={styles}>
      {direction==='back' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M19 12H5M12 5l-7 7 7 7"/></svg>}
      <span style={{display:'flex',alignItems:'baseline',gap:'2px',color:'#F5F5F0',textShadow:isLightTheme(currentTier)?'0 1px 3px rgba(0,0,0,0.5)':'0 1px 2px rgba(0,0,0,0.3)'}}>
        <span style={{fontSize:`${numberSize}px`,fontWeight:'700',color:numberColor,letterSpacing:'-0.02em'}}>{seconds}</span>
        <span style={{fontSize:`${unitSize}px`,fontWeight:'500',opacity:0.8}}>s</span>
      </span>
      {direction==='forward' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
    </button>
  );
}

// ============================================================================
// VIDEO PLAYER COMPONENT
// ============================================================================
// ┌───────────────────────────────────────────────────────────────────────────┐
// │ TEST HOOK — pin playback to ONE rung of the ladder.                       │
// │   ?forceVariant=v0   on the PAGE url → play v0/video.m3u8 directly        │
// │   ?forceVariant=v3                   → play v3/video.m3u8 directly        │
// │   (absent)                           → normal, master.m3u8               │
// │ Remove this function and the useMemo in VideoPlayer to revert.            │
// └───────────────────────────────────────────────────────────────────────────┘
// WHY A URL PARAM AND NOT A CONFIG:
//   In SRC_EQUALS (native HLS) Shaka does NOT choose the variant — Safari does. So
//   abr.restrictions / player.selectVariantTrack() are inert here. The only way to
//   pin a rung is to hand the player a MEDIA playlist instead of the MASTER; a media
//   playlist is directly playable and contains exactly one rendition.
//
// WHAT IT SEPARATES:
//   Every variant is encrypted with the SAME key (verified: one KEYID across v0-v3
//   and audio). So resolution cannot affect decryption.
//     v0 (1280x800) PLAYS  → the key is fine. The failure is the codec ladder —
//                            v3 is avc1.640034 @ 3456x2234@60 (H.264 High L5.2 at a
//                            non-standard size), which a hardware decoder can refuse
//                            outright while Chrome falls back to software.
//     v0 FAILS TOO         → resolution is irrelevant, the key is wrong, and it is
//                            DRMtoday's ingest.
//
// NOTE: a bare variant playlist has NO AUDIO — the master's EXT-X-MEDIA declares the
// audio group. Silent video is EXPECTED here and is not a fault.
//
// The CDN token is prefix-scoped to videos/<slug>, so it already authorizes
// v0/video.m3u8 and its segments. No re-mint needed.
function applyVariantOverrideDesktop(url) {
  if (!url) return url;
  let variant = null;
  try { variant = new URLSearchParams(window.location.search).get('forceVariant'); } catch { return url; }
  if (!variant || !/^[a-z0-9_-]+$/i.test(variant)) return url;
  const out = url.replace(/\/master\.m3u8(\?|$)/, '/' + variant + '/video.m3u8$1');
  return out;
}

// ── legacy FairPlay initData ─────────────────────────────────────────────────
// MEASURED 2026-07-16 via the raw-EME harness: on this Safari (26.3.1), native HLS
// FairPlay fires the LEGACY `webkitneedkey` event, not modern `encrypted` — even
// though requestMediaKeySystemAccess('com.apple.fps') succeeds and setMediaKeys
// attaches. The legacy event's initData is NOT a plain UTF-8 string:
//
//     [uint32 little-endian: byteLength of the URI][the URI as UTF-16LE]
//
// A 55-char skd URI arrives as 114 bytes (4 + 55*2). Read as UTF-8 it looks like
// "nskd://drmtoday?..." — the 'n' is 0x6E = 110, the length byte — and every second
// byte is an invisible NUL. Any regex against that string silently fails to match.
//
// Shaka's polyfill.installAll() normalises this before initDataTransform runs, which
// is why the app has always logged a clean "skd://…". These helpers exist so the
// transform can handle EITHER shape and, crucially, RETURN THE SHAPE IT RECEIVED.
function parseSkdInitDataDesktop(initData) {
  const u8 = initData instanceof Uint8Array ? initData : new Uint8Array(initData);
  if (u8.byteLength >= 6) {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const byteLen = dv.getUint32(0, true);
    if (byteLen + 4 === u8.byteLength && byteLen % 2 === 0) {
      const uri = new TextDecoder('utf-16le').decode(new Uint8Array(u8.buffer, u8.byteOffset + 4, byteLen));
      if (uri.startsWith('skd:')) return { uri, shape: 'legacy_len_utf16le' };
    }
  }
  return { uri: shaka.util.StringUtils.fromBytesAutoDetect(initData), shape: 'plain' };
}

function VideoPlayer({ upNext = null, upNextLocked = false, onUpNext, onUnlock, endCard = null, video, colors, currentTier, isPlaying, setIsPlaying, currentTime, setCurrentTime, videoUrl: rawVideoUrl, videoId, assetId, resumeFrom = 0, isMobile, isTablet, refreshLink }) {
  // Shadows the prop, so every downstream use (isHls, videoUrlKey, load(), the token
  // effect) picks this up unchanged. Without ?forceVariant this is a pure passthrough.
  const videoUrl = useMemo(() => applyVariantOverrideDesktop(rawVideoUrl), [rawVideoUrl]);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const volumeRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const shakaRef = useRef(null);
  const cdnTokenRef = useRef(null);   // live CDN token, re-appended to every shaka request (refreshable)
  const guardRef = useRef(freshGuard());   // request guard (see the top of the file)
  const restartRef = useRef(null);         // restartFromLink, for the effects declared above it
  const [stopped, setStopped] = useState(0);   // bumps on every stop and restart, so the recovery effect re-reads the guard
  const [linkOverlay, setLinkOverlay] = useState(null);   // null | 'loading' | 'resuming' | 'ready' (step 2's overlay; 'resuming' = the Resume tap's own reload)
  const resumingRef = useRef(false);   // the Resume tap's reload is in progress: its own play() must not close the overlay
  const { token, profile, api } = useAuth();
  // Read live inside the Shaka effect instead of being a DEPENDENCY of it. A new JWT
  // (from a refresh) must never tear down and rebuild a player that is happily playing.
  const authTokenRef = useRef(token);
  authTokenRef.current = token;
  // The full signed URL, live. The effect keys on the token-STRIPPED url (see
  // videoUrlKey) so a re-mint of the same video cannot retrigger it, but load()
  // still needs the real, current, signed URL — that comes from here.
  const videoUrlRef = useRef(videoUrl);
  videoUrlRef.current = videoUrl;
  // Content API base — same env var api.js uses (falls back to the User Server origin).
  const API_CONTENT_BASE = process.env.REACT_APP_CONTENT_API_URL || process.env.REACT_APP_API_URL || '';
  // DRMtoday org/merchant UUID — public (also in the JWT). Used for the FairPlay cert URL.
  const DRMTODAY_MERCHANT = process.env.REACT_APP_DRMTODAY_MERCHANT || 'f100090a-e116-42ba-b03a-1d157c6be3f3';
  const latestRef = useRef({ pos: 0, dur: 0 });
  const resumedRef = useRef(false);
  const resumeFromRef = useRef(resumeFrom);   // resume position captured at mount
  // Heartbeats (the 10-second send while playing, and the final send on unmount) live in useHeartbeat.
  const heartbeatOnTime = useHeartbeat(videoId);
  const [showControls, setShowControls] = useState(true);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Saved speed (1.22): start from the profile's preferredPlaybackSpeed when it is one of our speeds, else 1×.
  const [playbackSpeed, setPlaybackSpeed] = useState(() => { const s = Number(profile && profile.preferredPlaybackSpeed); return PLAYBACK_SPEEDSDesktop.includes(s) ? s : 1; });
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(!!videoUrl);
  const [error, setError] = useState(null);
  // Debounced version of `error` used for the overlay. A transient/recoverable error
  // during DRM/MSE startup can set `error` for a few hundred ms before the video is
  // ready and clears it — showing the overlay immediately caused a purple flash on every
  // load. We only reveal the overlay if the error is STILL set after a short grace period.
  const [showError, setShowError] = useState(false);
  useEffect(() => {
    if (!error) { setShowError(false); return; }
    const t = setTimeout(() => setShowError(true), 600); // grace period for self-healing
    return () => clearTimeout(t);
  }, [error]);

  const isHls = videoUrl && /\.m3u8($|\?)/.test(videoUrl);
  // ── THE FIX FOR LOAD_INTERRUPTED ──────────────────────────────────────────
  // urlSigner.mintPlaybackUrl mints a FRESH token (new exp, new sig) on every
  // /video/:id call. So `videoUrl` is a DIFFERENT STRING each fetch even for the
  // same video. With `videoUrl` as an effect dep, the second fetch (triggered when
  // `catalog` flips from placeholder to real) tore down a player that was still
  // inside load() → shaka error 7000 LOAD_INTERRUPTED → the stale player's catch
  // set "Failed to load video" over a video that had loaded fine.
  //
  // Keying on the path alone makes the effect identity stable across re-mints.
  // The live token reaches the player via cdnTokenRef (below), no teardown needed —
  // which is also the seam a real token-refresh will use.
  const videoUrlKey = videoUrl ? String(videoUrl).split('?')[0] : null;

  // Keep the CDN token current WITHOUT restarting playback.
  useEffect(() => {
    try { cdnTokenRef.current = new URL(videoUrl).searchParams.get('t'); }
    catch { cdnTokenRef.current = null; }
    // Request guard: the clock check (useCDNToken's rule) on the link that just arrived, then a fresh start if
    // the player is stopped. A fresh link resets the timed ladder.
    const guard = guardRef.current;
    const left = cdnTokenSecondsLeft(videoUrl);
    guard.clockOk = left === null || left >= CDN_TOKEN_VIDEO_MAX_SECONDS;
    if (guard.stopped) { guard.tries = 0; if (restartRef.current) restartRef.current(); }
  }, [videoUrl]);
  const totalSeconds = duration || (34*60+12);
  // Desktop timeline (1.26): hover shows the time under the pointer; press and drag moves the thumb
  // and seeks once, on release. A plain click still goes through handleProgressClick.
  const [hoverTime, setHoverTime] = useState(null);
  const [dragTime, setDragTime] = useState(null);
  const dragRef = useRef({ active: false, moved: false, x: 0, justEnded: false });
  // While dragging the timeline (1.26), the played bar and thumb follow the pointer.
  const progressPercent = ((dragTime ?? currentTime)/totalSeconds)*100;
  const bufferedPercent = duration>0?(buffered/duration)*100:60;
  const skipAmount = isMobile?10:15;
  const pStyles = getProgressStylesDesktop(currentTier);
  const vStyles = getVolumeStyles(currentTier);

  // -- Shaka Player setup (DRM: Widevine + FairPlay via DRMtoday through our license endpoint) --
  useEffect(() => {
    if (!videoUrl || !videoRef.current) return;
    const vid = videoRef.current;

    // tear down any prior instance
    if (shakaRef.current) { shakaRef.current.destroy(); shakaRef.current = null; }
    if (guardRef.current.stopped) setStopped((n) => n + 1);   // request guard: the old lesson's recovery stands down
    guardRef.current = { ...freshGuard(), clockOk: guardRef.current.clockOk };   // request guard: a new player, a clean slate

    // Non-DRM / non-HLS source (e.g. a plain mp4 preview): just set src.
    if (!isHls) { vid.src = videoUrl; return; }

    shaka.polyfill.installAll();

    // ── MSE, NOT SAFARI'S NATIVE HLS ─────────────────────────────────────────
    // Shaka's README: "By default, FairPlay is handled using Apple's native HLS player,
    // when on Safari. We do support FairPlay through MSE/EME, however. See the
    // streaming.useNativeHlsForFairPlay configuration value."
    //
    // Shaka's FairPlay support matrix:
    //                    Modern EME   legacy Apple Media Keys
    //     src= (CMAF)    Y            Y
    //     MSE  (CMAF)    Y            -            ← MSE requires MODERN EME
    // Our content is CMAF (fMP4 .m4s), so MSE + com.apple.fps is a supported combo.
    //
    // WHY WE LEFT THE NATIVE HLS PATH — everything below was measured on it:
    //   1. Modern EME + native HLS: license 200, CKC accepted, ZERO frames, no error.
    //      Safari's NATIVE setMediaKeys() does not enable FairPlay decryption for
    //      native HLS; only webkitSetMediaKeys() does (shaka-player#9794).
    //   2. legacy Apple Media Keys + the legacy container: video DECRYPTS and renders.
    //      But the packager gives audio and video the SAME skd content-id
    //      (--hls_key_uri is a single global flag — shaka-packager#439/#470), so both
    //      raise webkitneedkey with identical init data, Shaka dedupes the second, and
    //      audio is never keyed. Safari's clock is AUDIO-driven → currentTime pinned at
    //      0.00 with 12s of video decoded → frozen first frame.
    //   3. Forcing a session per event → 6006: the CDM rejects a second session for a
    //      content-id it already holds.
    //   4. Giving audio a distinct content-id (Worker appends &trk=audio; DRMtoday
    //      ignores unknown params — measured 200) → TWO licenses, both 200, both
    //      sessions updated → STILL FROZEN.
    //
    // (4) is shaka-player#9268: "FairPlay multi-key content intermittently fails to
    // play (3016)... Shaka requests a license for only one of the two tracks... the
    // same assets play correctly in Video.js and other HTML5 players." Open, unfixed.
    // The native-HLS FairPlay path is broken for multi-track content.
    //
    // MSE sidesteps ALL of it. Shaka parses the manifest, fetches the segments, and
    // drives EME itself — exactly the path Widevine already uses successfully here.
    // No webkitneedkey, no session-per-track, no dedupe, no Safari native-HLS quirks.
    // Modern EME is required for MSE, so no PatchedMediaKeysApple.
    const FPS_KEYSYSTEM = 'com.apple.fps';

    if (!shaka.Player.isBrowserSupported()) { setError('This browser can’t play protected video'); return; }

    // The signed CDN needs ?t=<token> on EVERY request (manifest + segments). Shaka resolves
    // child playlists/segments relative to the manifest, dropping the query — so a request
    // filter re-appends the token (read live from a ref so a refresh can swap it).
    // cdnTokenRef is kept live by its own effect above — reading it here would race
    // with a re-mint and pin a stale token for the life of the player.

    const player = new shaka.Player();
    shakaRef.current = player;

    const licenseBase = (API_CONTENT_BASE || '').replace(/\/$/, '');
    const widevineLicense = `${licenseBase}/license/widevine?assetId=${encodeURIComponent(assetId || '')}`;
    // FairPlay (Safari/iOS) goes to our endpoint too; the route forwards to DRMtoday's
    // FairPlay license server and mints the same proven auth token server-side.
    const fairplayLicense = `${licenseBase}/license/fairplay?assetId=${encodeURIComponent(assetId || '')}`;
    // DRMtoday's FairPlay CERTIFICATE endpoint (public; keyed by merchant). Shaka fetches
    // this before it can build the SPC. Confirmed from DRMtoday integration docs.
    // ── FairPlay certificate ─────────────────────────────────────────────────
    // MEASURED 2026-07-16: the cert served at /cert/<merchant> is RSA-1024
    // (modulus 0x02 0x81 0x81 → 128 bytes), serial 2777B10FB9358DC3, notBefore
    // 2026-07-13. RSA-1024 is the OLD FPS generation; SDK 4.0+ certificates are
    // RSA-2048. The comment above says we need SDK 26 credentials — so the default
    // being served may be the wrong bundle entirely.
    //
    // WHY THAT PRODUCES EXACTLY OUR SYMPTOM:
    //   Apple ships certificate + private key + ASk as ONE bundle. Our HTTP 200
    //   proves the cert/private-key pair matches — the KSM could not decrypt the SPC
    //   otherwise. It proves NOTHING about the ASk: the ASk never touches SPC
    //   decryption, nothing validates it, and a mismatched one yields a well-formed,
    //   correctly-signed CKC carrying a GARBAGE content key. The CDM accepts it
    //   (session.update resolves), then decodes noise → MEDIA_ERR_DECODE, zero frames,
    //   no error. Which is what we have.
    //
    // TESTING ANOTHER BUNDLE WITHOUT TOUCHING THE DASHBOARD:
    //   The SPC carries a hash identifying which certificate the client used, so a KSM
    //   holding several bundles picks the matching private key + ASk from it. Swapping
    //   the cert here therefore swaps which bundle DRMtoday answers with — no default
    //   change, no risk to other sessions.
    //
    //   Drop the .cer files (DER, as Apple issued them) into public/ — same-origin, so
    //   no CORS — then:
    //       ?certUrl=/fps-2048.cer      ?certUrl=/fps-1024.cer      (no param = default)
    //   Whichever one PLAYS is the bundle whose ASk DRMtoday actually holds.
    //
    //   FairPlay certificates have NOTHING to do with Widevine — different DRM,
    //   different license server, no Apple cert in that path. This cannot affect Chrome.
    // HOW TO USE — from the Safari console, no URL, no file, no deploy of the cert:
    //
    //   1. terminal:   base64 -i cert_2.der | tr -d '\n' | pbcopy
    //   2. console:    sessionStorage.setItem('fpsCertB64', '<paste>'); location.reload()
    //   3. to revert:  sessionStorage.removeItem('fpsCert64'); location.reload()
    //
    // sessionStorage survives the reload and dies with the tab. The certificate is
    // PUBLIC — every Safari downloads it before playback, and an unauthenticated curl
    // fetches it — so this leaks nothing. The private key and ASk are never involved.
    //
    // ?certUrl=<url> also works if you'd rather point at a hosted .cer.
    let certOverride = null;
    try {
      const b64 = window.sessionStorage.getItem('fpsCertB64');
      if (b64) { certOverride = 'data:application/octet-stream;base64,' + b64.trim(); }
      else {
        const u = new URLSearchParams(window.location.search).get('certUrl');
        if (u) { certOverride = u; }
      }
    } catch { /* fall through to the default */ }
    const DRMTODAY_FPS_CERT = certOverride || `https://lic.drmtoday.com/license-server-fairplay/cert/${DRMTODAY_MERCHANT}`;

    player.configure({
      drm: {
        // TRIED AND REVERTED 2026-07-17: ignoreDuplicateInitData:false.
        // Reasoning was that initDataTransform fires twice (video + audio) but only one
        // license request goes out, so the second track never gets keyed. Forcing a
        // session per webkitneedkey instead produced
        //   6006 FAILED_TO_GENERATE_LICENSE_REQUEST — "EME PatchedMediaKeysApple key error"
        // i.e. the CDM rejects a SECOND session for the same content-id outright. So the
        // duplicate suppression is correct here and the stall has another cause.
        servers: {
          'com.widevine.alpha': widevineLicense,
          // FPS_KEYSYSTEM is 'com.apple.fps.1_0' — legacy Apple Media Keys. See the
          // block above for why. (Was 'com.apple.fps' / Modern EME, which never
          // enabled decryption for native HLS.)
          [FPS_KEYSYSTEM]: fairplayLicense,
        },
        advanced: {
          'com.widevine.alpha': { persistentStateRequired: false },
          [FPS_KEYSYSTEM]: {
            persistentStateRequired: false,
            distinctiveIdentifierRequired: false,
            serverCertificateUri: DRMTODAY_FPS_CERT,
          },
        },
      },
      streaming: {
        // THE CHANGE. Default is true → Safari's native HLS player → every FairPlay
        // bug we spent this investigation on. false → Shaka drives MSE/EME itself.
        //
        // Side effect worth knowing: in MSE mode SHAKA fetches the segments, so the
        // request filter below re-appends ?t= to each one. The Worker's manifest
        // rewrite becomes belt-and-braces rather than load-bearing (it only mattered
        // because NATIVE players drop the query when resolving relative URLs).
        useNativeHlsForFairPlay: false,
        rebufferingGoal: 4,
        bufferingGoal: 30,
      },
    });

    // ── initDataTransform — MODERN EME ───────────────────────────────────────
    // Modern EME takes the content-id as plain bytes. It must NOT be wrapped in
    // Apple's legacy container ([len][initData][len][contentId UTF-16][len][cert]) —
    // that is what legacy Apple Media Keys requires, and feeding it to Modern EME
    // produced SPC_CONTAINER_HEADER_MALFORMED. The container code is gone with the
    // legacy path; if you ever go back to com.apple.fps.1_0, it must come back too.
    //
    // CONTENT-ID: measured against DRMtoday, only one form returns 200:
    //     skd://drmtoday?assetId=X&variantId=hd  → 400 (x-dt-resp-code 10003, 10001)
    //     skd://drmtoday?assetId=X               → 200   ← this one
    //     ?assetId=X&variantId=hd                → 400
    //     ?assetId=X                             → 400
    // The keys were ingested under assetId alone, so we strip the variantId the
    // packager wrote. DRMtoday ignores unknown params (measured), so the Worker's
    // &trk=audio on the audio playlist survives this strip harmlessly — [^&]* stops
    // at the next '&'.
    player.configure('drm.initDataTransform', (initData, initDataType) => {
      if (initDataType !== 'skd') return initData;
      const { uri } = parseSkdInitDataDesktop(initData);
      const contentId = uri.replace(/[?&]variantId=[^&]*/i, '');
      return shaka.util.StringUtils.toUTF8(contentId);
    });

    // Attach auth + DRM-system-specific request/response handling.
    const net = player.getNetworkingEngine();
    net.registerRequestFilter((type, request) => {
      const RT = shaka.net.NetworkingEngine.RequestType;
      if (type === RT.LICENSE) {
        // license call goes to OUR content-api → attach the user's bearer token so the
        // endpoint can check entitlement before minting the DRMtoday token.
        // Read from the ref, NOT the closure: a refreshed JWT must reach the next
        // license request without the effect (and the player) being rebuilt.
        const authToken = authTokenRef.current;
        if (authToken) request.headers['Authorization'] = `Bearer ${authToken}`;
        // FairPlay: the CDM message must be sent as spc=<base64> (form-urlencoded).
        const isFairPlay = request.uris.some((u) => u.indexOf('/license/fairplay') > -1);
        if (isFairPlay && request.body) {
          const payload = new Uint8Array(request.body);
          const b64 = shaka.util.Uint8ArrayUtils.toStandardBase64(payload);
          request.headers['Content-Type'] = 'application/x-www-form-urlencoded';
          request.body = shaka.util.StringUtils.toUTF8('spc=' + encodeURIComponent(b64));
        }
      } else {
        // manifest + segments → CDN, re-append ?t=<token>
        const t = cdnTokenRef.current;
        // Request guard: a link that has run out is never sent (the CDN would only refuse it), nor anything once
        // the player is stopped. Shaka turns the throw into a failed request (not retried); during a load() that
        // rejects the load, during playback it reaches the error listener below.
        if (guardRef.current.stopped || (guardRef.current.clockOk && cdnTokenExpired(t))) throw new Error('cdn link expired');
        if (t) {
          request.uris = request.uris.map((u) => {
            try { const url = new URL(u); url.searchParams.set('t', t); return url.toString(); }
            catch { return u; }
          });
        }
      }
    });

    // FairPlay response: DRMtoday returns the CKC (sometimes wrapped). Unwrap it into the
    // raw base64 the CDM expects. Widevine responses are handled by our endpoint (raw bytes).
    net.registerResponseFilter((type, response) => {
      const RT = shaka.net.NetworkingEngine.RequestType;
      if (type !== RT.LICENSE) return;
      const isFairPlay = response.uri && response.uri.indexOf('/license/fairplay') > -1;
      if (!isFairPlay) return;
      let txt = shaka.util.StringUtils.fromUTF8(response.data).trim();
      // strip a <ckc>…</ckc> wrapper if present
      const wrapped = txt.startsWith('<ckc>') && txt.endsWith('</ckc>');
      if (wrapped) txt = txt.slice(5, -6);
      // A throw here propagates as a load failure, which is the correct outcome:
      // a CKC that isn't base64 is not something the CDM can be handed.
      response.data = shaka.util.Uint8ArrayUtils.fromBase64(txt).buffer;
    });

    // Request guard: every failed CDN request counts, the ones shaka means to retry included (it reports those
    // as recoverable). Past the budget, or on a link that has run out, the player is unloaded and holds its spot;
    // restartFromLink (below) is the only way on from there.
    const noteFailedRequest = () => {
      const guard = guardRef.current;
      const now = Date.now();
      guard.failures = guard.failures.filter((at) => now - at < CDN_GUARD_WINDOW_SECONDS * 1000);
      guard.failures.push(now);
      const expired = guard.clockOk && cdnTokenExpired(cdnTokenRef.current);
      if (!expired && guard.failures.length < CDN_GUARD_MAX_FAILURES) return false;
      if (guard.stopped) return true;
      const cur = (videoRef.current && videoRef.current.currentTime) || 0;
      guard.pos = cur > 0 ? cur : (latestRef.current.pos > 0 ? latestRef.current.pos : guard.pos);   // the spot survives a stop during a restart
      guard.key = videoUrlKey;
      guard.stopped = true;
      guard.failures = [];
      guard.loading = false;   // unload() interrupts any load in flight (it rejects with 7000, which is ignored)
      player.unload().catch(() => {});
      setIsPlaying(false);   // unload() pauses the element without a pause event: the page must know
      // The overlay is the catch-all (October 30, 2026): with a spot to come back to, a stop never shows the error
      // screen. A dead link has nothing the player can try, so it is "Resume lesson" at once; a live link reloads
      // ("Reloading…") and the watchdog falls back to "Resume lesson". The tap refreshes the session, the link and
      // the video itself; only a tap that fails shows the error screen.
      setError(null); setLinkOverlay(expired ? 'ready' : 'loading');   // never the error screen: the overlay, and the tap recovers
      setIsLoading(false);
      setStopped((n) => n + 1);
      return true;
    };
    player.addEventListener('error', (e) => {
      const detail = e && e.detail ? e.detail : e;

      // LOAD_INTERRUPTED means WE cancelled the load (teardown/replacement). It is
      // bookkeeping, not a fault, and must never reach the user.
      if (detail && detail.code === 7000) return;
      if (detail && detail.category === shaka.util.Error.Category.NETWORK && noteFailedRequest()) return;

      // Shaka fires this for BOTH recoverable (severity 1) and critical (severity 2)
      // errors. Recoverable ones self-heal (buffering, a retried segment) and must NOT
      // put up the failure overlay — doing so left a stuck "Failed to load" over a video
      // that was actually playing. Only surface CRITICAL errors.
      const severity = detail && detail.severity;
      if (severity && severity !== shaka.util.Error.Severity.CRITICAL) return;
      const code = detail && detail.code;
      if (code === 403 || code === 6007 || code === 6001) setError('You don’t have access to this video');
      else {
        // The overlay, not the error screen, wherever there is a spot to come back to (October 30, 2026): the tap
        // reloads the video from a fresh session token and a fresh link.
        const v = videoRef.current; const at = (v && v.currentTime > 0) ? v.currentTime : (latestRef.current.pos || 0);
        guardRef.current.pos = at; setIsPlaying(false); setLinkOverlay('ready');
      }
      setIsLoading(false);
    });

    // Guards every async continuation below. Without this, a player torn down
    // mid-load still runs its catch and calls setError() — clobbering the player
    // that REPLACED it. The fetch effect has always had this; this one did not.
    let cancelled = false;

    (async () => {
      try {
        await player.attach(vid);
        if (cancelled) return;
        await player.load(videoUrlRef.current, resumeFromRef.current > 0 ? resumeFromRef.current : undefined);
        if (cancelled) return;

        setError(null);        // success → clear any stale/recoverable error state
        setIsLoading(false);
      } catch (err) {
        // A superseded player is EXPECTED to reject. It is not a user-visible failure
        // and must not touch shared state — the player that replaced it owns that now.
        if (cancelled || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        const code = err && err.code;
        const spot = resumeFromRef.current > 0 ? resumeFromRef.current : 0;
        if (code === 403 || code === 6007 || code === 6001) setError('You don’t have access to this video');
        else { guardRef.current.pos = spot; setLinkOverlay('ready'); }   // the overlay, never the error screen (October 30, 2026)
        setIsLoading(false);
        // Request guard: a load that failed on the network (a refused manifest, a dead link) is a stopped player
        // too: it holds the resume spot and tries again on a link, a gesture or the ladder, never on its own.
        if (err && err.category === shaka.util.Error.Category.NETWORK) {
          const guard = guardRef.current;
          guard.stopped = true; guard.key = videoUrlKey; guard.pos = spot;
          const dead = guard.clockOk && cdnTokenExpired(cdnTokenRef.current);
          setError(null); setLinkOverlay(dead ? 'ready' : 'loading');
          setStopped((n) => n + 1);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (shakaRef.current) { shakaRef.current.destroy(); shakaRef.current = null; }
    };
  }, [videoUrlKey, isHls, assetId, API_CONTENT_BASE]);

  // -- YouTube-style controls --
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if(hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if(isPlaying) hideTimeoutRef.current = setTimeout(()=>setShowControls(false),3000);
  }, [isPlaying]);

  useEffect(() => {
    if(!isPlaying){setShowControls(true);if(hideTimeoutRef.current)clearTimeout(hideTimeoutRef.current);}
    else hideTimeoutRef.current=setTimeout(()=>setShowControls(false),3000);
    return ()=>{if(hideTimeoutRef.current)clearTimeout(hideTimeoutRef.current);};
  }, [isPlaying]);

  const handleVideoAreaClick = useCallback((e) => {
    if(e.target.closest('[data-controls]')) return;
    if(isMobile){if(showControls){setShowControls(false);if(hideTimeoutRef.current)clearTimeout(hideTimeoutRef.current);}else showControlsTemporarily();}
    else{setIsPlaying(!isPlaying);showControlsTemporarily();}
  }, [isMobile, isPlaying, showControls, showControlsTemporarily, setIsPlaying]);

  const handleMouseMove = useCallback(()=>{if(!isMobile)showControlsTemporarily();},[isMobile,showControlsTemporarily]);
  const handleMouseLeave = useCallback(()=>{if(!isMobile&&isPlaying){if(hideTimeoutRef.current)clearTimeout(hideTimeoutRef.current);hideTimeoutRef.current=setTimeout(()=>setShowControls(false),1000);}},[isMobile,isPlaying]);

  // -- Play/pause with muted fallback --
  // A rejected play() falls back to a muted retry (autoplay policy); if that also
  // fails the intent is cleared so the UI returns to a play button.
  // ── Starting playback (fix: "stuck after the next lesson", "the next lesson is muted") ─────────────────
  // A refused autoplay and an interrupted play are different things:
  //   NotAllowedError · the browser refuses sound without a tap (always so on iOS after an automatic lesson change).
  //     Start muted, which every browser allows, but only temporarily: "Tap for sound" shows, and the first tap,
  //     click or key press anywhere restores the sound, inside that tap (what iOS requires).
  //   anything else (AbortError: interrupted, e.g. by the stream still loading or a late FairPlay key) · never mute;
  //     show paused at once, with the play button; the student taps again (October 11: the retries are removed).
  const [autoMuted, setAutoMuted] = useState(false);
  useEffect(() => {
    if(videoUrl&&videoRef.current){
      if(isPlaying){
        let cancelled = false;
        const attempt = () => {
          const v = videoRef.current; if (cancelled || !v) return;
          const pr = v.play(); if (!pr || !pr.catch) return;
          pr.catch((err) => {
            if (cancelled || !videoRef.current) return;
            if (err && err.name === 'NotAllowedError') {
              if (videoRef.current.muted) { setIsPlaying(false); return; }   // refused even muted: show paused
              videoRef.current.muted = true; setIsMuted(true); setAutoMuted(true);
              const p2 = videoRef.current.play(); if (p2 && p2.catch) p2.catch(() => { if (!cancelled) setIsPlaying(false); });
            } else setIsPlaying(false);   // AbortError (interrupted): no retry; show paused, never mute
          });
        };
        attempt();
        return () => { cancelled = true; };
      }
      else videoRef.current.pause();
    }
    return undefined;
  }, [isPlaying, videoUrl, setIsPlaying]);
  useEffect(() => {
    if (!autoMuted) return undefined;
    // a tap on the player itself only restores the sound (it must not also pause or play); elsewhere it carries on.
    // M also means "restore the sound" here: the player's own M toggle would otherwise flip it straight back to muted.
    const restore = (e) => { const v = videoRef.current; if (v) v.muted = false; setIsMuted(false); setAutoMuted(false); if (e && e.type !== 'keydown' && containerRef.current && e.target && containerRef.current.contains(e.target)) { e.stopPropagation(); e.preventDefault(); } if (e && e.type === 'keydown' && (e.key === 'm' || e.key === 'M')) { e.stopImmediatePropagation(); e.preventDefault(); } };
    const o = { capture: true };
    window.addEventListener('touchend', restore, o); window.addEventListener('click', restore, o); window.addEventListener('keydown', restore, o);
    return () => { window.removeEventListener('touchend', restore, o); window.removeEventListener('click', restore, o); window.removeEventListener('keydown', restore, o); };
  }, [autoMuted]);
  useEffect(() => { if (!isMuted) setAutoMuted(false); }, [isMuted]);
  // "Tap for sound": big and centred for SOUND_PROMPT_BIG_MS, then the small corner pill until the sound is on.
  // Display only: a tap anywhere restores the sound either way (the effect above).
  const [soundPromptBig, setSoundPromptBig] = useState(false);
  useEffect(() => {
    if (!autoMuted) { setSoundPromptBig(false); return undefined; }
    setSoundPromptBig(true);
    const t = setTimeout(() => setSoundPromptBig(false), SOUND_PROMPT_BIG_MS);
    return () => clearTimeout(t);
  }, [autoMuted]);

  useEffect(()=>{if(videoRef.current)videoRef.current.volume=isMuted?0:volume/100;},[volume,isMuted]);
  // Media ready: counts each time the media reports its metadata (first load, a new lesson). The
  // speed and the subtitle tracks apply from this, because the <video> may not exist yet when the
  // player first appears, and isLoading can stay false if the player mounts before its URL.
  const [mediaReady, setMediaReady] = useState(0);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onReady = () => setMediaReady((n) => n + 1);
    v.addEventListener('loadedmetadata', onReady);
    return () => v.removeEventListener('loadedmetadata', onReady);
  }, [videoUrl]);

  // ── End of lesson (1.23) ──────────────────────────────────────────────────
  // The page passes upNext / upNextLocked / onUpNext / onUnlock to the LESSON player only, so the
  // answer-key player never shows the card. The video's end is detected by these new listeners;
  // the existing end handler is untouched.
  const endCardEnabled = typeof onUpNext === 'function' || typeof onUnlock === 'function';
  const [ended, setEnded] = useState(false);
  const [nativeFs, setNativeFs] = useState(false);     // iPhone's own full-screen player hides our card
  useEffect(() => {
    setEnded(false);
    const v = videoRef.current;
    if (!v) return undefined;
    const onEnd = () => setEnded(true);
    const onAgain = () => setEnded(false);
    v.addEventListener('ended', onEnd); v.addEventListener('play', onAgain); v.addEventListener('seeking', onAgain);
    return () => { v.removeEventListener('ended', onEnd); v.removeEventListener('play', onAgain); v.removeEventListener('seeking', onAgain); };
  }, [videoUrlKey]);   // a new video, not a new link (link refresh): a fresh link must not reset the end card
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const on = () => setNativeFs(true); const off = () => setNativeFs(false);
    el.addEventListener('webkitbeginfullscreen', on); el.addEventListener('webkitendfullscreen', off);
    return () => { el.removeEventListener('webkitbeginfullscreen', on); el.removeEventListener('webkitendfullscreen', off); };
  }, [videoUrl]);
  // No automatic advance: the next lesson starts only when the student taps "Play next lesson" (or Up Next),
  // so it always starts from a tap, the way every browser (iPad and iPhone included) lets a new video start.
  const replay = () => {
    setEnded(false);
    const v = videoRef.current;
    if (v) { v.currentTime = 0; setCurrentTime(0); }
    setIsPlaying(true);
  };
  useEffect(()=>{if(videoRef.current)videoRef.current.playbackRate=playbackSpeed;},[playbackSpeed, mediaReady]);   // also once the media is ready: a speed chosen before it existed (e.g. a saved one) must still apply

  // ── Subtitles (1.21) ──────────────────────────────────────────────────────
  // Tracks come from the lesson data: video.subtitles = [{ lang, label, url, kind }], kind
  // 'subtitles' or 'captions' (English (CC) keeps its own key, 'en-cc'). With none, the
  // Subtitles control doesn't appear. Tracks are added once per player instance, after
  // the media is ready (mediaReady), by the effect below; never inside the load effect above.
  const subtitleOptions = useMemo(() => (Array.isArray(video && video.subtitles) ? video.subtitles : [])
    .filter((s) => s && s.url && s.lang)
    .map((s) => ({ ...s, kind: s.kind === 'captions' ? 'captions' : 'subtitles', key: s.kind === 'captions' ? s.lang + '-cc' : s.lang })), [video]);
  // Saved subtitles (1.22): start from the profile's preferredSubtitles, else Off. A lesson without
  // that language shows Off (activeSubtitleKey) without changing the saved value.
  const [subtitleKey, setSubtitleKey] = useState(() => (profile && typeof profile.preferredSubtitles === 'string' ? profile.preferredSubtitles : 'off'));
  const activeSubtitleKey = subtitleOptions.some((s) => s.key === subtitleKey) ? subtitleKey : 'off';
  // Saving (1.22) switches itself on: it only saves a field the profile already has (the sign the
  // backend supports it), so no request goes to an endpoint that doesn't exist yet. The in-memory
  // profile is updated too, so the next lesson starts with the choice.
  const savePreference = (field, value) => {
    if (!api || !profile || !Object.prototype.hasOwnProperty.call(profile, field)) return;
    if (profile[field] === value) return;   // unchanged: nothing to save (also keeps React's double-run in development quiet)
    profile[field] = value;
    try {
      const req = api.fetch('/profile/preferences', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      if (req && req.catch) req.catch(() => { /* a failed save changes nothing on screen */ });
    } catch (err) { /* a failed save changes nothing on screen */ }
  };
  const prefsReadyRef = useRef(false);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredPlaybackSpeed', playbackSpeed); }, [playbackSpeed]);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredSubtitles', subtitleKey); }, [subtitleKey]);
  useEffect(() => { prefsReadyRef.current = true; }, []);
  const [tracksReady, setTracksReady] = useState(0);
  const subsAddedFor = useRef(null);
  useEffect(() => {
    const player = shakaRef.current;
    if (!mediaReady || !player || !subtitleOptions.length || subsAddedFor.current === player) return;
    subsAddedFor.current = player;
    (async () => {
      for (const s of subtitleOptions) {
        try { await player.addTextTrackAsync(s.url, s.lang, s.kind === 'captions' ? 'caption' : 'subtitle', 'text/vtt', undefined, s.label); }
        catch (err) { /* one missing file must not block the others */ }
        if (shakaRef.current !== player) return;   // replaced by a new lesson's player
      }
      setTracksReady((n) => n + 1);
    })();
  }, [mediaReady, subtitleOptions]);
  // ── Request guard: the only way on from a stop. One load() of the current link at the remembered spot. ──────
  // Nothing happens on a link that has run out (useCDNToken brings a fresher one), or for another lesson (the
  // load effect builds a new player for it). The subtitle tracks are added again on the new load.
  const restartFromLink = () => {
    const guard = guardRef.current; const p = shakaRef.current; const url = videoUrlRef.current;
    if (!guard.stopped || !p || !url || String(url).split('?')[0] !== guard.key) return false;
    if (guard.clockOk && cdnTokenExpired(cdnTokenRef.current)) return false;
    guard.stopped = false; guard.failures = [];
    setStopped((n) => n + 1);   // the recovery effect re-reads the guard: not stopped → it stands down; a failed load arms it again
    resumedRef.current = true;   // the spot goes to load() as its start time; loadedmetadata must not seek elsewhere
    subsAddedFor.current = null; setTracksReady(0);
    const withOverlay = guard.pos >= 1;   // step 2's rule: a lesson never played reloads quietly
    guard.loading = true;
    setError(null); setIsLoading(!withOverlay); setLinkOverlay(withOverlay ? 'loading' : null);
    p.load(url, guard.pos > 0 ? guard.pos : undefined)
      .then(() => {
        if (shakaRef.current !== p || guard.stopped) return;   // stopped again meanwhile: the stop owns the state
        guard.loading = false;
        setError(null); setIsLoading(false);
        setLinkOverlay(withOverlay && !guard.playAfter ? 'ready' : null);
        if (guard.playAfter) { guard.playAfter = false; setIsPlaying(true); }
      })
      .catch((err) => {
        if (shakaRef.current !== p || guard.stopped || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        guard.loading = false;
        guard.stopped = true; guard.playAfter = false;   // still stopped; key and pos kept; no play without a new press
        setLinkOverlay('ready');   // the overlay, never the error screen (October 30, 2026)
        setIsLoading(false);
        setStopped((n) => n + 1);
      });
    return true;
  };
  restartRef.current = restartFromLink;
  // While stopped: back online or the page shown → one try; the timed ladder → one try per rung, then no more.
  useEffect(() => {
    const guard = guardRef.current;
    if (!stopped || !guard.stopped) return undefined;
    const again = () => { if (restartRef.current) restartRef.current(); };
    const onVisible = () => { if (document.visibilityState === 'visible') again(); };
    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', onVisible);
    let timer = null;
    if (guard.tries < CDN_GUARD_RETRY_SECONDS.length) {
      timer = window.setTimeout(() => { guard.tries += 1; again(); }, CDN_GUARD_RETRY_SECONDS[guard.tries] * 1000);
    }
    return () => { window.removeEventListener('online', again); document.removeEventListener('visibilitychange', onVisible); if (timer) window.clearTimeout(timer); };
  }, [stopped]);
  // A play press on a stopped player: one try, and play once it has loaded (the press is the gesture).
  useEffect(() => {
    if (!isPlaying) { guardRef.current.playAfter = false; return; }   // paused meanwhile: a later restart stays paused
    if (!guardRef.current.stopped) return;
    guardRef.current.tries = 0; guardRef.current.playAfter = true;
    if (restartRef.current) restartRef.current();
  }, [isPlaying]);
  // The overlay (step 2's rules): gone once the video plays another way; gone on a new lesson; and "Reloading…"
  // with no load in sight for CDN_GUARD_OVERLAY_WAIT_SECONDS becomes "Resume lesson" (the tap refreshes everything).
  useEffect(() => { if (isPlaying && linkOverlay && !resumingRef.current) setLinkOverlay(null); }, [isPlaying, linkOverlay]);
  useEffect(() => { setLinkOverlay(null); }, [videoUrlKey]);
  useEffect(() => {
    if (linkOverlay !== 'loading') return undefined;
    let timer = null;
    // Waits at least CDN_GUARD_OVERLAY_WAIT_SECONDS, and past the next ladder rung while one can still try (a link
    // that has run out cannot be tried, so then only the wait counts).
    const delay = () => {
      const guard = guardRef.current;
      const canTry = guard.tries < CDN_GUARD_RETRY_SECONDS.length && !(guard.clockOk && cdnTokenExpired(cdnTokenRef.current));
      return Math.max(CDN_GUARD_OVERLAY_WAIT_SECONDS, canTry ? CDN_GUARD_RETRY_SECONDS[guard.tries] + 5 : 0) * 1000;
    };
    const check = () => {
      const guard = guardRef.current;
      if (guard.loading) { timer = window.setTimeout(check, delay()); return; }   // a slow load: keep waiting
      if (guard.stopped) setLinkOverlay('ready');   // the tap takes it from here (October 30, 2026)
    };
    timer = window.setTimeout(check, delay());
    return () => window.clearTimeout(timer);
  }, [linkOverlay, stopped]);
  // Resume (October 20, 2026): the overlay may have said "Resume lesson" for hours. The tap re-establishes everything
  // at tap time instead of trusting the player prepared back then: 1. a fresh session token, 2. a fresh link into page
  // state (through the page's hook, so both players and the PDF links see it), 3. a reload from it at the spot (a new
  // DRM session whose license carries the fresh token, fresh buffers), 4. play. A play-then-pause inside the tap
  // captures the activation without playing the old stream; the overlay stays up ('resuming') until the reload plays.
  const continueFromLink = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const v = videoRef.current; const p = shakaRef.current; const guard = guardRef.current;
    if (!v || !p) return;
    resumingRef.current = true;
    const first = v.play(); if (first && first.catch) first.catch(() => {});
    v.pause();   // play-then-pause inside the tap: the activation is captured, nothing plays until the reload
    setLinkOverlay('resuming');
    const pos = v.currentTime > 0 ? v.currentTime : (guard.pos || 0);
    (async () => {
      try {
        // offline: nothing to refresh; the guard's own 'online' trigger reloads later
        if (navigator.onLine !== false) await api.refreshToken();
        if (typeof refreshLink === 'function') {
          const before = cdnTokenRef.current;
          const r = await refreshLink();
          // the fresh link reaches this player through a render and its [videoUrl] effect, which writes the token
          // the request filter sends (cdnTokenRef); wait for that write (normally one tick, at most 1 s)
          if (r === 'fresh') for (let i = 0; i < 40 && cdnTokenRef.current === before; i++) await new Promise((res) => setTimeout(res, 25));
        }
        if (shakaRef.current !== p) return;   // the lesson changed meanwhile
        if (guard.stopped || guard.loading) {   // stopped again while waiting, or a restart load already in flight
          guard.playAfter = true;                // (the fresh link's own): that load is the reload; play after it
          if (guard.stopped) {
            guard.tries = 0;
            const started = restartRef.current ? restartRef.current() : false;
            if (!started) { guard.playAfter = false; setLinkOverlay('ready'); }   // still no usable link: Resume lesson again
          }
          return;
        }
        const at = v.currentTime > 0 ? v.currentTime : pos;   // the spot now (the tap's play() may have moved on)
        guard.loading = true;
        resumedRef.current = true;   // the spot is load()'s start time; loadedmetadata must not seek elsewhere
        subsAddedFor.current = null; setTracksReady(0);   // the subtitle tracks are added again on the new load
        await p.load(videoUrlRef.current, at > 0 ? at : undefined);
        if (shakaRef.current !== p || guard.stopped) return;   // stopped again meanwhile: the stop owns the state
        guard.loading = false;
        setLinkOverlay(null); setError(null); setIsLoading(false);
        const again = v.play(); if (again && again.catch) again.catch(() => {});
        setIsPlaying(true);
      } catch (err) {
        if (shakaRef.current !== p || guard.stopped || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        // the same state as a failed restart load: stopped, with the key and the spot kept, so the next link, press,
        // online, visibility or ladder rung tries again; no play without a new press
        guard.loading = false;
        guard.stopped = true; guard.key = String(videoUrlRef.current).split('?')[0]; guard.pos = v.currentTime > 0 ? v.currentTime : pos; guard.playAfter = false;
        setLinkOverlay('ready'); setIsLoading(false); setIsPlaying(false);   // back to Resume lesson, never the error screen (October 30, 2026)
        setStopped((n) => n + 1);
      } finally {
        resumingRef.current = false;
      }
    })();
  };
  useEffect(() => {
    const player = shakaRef.current;
    if (!player || !tracksReady) return;
    const opt = subtitleOptions.find((s) => s.key === subtitleKey);
    try {
      if (!opt) { player.setTextTrackVisibility(false); return; }
      const kind = opt.kind === 'captions' ? 'caption' : 'subtitle';
      const all = player.getTextTracks();
      const tr = all.find((t) => t.language === opt.lang && t.kind === kind && (!opt.label || t.label === opt.label))
        || all.find((t) => t.language === opt.lang && t.kind === kind);
      if (tr) player.selectTextTrack(tr);
      player.setTextTrackVisibility(true);
    } catch (err) { /* subtitles are optional: never disturb playback */ }
  }, [subtitleKey, tracksReady, subtitleOptions]);

  const formatTime=(s)=>{if(!s||isNaN(s))return'0:00';return`${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`;};

  const timeAtX = (clientX) => {
    if (!progressRef.current || !totalSeconds) return null;
    const r = progressRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(totalSeconds, ((clientX - r.left) / r.width) * totalSeconds));
  };
  const commitDragSeek = (t) => {
    setCurrentTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };
  const handleProgressClick=(e)=>{e.stopPropagation();if(progressRef.current){const r=progressRef.current.getBoundingClientRect();const t=Math.floor(((e.clientX-r.left)/r.width)*totalSeconds);setCurrentTime(t);if(videoRef.current)videoRef.current.currentTime=t;}};

  const handleVolumeChange=useCallback((cx)=>{if(volumeRef.current){const r=volumeRef.current.getBoundingClientRect();setVolume(Math.max(0,Math.min(100,((cx-r.left)/r.width)*100)));setIsMuted(false);}},[]);
  const handleVolumeClick=(e)=>{e.stopPropagation();handleVolumeChange(e.clientX);};
  const handleVolumeTouchMove=useCallback((e)=>{e.preventDefault();if(e.touches[0])handleVolumeChange(e.touches[0].clientX);},[handleVolumeChange]);

  const skip=(s)=>{const t=Math.max(0,Math.min(totalSeconds,currentTime+s));setCurrentTime(t);if(videoRef.current)videoRef.current.currentTime=t;showControlsTemporarily();};
  // ── Lock-screen and headphone controls (1.24) ───────────────────────────
  // The Media Session API. Registered when this player starts playing, and kept after a pause so the
  // lock-screen play button still works; cleared when this player unmounts, if it is still the one
  // registered. Handlers go through a ref, so they always call this render's functions.
  const mediaSessionRef = useRef({});
  mediaSessionRef.current = {
    play: () => setIsPlaying(true),
    pause: () => setIsPlaying(false),
    skip: (s) => skip(s),
    seekTo: (t) => { const v = videoRef.current; if (v) { v.currentTime = t; setCurrentTime(t); } },
  };
  const mediaSessionOwner = useRef({});
  useEffect(() => {
    if (!isPlaying || typeof navigator === 'undefined' || !navigator.mediaSession) return;
    const ms = navigator.mediaSession;
    try {
      if (typeof window.MediaMetadata === 'function') ms.metadata = new window.MediaMetadata({ title: (video && video.title) || '', artist: (video && video.series && video.series.name) || '', album: 'RoastBrew' });
    } catch (err) { /* metadata is optional */ }
    const set = (a, h) => { try { ms.setActionHandler(a, h); } catch (err) { /* this action isn't supported here */ } };
    set('play', () => mediaSessionRef.current.play());
    set('pause', () => mediaSessionRef.current.pause());
    set('seekbackward', () => mediaSessionRef.current.skip(-10));
    set('seekforward', () => mediaSessionRef.current.skip(10));
    set('seekto', (d) => { if (d && typeof d.seekTime === 'number') mediaSessionRef.current.seekTo(d.seekTime); });
    window.__rbMediaSessionOwner = mediaSessionOwner.current;
  }, [isPlaying, video]);
  useEffect(() => () => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession || window.__rbMediaSessionOwner !== mediaSessionOwner.current) return;
    ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'].forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch (err) { /* not supported */ } });
    window.__rbMediaSessionOwner = null;
  }, []);

  const toggleFullscreen=async()=>{try{if(isMobile&&videoRef.current?.webkitEnterFullscreen){await videoRef.current.webkitEnterFullscreen();return;}if(!document.fullscreenElement&&!document.webkitFullscreenElement){if(containerRef.current?.requestFullscreen)await containerRef.current.requestFullscreen();else if(containerRef.current?.webkitRequestFullscreen)await containerRef.current.webkitRequestFullscreen();try{await window.screen.orientation.lock('landscape');}catch(e){}}else{if(document.exitFullscreen)await document.exitFullscreen();else if(document.webkitExitFullscreen)await document.webkitExitFullscreen();try{await window.screen.orientation.lock('portrait');}catch(e){try{window.screen.orientation.unlock();}catch(e2){}}}}catch(e){}};
  // ── KEYBOARD SHORTCUTS ─────────────────────────────────────────────────────
  //   ← / →     skip by skipAmount (the same distance the on-screen buttons use)
  //   Space / K play-pause
  //   M         mute
  //   F         fullscreen
  //
  // PRECEDENCE, in this order:
  //   1. Focus in a text field wins over everything. Typing "quadratic" into the
  //      search bar while a lesson plays must move the caret and type spaces, not
  //      scrub and pause the video. Checked against the event target AND
  //      document.activeElement so it covers every input on the page — the search
  //      bar today, and anything added later without knowing this handler exists.
  //   2. Space always toggles play/pause when focus is free. Someone who paused
  //      with Space expects Space to resume, and under the "only while playing"
  //      rule below it would not.
  //   3. Everything else applies ONLY while this player is playing, so the arrow
  //      keys still scroll the page when nothing is running.
  //
  // Two players exist on this page (lesson + answer key) but they cannot both be
  // playing — an effect forces the other false whenever one starts — so "the
  // playing one" is unambiguous and no targeting logic is needed.
  //
  // NOTE: this does NOT stopPropagation. App.jsx's ensureFresh listens for keydown
  // to re-mint expiring signed URLs; swallowing the event would silently disable
  // that. preventDefault is called only for keys actually handled, so page
  // scrolling is untouched otherwise.
  // ── Desktop shortcuts (1.25) ─────────────────────────────────────────────
  // J/L ±10 s, < > through the speeds, C subtitles on/off, 0–9 jump to 0–90%, ? the shortcut sheet.
  // Handled above the "idle" line in onKey, so they also work while paused.
  const [showShortcuts, setShowShortcuts] = useState(false);
  const lastSubtitleRef = useRef(null);
  useEffect(() => { if (activeSubtitleKey !== 'off') lastSubtitleRef.current = activeSubtitleKey; }, [activeSubtitleKey]);
  const stepSpeed = (dir) => {
    const i = PLAYBACK_SPEEDSDesktop.indexOf(playbackSpeed);
    const next = PLAYBACK_SPEEDSDesktop[Math.max(0, Math.min(PLAYBACK_SPEEDSDesktop.length - 1, (i < 0 ? PLAYBACK_SPEEDSDesktop.indexOf(1) : i) + dir))];
    setPlaybackSpeed(next); showControlsTemporarily();
  };
  const toggleSubtitles = () => {
    if (!subtitleOptions.length) return;
    if (activeSubtitleKey !== 'off') { setSubtitleKey('off'); return; }
    const last = lastSubtitleRef.current;
    setSubtitleKey(last && subtitleOptions.some((s) => s.key === last) ? last : subtitleOptions[0].key);
  };
  const jumpTo = (fraction) => {
    const v = videoRef.current; const d = (v && Number.isFinite(v.duration) && v.duration > 0) ? v.duration : totalSeconds;
    if (!d) return;
    const t = d * fraction; setCurrentTime(t); if (v) v.currentTime = t; showControlsTemporarily();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const t = e.target;
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      const isField = (el) => !!(el && el.closest && el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
      if (isField(t) || isField(active)) return;      // focus wins, always

      const key = e.key;

      if (key === ' ' || key === 'Spacebar' || key === 'k' || key === 'K') {
        e.preventDefault();                            // Space would scroll the page
        setIsPlaying(!isPlaying);
        showControlsTemporarily();
        return;
      }

      // New shortcuts (1.25): they work whether or not the video is playing.
      if (key === 'Escape' && showShortcuts) { e.preventDefault(); setShowShortcuts(false); return; }
      if (key === '?') { e.preventDefault(); setShowShortcuts((v) => !v); return; }
      if (key === 'j' || key === 'J') { e.preventDefault(); skip(-10); return; }
      if (key === 'l' || key === 'L') { e.preventDefault(); skip(10); return; }
      if (key === '<' || key === ',') { e.preventDefault(); stepSpeed(-1); return; }
      if (key === '>' || key === '.') { e.preventDefault(); stepSpeed(1); return; }
      if ((key === 'c' || key === 'C') && subtitleOptions.length) { e.preventDefault(); toggleSubtitles(); return; }
      if (/^[0-9]$/.test(key)) { e.preventDefault(); jumpTo(Number(key) / 10); return; }

      if (!isPlaying) return;                          // idle: leave the keys alone

      if (key === 'ArrowRight') { e.preventDefault(); skip(skipAmount); }
      else if (key === 'ArrowLeft') { e.preventDefault(); skip(-skipAmount); }
      else if (key === 'm' || key === 'M') { e.preventDefault(); setIsMuted(!isMuted); showControlsTemporarily(); }
      else if (key === 'f' || key === 'F') { e.preventDefault(); toggleFullscreen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const togglePiP=async()=>{if(!videoRef.current)return;try{if(document.pictureInPictureElement)await document.exitPictureInPicture();else if(document.pictureInPictureEnabled)await videoRef.current.requestPictureInPicture();}catch(e){}};

  useEffect(()=>{
    const h=()=>setIsFullscreen(!!(document.fullscreenElement||document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange',h);document.addEventListener('webkitfullscreenchange',h);
    const v=videoRef.current;const ib=()=>setIsFullscreen(true);const ie=()=>setIsFullscreen(false);
    if(v){v.addEventListener('webkitbeginfullscreen',ib);v.addEventListener('webkitendfullscreen',ie);}
    return()=>{document.removeEventListener('fullscreenchange',h);document.removeEventListener('webkitfullscreenchange',h);if(v){v.removeEventListener('webkitbeginfullscreen',ib);v.removeEventListener('webkitendfullscreen',ie);}};
  },[]);

  useEffect(()=>{if(!showSpeedMenu)return;const h=()=>setShowSpeedMenu(false);document.addEventListener('click',h);return()=>document.removeEventListener('click',h);},[showSpeedMenu]);

  const playBtnSize = isMobile?52:88, playIconSz = isMobile?22:36;

  // Speed menu (shared between mobile top bar & desktop bottom bar)
  // Subtitles menu (desktop, 1.21): built like the speed menu: Off, then each language this lesson has.
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false);
  useEffect(()=>{if(!showSubtitleMenu)return;const h=()=>setShowSubtitleMenu(false);document.addEventListener('click',h);return()=>document.removeEventListener('click',h);},[showSubtitleMenu]);
  useEffect(()=>{if(showSpeedMenu)setShowSubtitleMenu(false);},[showSpeedMenu]);
  const subtitleMenuEl = showSubtitleMenu && (
    <div onClick={(e)=>e.stopPropagation()} role="menu" aria-label="Subtitles" style={{position:'absolute',bottom:'100%',right:0,marginBottom:'8px',background:'rgba(20,20,20,0.95)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',borderRadius:'10px',padding:'8px',minWidth:'170px',boxShadow:'0 8px 32px rgba(0,0,0,0.5)',border:'1px solid rgba(255,255,255,0.1)',zIndex:100}}>
      {[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((s) => (
        <button key={s.key} role="menuitemradio" aria-checked={activeSubtitleKey===s.key} onClick={(e)=>{e.stopPropagation();setSubtitleKey(s.key);setShowSubtitleMenu(false);}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',width:'100%',padding:'8px 12px',background:activeSubtitleKey===s.key?'rgba(255,255,255,0.1)':'transparent',border:'none',borderRadius:'6px',cursor:'pointer',color:activeSubtitleKey===s.key?colors.accent:'#fff',fontSize:'14px',fontFamily:'"DM Sans",sans-serif',fontWeight:activeSubtitleKey===s.key?'600':'400',textAlign:'left',WebkitTapHighlightColor:'transparent'}}>
          <span>{s.label}</span>
          {activeSubtitleKey===s.key&&<svg width="14" height="14" viewBox="0 0 24 24" fill={colors.accent}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>}
        </button>
      ))}
    </div>
  );

  const speedMenuEl = showSpeedMenu && (
    <div onClick={(e)=>e.stopPropagation()} style={{position:'absolute',bottom:'100%',right:0,marginBottom:'8px',background:'rgba(20,20,20,0.95)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',borderRadius:'10px',padding:'8px',minWidth:isMobile?'120px':'140px',boxShadow:'0 8px 32px rgba(0,0,0,0.5)',border:'1px solid rgba(255,255,255,0.1)',zIndex:100}}>
      {PLAYBACK_SPEEDSDesktop.map(spd=>(
        <button key={spd} onClick={(e)=>{e.stopPropagation();setPlaybackSpeed(spd);setShowSpeedMenu(false);}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%',padding:isMobile?'10px 12px':'8px 12px',background:playbackSpeed===spd?'rgba(255,255,255,0.1)':'transparent',border:'none',borderRadius:'6px',cursor:'pointer',color:playbackSpeed===spd?colors.accent:'#fff',fontSize:isMobile?'13px':'14px',fontFamily:'"DM Sans",sans-serif',fontWeight:playbackSpeed===spd?'600':'400',WebkitTapHighlightColor:'transparent'}}>
          <span>{spd===1?'Normal':`${spd}x`}</span>
          {playbackSpeed===spd&&<svg width="14" height="14" viewBox="0 0 24 24" fill={colors.accent}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>}
        </button>
      ))}
    </div>
  );

  const topBarBtn = (children, extraStyle={}, onClick=undefined) => (
    <button onClick={onClick} style={{flex:1,minHeight:'32px',padding:'6px',background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'8px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'4px',color:'rgba(255,255,255,0.9)',fontSize:'9px',fontWeight:'600',fontFamily:'"DM Sans",sans-serif',backdropFilter:'blur(10px)',WebkitTapHighlightColor:'transparent',...extraStyle}}>{children}</button>
  );

  // ── B3 (2.12i) · the bar's tier follows the player's own width (the approved design) ──────────────────
//   large  720 px and up: the full bar, exactly as before (its code is unchanged)
//   medium 480–719 px:    the volume slider and Picture-in-Picture move into a settings menu
//   small  under 480 px:  play, time, settings and full screen; skip, speed, subtitles and volume move into
//                         the settings menu
//   Every keyboard shortcut keeps working at every tier. The settings menu behaves like the speed and subtitle
//   menus: one menu at a time, an outside click closes it, and so does Esc. In medium and small, speed and subtitles
//   are views of the settings menu (with a back row), and the menu is capped at the player's height and scrolls, so
//   nothing is clipped on a short player. While it's open, the centred play button hides: it sits above the controls
//   overlay (z-index 20 over 15) and would otherwise cover the menu.
  const [barTier, setBarTier] = useState('large');
  const [playerH, setPlayerH] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const pick = () => { const r = el.getBoundingClientRect(); setBarTier(r.width >= 720 ? 'large' : r.width >= 480 ? 'medium' : 'small'); setPlayerH(Math.round(r.height)); };
    pick();
    const observer = new ResizeObserver(pick);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsView, setSettingsView] = useState('main');   // 'main' · 'speed' · 'subtitles'
  const openSettings = (view) => { setSettingsView(view); setShowSettings(true); };
  useEffect(()=>{if(!showSettings)setSettingsView('main');},[showSettings]);
  useEffect(()=>{if(!showSettings)return;const h=()=>setShowSettings(false);document.addEventListener('click',h);return()=>document.removeEventListener('click',h);},[showSettings]);
  useEffect(()=>{if(!showSettings)return;const k=(e)=>{if(e.key==='Escape')setShowSettings(false);};window.addEventListener('keydown',k);return()=>window.removeEventListener('keydown',k);},[showSettings]);
  useEffect(()=>{if(showSettings){setShowSpeedMenu(false);setShowSubtitleMenu(false);}},[showSettings]);
  useEffect(()=>{if(showSpeedMenu||showSubtitleMenu)setShowSettings(false);},[showSpeedMenu,showSubtitleMenu]);
  useEffect(()=>{if(barTier==='large')setShowSettings(false);},[barTier]);
  const compactMenuOpen = barTier !== 'large' && showSettings;
  const menuMaxH = Math.max(72, playerH - 96);
  const barIconBtn = { padding:'8px', background:'none', border:'none', cursor:'pointer', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center' };
  const volumeIcon = (sz) => (<svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{isMuted||volume===0?<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></>:<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14"/></>}</svg>);
  const activeSubtitleLabel = activeSubtitleKey === 'off' ? 'Off' : ((subtitleOptions.find((o) => o.key === activeSubtitleKey) || {}).label || 'Off');
  const rowStyle = {display:'flex',alignItems:'center',justifyContent:'space-between',gap:'16px',width:'100%',padding:'8px 12px',background:'none',border:'none',borderRadius:'6px',cursor:'pointer',color:'#fff',fontFamily:'"DM Sans",sans-serif',fontSize:'13px',textAlign:'left',whiteSpace:'nowrap'};
  const settingsRow = (label, onClick, value) => (
    <button role="menuitem" onClick={(e)=>{e.stopPropagation();onClick();}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'16px',width:'100%',padding:'8px 12px',background:'none',border:'none',borderRadius:'6px',cursor:'pointer',color:'#fff',fontFamily:'"DM Sans",sans-serif',fontSize:'13px',textAlign:'left',whiteSpace:'nowrap'}}>
      <span>{label}</span>{value !== undefined && <span style={{color:'rgba(255,255,255,0.6)'}}>{value}</span>}
    </button>
  );
  const backRow = (label) => (<button role="menuitem" onClick={(e)=>{e.stopPropagation();setSettingsView('main');}} style={{...rowStyle,color:'rgba(255,255,255,0.75)'}}><span>‹ {label}</span></button>);
  const choiceRow = (key, label, checked, onPick) => (
    <button key={key} role="menuitemradio" aria-checked={checked} onClick={(e)=>{e.stopPropagation();onPick();setShowSettings(false);}} style={rowStyle}>
      <span>{label}</span>{checked&&<svg width="14" height="14" viewBox="0 0 24 24" fill={colors.accent} aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>}
    </button>
  );
  const settingsMenuEl = showSettings && (
    <div onClick={(e)=>e.stopPropagation()} role="menu" aria-label={settingsView==='speed'?'Playback speed':settingsView==='subtitles'?'Subtitles':'Settings'} style={{position:'absolute',bottom:'100%',right:0,marginBottom:'8px',background:'rgba(20,20,20,0.95)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',borderRadius:'10px',padding:'6px',minWidth:'210px',maxHeight:menuMaxH+'px',overflowY:'auto',zIndex:30,boxShadow:'0 8px 32px rgba(0,0,0,0.5)'}}>
      {settingsView==='speed' && <>{backRow('Speed')}{PLAYBACK_SPEEDSDesktop.map((spd)=>choiceRow(spd, spd===1?'Normal':`${spd}x`, playbackSpeed===spd, ()=>setPlaybackSpeed(spd)))}</>}
      {settingsView==='subtitles' && <>{backRow('Subtitles')}{[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((o)=>choiceRow(o.key, o.label, activeSubtitleKey===o.key, ()=>setSubtitleKey(o.key)))}</>}
      {settingsView==='main' && <>
      {barTier==='small' && settingsRow(`Back ${skipAmount} s`, ()=>skip(-skipAmount))}
      {barTier==='small' && settingsRow(`Forward ${skipAmount} s`, ()=>skip(skipAmount))}
      {barTier==='small' && settingsRow('Speed', ()=>setSettingsView('speed'), `${playbackSpeed===1?'Normal':`${playbackSpeed}x`} ›`)}
      {barTier==='small' && subtitleOptions.length>0 && settingsRow('Subtitles', ()=>setSettingsView('subtitles'), `${activeSubtitleLabel} ›`)}
      <div role="group" aria-label="Volume" style={{display:'flex',alignItems:'center',gap:'8px',padding:'4px 8px'}}>
        <button aria-label={isMuted||volume===0?'Unmute':'Mute'} onClick={(e)=>{e.stopPropagation();setIsMuted(!isMuted);}} style={barIconBtn}>{volumeIcon(20)}</button>
        <div ref={volumeRef} role="slider" aria-label="Volume" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(isMuted?0:volume)} onClick={handleVolumeClick} style={{flex:1,height:'4px',background:'rgba(255,255,255,0.25)',borderRadius:'2px',cursor:'pointer',position:'relative'}}>
          <div style={{width:`${isMuted?0:volume}%`,height:'100%',borderRadius:'2px',background:colors.accent,pointerEvents:'none'}} />
          <div style={{position:'absolute',left:`${isMuted?0:volume}%`,top:'50%',transform:'translate(-50%,-50%)',width:'12px',height:'12px',borderRadius:'50%',background:'#fff',pointerEvents:'none'}} />
        </div>
      </div>
      {settingsRow('Picture-in-Picture', ()=>{setShowSettings(false);togglePiP();})}
      </>}
    </div>
  );
  const compactRow = (
    <div data-bar-tier={barTier} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px'}}>
      <div style={{display:'flex',alignItems:'center',gap:'6px',minWidth:0}}>
        <button aria-label={isPlaying?'Pause':'Play'} onClick={(e)=>{e.stopPropagation();setIsPlaying(!isPlaying);showControlsTemporarily();}} style={barIconBtn}>
          <svg width={24} height={24} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{isPlaying?<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>:<path d="M8 5v14l11-7z"/>}</svg>
        </button>
        {barTier==='medium' && <SkipButtonDesktop direction="back" onClick={()=>skip(-skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />}
        {barTier==='medium' && <SkipButtonDesktop direction="forward" onClick={()=>skip(skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />}
        {barTier==='medium' && <button aria-label={isMuted||volume===0?'Unmute':'Mute'} onClick={(e)=>{e.stopPropagation();setIsMuted(!isMuted);}} style={barIconBtn}>{volumeIcon(22)}</button>}
        <span style={{fontSize:'13px',color:'rgba(255,255,255,0.9)',fontFamily:'"DM Sans",sans-serif',whiteSpace:'nowrap'}}>{formatTime(currentTime)} / {formatTime(totalSeconds)}</span>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'6px',flexShrink:0}}>
        {barTier==='medium' && (
          <div style={{position:'relative'}}>
            <button aria-label="Playback speed" onClick={(e)=>{e.stopPropagation();if(showSettings&&settingsView==='speed')setShowSettings(false);else openSettings('speed');}} style={{padding:'6px 10px',background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'6px',cursor:'pointer',color:'#fff',fontFamily:'"DM Sans",sans-serif',fontSize:'12px',whiteSpace:'nowrap'}}>{playbackSpeed===1?'1x':`${playbackSpeed}x`}</button>
          </div>
        )}
        {barTier==='medium' && subtitleOptions.length>0 && (
          <div style={{position:'relative'}}>
            <button aria-label="Subtitles" aria-pressed={activeSubtitleKey!=='off'} onClick={(e)=>{e.stopPropagation();if(showSettings&&settingsView==='subtitles')setShowSettings(false);else openSettings('subtitles');}} style={barIconBtn}>
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 15h4M15 15h2M7 11h2M13 11h4"/></svg>
            </button>
          </div>
        )}
        <div style={{position:'relative'}}>
          <button aria-label="Settings" aria-haspopup="menu" aria-expanded={showSettings} onClick={(e)=>{e.stopPropagation();if(showSettings)setShowSettings(false);else openSettings('main');}} style={barIconBtn}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
          </button>
          {settingsMenuEl}
        </div>
        <button aria-label={isFullscreen?'Exit full screen':'Full screen'} onClick={(e)=>{e.stopPropagation();toggleFullscreen();}} style={barIconBtn}>
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">{isFullscreen?<path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3"/>:<path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3"/>}</svg>
        </button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="video-player-container" style={{position:'relative',aspectRatio:'16/9',borderRadius:isFullscreen?0:(isMobile?'10px':'16px'),overflow:'hidden',background:'#000',cursor:showControls?'default':'none'}}
      onClick={handleVideoAreaClick} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      {autoMuted && (soundPromptBig ? (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 22px) clamp(22px, 5vw, 34px)', borderRadius: '18px', border: 'none', background: '#111111', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 'clamp(16px, 2.4vw, 22px)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: 'clamp(34px, 7vw, 48px)', height: 'clamp(34px, 7vw, 48px)' }}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ) : (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 40, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '999px', border: 'none', background: 'rgba(0,0,0,0.72)', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ))}

      <div className="video-landscape-bg" />
      <div className="video-player-inner" style={{position:'relative',width:'100%',height:'100%',overflow:'hidden',borderRadius:'inherit'}}>

      {/* Video element. No onError by design: Shaka owns DRM/load error reporting (see the
          player 'error' handler). The raw <video> element emits transient errors during
          DRM/MSE setup that self-heal, so surfacing them here caused a stuck "failed"
          overlay over a video that was actually playing. */}
      {videoUrl && <video ref={videoRef} muted={isMuted} style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'contain'}}
        onLoadedMetadata={()=>{if(videoRef.current){setDuration(videoRef.current.duration);setIsLoading(false);setError(null);if(!resumedRef.current){const r=resumeFromRef.current;if(r>0&&r<videoRef.current.duration){videoRef.current.currentTime=r;setCurrentTime(r);}resumedRef.current=true;}}}}
        onTimeUpdate={()=>{if(videoRef.current&&!guardRef.current.stopped){const t=videoRef.current.currentTime;setCurrentTime(t);const d=videoRef.current.duration||duration||0;latestRef.current={pos:t,dur:d};heartbeatOnTime(t,d,videoRef.current.playbackRate,!videoRef.current.paused&&!videoRef.current.seeking);}}}
        onSeeking={()=>heartbeatOnTime.seeking()}
        onProgress={()=>{if(videoRef.current?.buffered.length>0)setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length-1));}}
        onWaiting={()=>setIsLoading(true)}
        onCanPlay={()=>{setIsLoading(false);setError(null);if(videoRef.current)videoRef.current.volume=volume/100;}}
        onEnded={()=>setIsPlaying(false)} onPlay={reportPlaying(setIsPlaying)} onPause={reportPlaying(setIsPlaying)}
        playsInline preload="metadata" />}

      {/* Gradient placeholder */}
      {!videoUrl && <div style={{position:'absolute',inset:0,background:'#0a0a0a'}} />}

      {/* Loading */}
      {isLoading&&!showError&&<div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.5)',zIndex:20}}><div style={{width:isMobile?40:48,height:isMobile?40:48,border:'3px solid rgba(255,255,255,0.2)',borderTopColor:colors.accent,borderRadius:'50%',animation:'spin 0.8s linear infinite'}} /></div>}

      {/* Error (debounced — see showError, avoids a flash on normal load) */}
      {showError&&<div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:`linear-gradient(135deg,${gradientFor(video.id)[0]}40,${gradientFor(video.id)[1]}40)`,zIndex:20}}>
        <svg width={isMobile?40:48} height={isMobile?40:48} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
        <p style={{marginTop:16,color:'#fff',fontFamily:'"DM Sans",sans-serif',fontSize:isMobile?12:14}}>{error}</p>
      </div>}
      <LinkOverlay state={linkOverlay} at={guardRef.current.pos} colors={colors} onContinue={continueFromLink} small={isMobile} />

      {/* Cinematic vignette (mobile) */}
      {isMobile&&<div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse at center,transparent 40%,rgba(0,0,0,0.05) 100%)',pointerEvents:'none',zIndex:1}} />}

      {/* Dark overlay when paused */}
      {!showError&&<div style={{position:'absolute',inset:0,background:'#000',opacity:isPlaying?0:0.4,zIndex:5,pointerEvents:'none',transition:'opacity 0.5s ease'}} />}

      {/* ===== MOBILE LAYOUT ===== */}
      {isMobile && <>
        {/* Top Settings Bar */}
        <div data-controls="true" onClick={(e)=>e.stopPropagation()} style={{position:'absolute',top:0,left:0,right:0,padding:'8px 10px',background:'linear-gradient(to bottom,rgba(0,0,0,0.8) 0%,rgba(0,0,0,0.4) 70%,transparent 100%)',display:'flex',justifyContent:'space-evenly',alignItems:'center',zIndex:16,gap:'8px',opacity:showControls?1:0,transition:'opacity 0.3s ease',pointerEvents:showControls?'auto':'none'}}>
          {subtitleOptions.length > 0 && (
            <div style={{flex:1,position:'relative'}}>
              {topBarBtn(<><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 12h4M13 12h4M7 16h10"/></svg><span>CC</span></>,{},e=>{e.stopPropagation();setShowSpeedMenu(false);setShowSubtitleMenu(!showSubtitleMenu);})}
              {subtitleMenuEl}
            </div>
          )}
          <div style={{flex:1,position:'relative'}}>
            {topBarBtn(<span>{playbackSpeed===1?'1x':`${playbackSpeed}x`}</span>,{},e=>{e.stopPropagation();setShowSpeedMenu(!showSpeedMenu);})}
            {speedMenuEl}
          </div>
          {topBarBtn(<><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor" stroke="none"/></svg><span>PiP</span></>,{},e=>{e.stopPropagation();togglePiP();})}
          {topBarBtn(<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isMuted||volume===0?<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></>:<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></>}</svg>,{},e=>{e.stopPropagation();setIsMuted(!isMuted);})}
        </div>

        {/* Center Play Area with Skip Buttons */}
        <div data-controls="true" onClick={(e)=>e.stopPropagation()} style={{position:'absolute',top:'45%',left:'50%',transform:'translate(-50%,-50%)',display:'flex',alignItems:'center',gap:'14px',zIndex:16,opacity:showControls?1:0,transition:'opacity 0.3s ease',pointerEvents:showControls?'auto':'none'}}>
          <SkipButtonDesktop direction="back" onClick={()=>skip(-skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />
          <button onClick={()=>{setIsPlaying(!isPlaying);showControlsTemporarily();}} style={{width:`${playBtnSize}px`,height:`${playBtnSize}px`,borderRadius:'50%',background:'rgba(255,255,255,0.95)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 8px 32px rgba(0,0,0,0.4)',cursor:'pointer',border:'none',animation:!isPlaying?'pulse 2.2s ease-in-out infinite':'none'}}>
            <svg width={playIconSz} height={playIconSz} viewBox="0 0 24 24" fill="#000" style={{marginLeft:!isPlaying?'3px':'0'}}>
              {isPlaying?<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>:<path d="M8 5v14l11-7z"/>}
            </svg>
          </button>
          <SkipButtonDesktop direction="forward" onClick={()=>skip(skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />
        </div>

        {/* Bottom Controls - simplified */}
        <div data-controls="true" onClick={(e)=>e.stopPropagation()} style={{position:'absolute',bottom:0,left:0,right:0,padding:'8px 12px',background:'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.5) 50%,transparent 100%)',zIndex:16,opacity:showControls?1:0,transition:'opacity 0.3s ease',pointerEvents:showControls?'auto':'none'}}>
          <div ref={progressRef} onClick={handleProgressClick} style={{height:'4px',background:'rgba(255,255,255,0.2)',borderRadius:'3px',cursor:'pointer',marginBottom:'10px',position:'relative'}}>
            <div style={{position:'absolute',height:'100%',width:`${bufferedPercent}%`,background:'rgba(255,255,255,0.3)',borderRadius:'3px',pointerEvents:'none'}} />
            <div style={{position:'absolute',height:'100%',width:`${progressPercent}%`,borderRadius:'3px',pointerEvents:'none',...pStyles.played}} />
            <div style={{position:'absolute',left:`${progressPercent}%`,top:'50%',transform:'translate(-50%,-50%)',width:'12px',height:'12px',borderRadius:'50%',pointerEvents:'none',...pStyles.thumb}} />
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:'11px',color:'rgba(255,255,255,0.9)',fontFamily:'"DM Sans",sans-serif',fontVariantNumeric:'tabular-nums'}}>{formatTime(currentTime)} / {formatTime(totalSeconds)}</span>
            <button onClick={(e)=>{e.stopPropagation();toggleFullscreen();}} style={{padding:'6px',background:'none',border:'none',cursor:'pointer',color:'#fff'}}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isFullscreen?<path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3"/>:<path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3"/>}</svg>
            </button>
          </div>
        </div>
      </>}

      {/* ===== DESKTOP/TABLET LAYOUT ===== */}
      {!isMobile && <>
        {/* Center play pulse */}
        {(!isPlaying||showControls)&&!compactMenuOpen&&<div data-controls="true" style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',zIndex:20,opacity:!isPlaying?1:(showControls?0.9:0),transition:'opacity 0.3s ease',pointerEvents:(!isPlaying||showControls)?'auto':'none'}} onClick={(e)=>{e.stopPropagation();setIsPlaying(!isPlaying);showControlsTemporarily();}}>
          <div style={{width:`${playBtnSize}px`,height:`${playBtnSize}px`,borderRadius:'50%',background:'rgba(255,255,255,0.95)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 8px 32px rgba(0,0,0,0.4)',cursor:'pointer',animation:!isPlaying?'pulse 2.2s ease-in-out infinite':'none'}}>
            <svg width={playIconSz} height={playIconSz} viewBox="0 0 24 24" fill="#000" style={{marginLeft:!isPlaying?'4px':'0'}}>{isPlaying?<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>:<path d="M8 5v14l11-7z"/>}</svg>
          </div>
        </div>}

        {/* Controls overlay */}
        <div style={{position:'absolute',inset:0,background:'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.6) 30%,transparent 60%,transparent 80%,rgba(0,0,0,0.4) 100%)',opacity:showControls?1:0,transition:'opacity 0.3s ease',pointerEvents:showControls?'auto':'none',zIndex:15}}>
          {/* Top bar */}
          <div style={{position:'absolute',top:0,left:0,right:0,padding:'20px 24px',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div>{/* The course and lesson names are no longer drawn on the video (October 15, 2026): the title stack above it has them. The box stays so the layout does not shift. */}</div>
          </div>
          {/* Bottom controls */}
          <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'20px 24px'}}>
            <div ref={!isMobile ? progressRef : undefined}
            onClick={(e) => { if (dragRef.current.justEnded) { dragRef.current.justEnded = false; e.stopPropagation(); return; } handleProgressClick(e); }}
            onMouseMove={(e) => setHoverTime(timeAtX(e.clientX))}
            onMouseLeave={() => setHoverTime(null)}
            onPointerDown={(e) => { if (e.pointerType !== 'mouse' || e.button !== 0) return; try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* capture is optional */ } dragRef.current = { active: true, moved: false, x: e.clientX, justEnded: false }; }}
            onPointerMove={(e) => { const d = dragRef.current; if (!d.active) return; if (!d.moved && Math.abs(e.clientX - d.x) < 3) return; d.moved = true; setDragTime(timeAtX(e.clientX)); }}
            onPointerUp={(e) => { const d = dragRef.current; if (!d.active) return; d.active = false; if (!d.moved) return; const t = timeAtX(e.clientX); setDragTime(null); if (t !== null) commitDragSeek(t); d.justEnded = true; setTimeout(() => { dragRef.current.justEnded = false; }, 0); }}
            onPointerCancel={() => { dragRef.current.active = false; setDragTime(null); }}
            style={{height:'5px',background:'rgba(255,255,255,0.2)',borderRadius:'3px',cursor:'pointer',marginBottom:'16px',position:'relative'}}>
              <div style={{position:'absolute',height:'100%',width:`${bufferedPercent}%`,background:'rgba(255,255,255,0.3)',borderRadius:'3px',pointerEvents:'none'}} />
              <div style={{position:'absolute',height:'100%',width:`${progressPercent}%`,borderRadius:'3px',pointerEvents:'none',...pStyles.played}} />
              <div style={{position:'absolute',left:`${progressPercent}%`,top:'50%',transform:'translate(-50%,-50%)',width:'14px',height:'14px',borderRadius:'50%',pointerEvents:'none',...pStyles.thumb}} />
              {(dragTime ?? hoverTime) !== null && (
                <div style={{ position: 'absolute', left: `${((dragTime ?? hoverTime) / totalSeconds) * 100}%`, bottom: '16px', transform: 'translateX(-50%)', padding: '3px 8px', borderRadius: '6px', background: 'rgba(0,0,0,0.85)', color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                  {formatTime(dragTime ?? hoverTime)}
                </div>
              )}
            </div>
            {barTier === 'large' ? (
<div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                <button aria-label={isPlaying?'Pause':'Play'} onClick={(e)=>{e.stopPropagation();setIsPlaying(!isPlaying);showControlsTemporarily();}} style={{padding:'8px',background:'none',border:'none',cursor:'pointer',color:'#fff'}}>
                  <svg width={24} height={24} viewBox="0 0 24 24" fill="currentColor">{isPlaying?<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>:<path d="M8 5v14l11-7z"/>}</svg>
                </button>
                <SkipButtonDesktop direction="back" onClick={()=>skip(-skipAmount)} currentTier={currentTier} isMobile={false} seconds={skipAmount} />
                <SkipButtonDesktop direction="forward" onClick={()=>skip(skipAmount)} currentTier={currentTier} isMobile={false} seconds={skipAmount} />
                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <button aria-label={isMuted||volume===0?'Unmute':'Mute'} onClick={(e)=>{e.stopPropagation();setIsMuted(!isMuted);}} style={{padding:'8px',background:'none',border:'none',cursor:'pointer',color:'#fff'}}>
                    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isMuted||volume===0?<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></>:<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></>}</svg>
                  </button>
                  <div ref={volumeRef} onClick={handleVolumeClick} onTouchStart={(e)=>{e.stopPropagation();handleVolumeChange(e.touches[0].clientX);}} onTouchMove={handleVolumeTouchMove} style={{width:'80px',height:'4px',background:'rgba(255,255,255,0.3)',borderRadius:'2px',cursor:'pointer',position:'relative'}}>
                    <div style={{width:`${isMuted?0:volume}%`,height:'100%',borderRadius:'2px',pointerEvents:'none',...vStyles}} />
                    <div style={{position:'absolute',left:`${isMuted?0:volume}%`,top:'50%',transform:'translate(-50%,-50%)',width:'10px',height:'10px',background:'#fff',borderRadius:'50%',pointerEvents:'none'}} />
                  </div>
                </div>
                <span style={{fontSize:'13px',color:'rgba(255,255,255,0.9)',fontFamily:'"DM Sans",sans-serif',fontVariantNumeric:'tabular-nums'}}>{formatTime(currentTime)} / {formatTime(totalSeconds)}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                <div style={{position:'relative'}}>
                  <button onClick={(e)=>{e.stopPropagation();setShowSpeedMenu(!showSpeedMenu);}} style={{padding:'6px 12px',background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'4px',cursor:'pointer',color:'#fff',fontSize:'13px',fontFamily:'"DM Sans",sans-serif',fontWeight:'500'}}>{playbackSpeed===1?'Normal':`${playbackSpeed}x`}</button>
                  {speedMenuEl}
                </div>
                {subtitleOptions.length > 0 && (
                  <div style={{position:'relative'}}>
                    <button aria-label="Subtitles" aria-pressed={activeSubtitleKey!=='off'} onClick={(e)=>{e.stopPropagation();setShowSpeedMenu(false);setShowSubtitleMenu(!showSubtitleMenu);}} style={{padding:'8px',background:'none',border:'none',cursor:'pointer',color:activeSubtitleKey!=='off'?colors.accent:'#fff'}}><svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 12h4M13 12h4M7 16h10"/></svg></button>
                    {subtitleMenuEl}
                  </div>
                )}
                <button aria-label="Picture-in-Picture" onClick={(e)=>{e.stopPropagation();togglePiP();}} style={{padding:'8px',background:'none',border:'none',cursor:'pointer',color:'#fff'}}><svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor" stroke="none"/></svg></button>
                <button aria-label={isFullscreen?'Exit full screen':'Full screen'} onClick={(e)=>{e.stopPropagation();toggleFullscreen();}} style={{padding:'8px',background:'none',border:'none',cursor:'pointer',color:'#fff'}}>
                  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isFullscreen?<path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3"/>:<path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3"/>}</svg>
                </button>
              </div>
            </div>
) : compactRow}
          </div>
        </div>
      </>}

      <style>{`
        @keyframes pulse{0%,100%{box-shadow:0 8px 32px rgba(0,0,0,0.4),0 0 0 0 rgba(255,255,255,0.4)}50%{box-shadow:0 8px 32px rgba(0,0,0,0.4),0 0 0 12px rgba(255,255,255,0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>
      </div>
      {/* Shortcut sheet (1.25): ? opens it; Esc, ? or a click closes it. */}
      {showShortcuts && (
        <div role="dialog" aria-label="Keyboard shortcuts" onClick={(e) => { e.stopPropagation(); setShowShortcuts(false); }}
          style={{ position: 'absolute', inset: 0, zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', fontFamily: '"DM Sans",sans-serif' }}>
          <div style={{ minWidth: '300px', maxWidth: '90%', padding: '18px 22px', borderRadius: '14px', background: 'rgba(20,20,20,0.97)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
            <div style={{ fontFamily: '"Cormorant Garamond",Georgia,serif', fontSize: '22px', marginBottom: '10px' }}>Keyboard shortcuts</div>
            {[['Space / K', 'Play or pause'], ['J / L', 'Back or forward 10 s'], ['← / →', 'Back or forward (while playing)'], ['< / >', 'Slower or faster'], ['C', 'Subtitles on or off'], ['0–9', 'Jump to 0–90%'], ['M', 'Mute'], ['F', 'Full screen'], ['?', 'This list']].map(([k, d]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', padding: '5px 0', fontSize: '13.5px', color: 'rgba(255,255,255,0.85)' }}>
                <span>{d}</span>
                <kbd style={{ padding: '2px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', fontFamily: 'inherit', fontWeight: 700, color: '#fff' }}>{k}</kbd>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* End of lesson (1.23): Up next / Unlock / Lesson complete, over the video. */}
      {ended && endCardEnabled && !nativeFs && <EndOfLessonCard colors={colors} upNext={upNext} upNextLocked={upNextLocked} info={endCard} onUpNext={onUpNext} onUnlock={onUnlock} onReplay={replay} />}
    </div>
  );
}

// ════════════════ Tablet player · moved as is from VideoViewPageTablet.jsx (MobilePlayer) ════════════════

const PLAYBACK_SPEEDSTablet = [0.5, 0.75, 1, 1.25, 1.5, 2];

// ============================================================================
// TIER-SPECIFIC STYLE HELPERS
// ============================================================================
const getSkipButtonStylesTablet = (currentTier, isHovered, isMobile) => {
  const base = { position:'relative', borderRadius:isMobile?'16px':'20px', cursor:'pointer', display:'flex', alignItems:'center', gap:isMobile?'5px':'8px', padding:isMobile?'7px 12px':'12px 20px', transition:'all 0.4s cubic-bezier(0.4,0,0.2,1)', overflow:'hidden', border:'none', fontFamily:'"DM Sans",sans-serif', WebkitTapHighlightColor:'transparent' };
  if (tierRank(currentTier) === 0) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(26,26,26,0.5),rgba(42,42,42,0.45))':'linear-gradient(135deg,rgba(26,26,26,0.4),rgba(42,42,42,0.35))', border:`1px solid ${isHovered?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.2)'}`, boxShadow:isHovered?'0 4px 16px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.2)':'0 2px 8px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.15)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  if (tierRank(currentTier) === 1) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(201,169,98,0.35),rgba(201,169,98,0.25))':'linear-gradient(135deg,rgba(201,169,98,0.25),rgba(201,169,98,0.15))', border:`1px solid ${isHovered?'rgba(201,169,98,0.6)':'rgba(201,169,98,0.4)'}`, boxShadow:isHovered?'0 6px 24px rgba(0,0,0,0.4),0 0 36px rgba(201,169,98,0.4),inset 0 1px 0 rgba(201,169,98,0.5)':'0 3px 12px rgba(0,0,0,0.3),0 0 20px rgba(201,169,98,0.18),inset 0 1px 0 rgba(201,169,98,0.3)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  return { ...base, background:isHovered?'linear-gradient(135deg,rgba(232,213,163,0.4),rgba(232,213,163,0.3))':'linear-gradient(135deg,rgba(232,213,163,0.3),rgba(232,213,163,0.2))', border:`1px solid ${isHovered?'rgba(232,213,163,0.7)':'rgba(232,213,163,0.5)'}`, boxShadow:isHovered?'0 8px 32px rgba(0,0,0,0.5),0 0 44px rgba(232,213,163,0.45),inset 0 1px 0 rgba(232,213,163,0.6)':'0 4px 16px rgba(0,0,0,0.4),0 0 24px rgba(232,213,163,0.24),inset 0 1px 0 rgba(232,213,163,0.4)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
};
const getSkipIconColorTablet = (t,h) => tierRank(t)===0?'#FFF':tierRank(t)===1?(h?'#E8D5A3':'#C9A962'):(h?'#FFF8E7':'#E8D5A3');
const getSkipNumberColorTablet = (t) => tierRank(t)===0?'#FFF':tierRank(t)===1?'#C9A962':'#E8D5A3';
const getProgressStylesTablet = (t) => {
  if (tierRank(t)===0) return { played:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.4)'}, thumb:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.5)'} };
  if (tierRank(t)===1) return { played:{background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 12px rgba(201,169,98,0.5)'}, thumb:{background:'linear-gradient(135deg,#E8D5A3,#C9A962)',boxShadow:'0 0 12px rgba(201,169,98,0.6)'} };
  return { played:{background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 16px rgba(232,213,163,0.6)'}, thumb:{background:'linear-gradient(135deg,#FFF8E7,#E8D5A3)',boxShadow:'0 0 16px rgba(232,213,163,0.7)'} };
};

// ============================================================================
// SKIP BUTTON - Pill-shaped with tier styling
// ============================================================================
function SkipButtonTablet({ direction, onClick, currentTier, isMobile, seconds=15, big=false }) {
  const [isHovered, setIsHovered] = useState(false);
  // big: the tablet centre cluster, a full 44px touch target
  const styles = { ...getSkipButtonStylesTablet(currentTier, isHovered, isMobile), ...(big ? { minHeight:'44px', padding:'0 14px', borderRadius:'18px', gap:'6px' } : {}) };
  const iconColor = getSkipIconColorTablet(currentTier, isHovered);
  const numberColor = getSkipNumberColorTablet(currentTier);
  const iconSize = big?14:isMobile?13:18, numberSize = big?14:isMobile?12:16, unitSize = big?10:isMobile?9:12;
  const iconFilter = tierRank(currentTier)===0?'drop-shadow(0 1px 3px rgba(0,0,0,0.5))':tierRank(currentTier)===1?(isHovered?'drop-shadow(0 0 8px rgba(201,169,98,0.5))':'drop-shadow(0 0 4px rgba(201,169,98,0.3))'):(isHovered?'drop-shadow(0 0 12px rgba(232,213,163,0.6))':'drop-shadow(0 0 6px rgba(232,213,163,0.4))');
  return (
    <button onClick={onClick} onMouseEnter={()=>setIsHovered(true)} onMouseLeave={()=>setIsHovered(false)} style={styles}>
      {direction==='back' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M19 12H5M12 5l-7 7 7 7"/></svg>}
      <span style={{display:'flex',alignItems:'baseline',gap:'2px',color:'#F5F5F0',textShadow:isLightTheme(currentTier)?'0 1px 3px rgba(0,0,0,0.5)':'0 1px 2px rgba(0,0,0,0.3)'}}>
        <span style={{fontSize:`${numberSize}px`,fontWeight:'700',color:numberColor,letterSpacing:'-0.02em'}}>{seconds}</span>
        <span style={{fontSize:`${unitSize}px`,fontWeight:'500',opacity:0.8}}>s</span>
      </span>
      {direction==='forward' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
    </button>
  );
}

// ============================================================================
// VIDEO PLAYER COMPONENT
// ============================================================================
// ┌───────────────────────────────────────────────────────────────────────────┐
// │ TEST HOOK — pin playback to ONE rung of the ladder.                       │
// │   ?forceVariant=v0   on the PAGE url → play v0/video.m3u8 directly        │
// │   ?forceVariant=v3                   → play v3/video.m3u8 directly        │
// │   (absent)                           → normal, master.m3u8               │
// │ Remove this function and the useMemo in VideoPlayer to revert.            │
// └───────────────────────────────────────────────────────────────────────────┘
// WHY A URL PARAM AND NOT A CONFIG:
//   In SRC_EQUALS (native HLS) Shaka does NOT choose the variant — Safari does. So
//   abr.restrictions / player.selectVariantTrack() are inert here. The only way to
//   pin a rung is to hand the player a MEDIA playlist instead of the MASTER; a media
//   playlist is directly playable and contains exactly one rendition.
//
// WHAT IT SEPARATES:
//   Every variant is encrypted with the SAME key (verified: one KEYID across v0-v3
//   and audio). So resolution cannot affect decryption.
//     v0 (1280x800) PLAYS  → the key is fine. The failure is the codec ladder —
//                            v3 is avc1.640034 @ 3456x2234@60 (H.264 High L5.2 at a
//                            non-standard size), which a hardware decoder can refuse
//                            outright while Chrome falls back to software.
//     v0 FAILS TOO         → resolution is irrelevant, the key is wrong, and it is
//                            DRMtoday's ingest.
//
// NOTE: a bare variant playlist has NO AUDIO — the master's EXT-X-MEDIA declares the
// audio group. Silent video is EXPECTED here and is not a fault.
//
// The CDN token is prefix-scoped to videos/<slug>, so it already authorizes
// v0/video.m3u8 and its segments. No re-mint needed.
function applyVariantOverrideTablet(url) {
  if (!url) return url;
  let variant = null;
  try { variant = new URLSearchParams(window.location.search).get('forceVariant'); } catch { return url; }
  if (!variant || !/^[a-z0-9_-]+$/i.test(variant)) return url;
  const out = url.replace(/\/master\.m3u8(\?|$)/, '/' + variant + '/video.m3u8$1');
  return out;
}

// ── legacy FairPlay initData ─────────────────────────────────────────────────
// MEASURED 2026-07-16 via the raw-EME harness: on this Safari (26.3.1), native HLS
// FairPlay fires the LEGACY `webkitneedkey` event, not modern `encrypted` — even
// though requestMediaKeySystemAccess('com.apple.fps') succeeds and setMediaKeys
// attaches. The legacy event's initData is NOT a plain UTF-8 string:
//
//     [uint32 little-endian: byteLength of the URI][the URI as UTF-16LE]
//
// A 55-char skd URI arrives as 114 bytes (4 + 55*2). Read as UTF-8 it looks like
// "nskd://drmtoday?..." — the 'n' is 0x6E = 110, the length byte — and every second
// byte is an invisible NUL. Any regex against that string silently fails to match.
//
// Shaka's polyfill.installAll() normalises this before initDataTransform runs, which
// is why the app has always logged a clean "skd://…". These helpers exist so the
// transform can handle EITHER shape and, crucially, RETURN THE SHAPE IT RECEIVED.
function parseSkdInitDataTablet(initData) {
  const u8 = initData instanceof Uint8Array ? initData : new Uint8Array(initData);
  if (u8.byteLength >= 6) {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const byteLen = dv.getUint32(0, true);
    if (byteLen + 4 === u8.byteLength && byteLen % 2 === 0) {
      const uri = new TextDecoder('utf-16le').decode(new Uint8Array(u8.buffer, u8.byteOffset + 4, byteLen));
      if (uri.startsWith('skd:')) return { uri, shape: 'legacy_len_utf16le' };
    }
  }
  return { uri: shaka.util.StringUtils.fromBytesAutoDetect(initData), shape: 'plain' };
}

const fmtTimeTablet = (s) => {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

// ── constants the mobile player expects ──────────────────────────────────────
// If the media never reports ready, stop showing a spinner forever and let the
// user press play themselves. A stuck `isLoading` used to hide the controls
// behind an overlay with no way out.
const LOADING_GIVEUP_MSTablet = 15000;

// +/-10s on mobile (the desktop page uses 15).
const SKIP_SECONDSTablet = 10;

// ============================================================================
// PLAYER — copied verbatim from VideoViewPageMobile.jsx
// ============================================================================

// ── PICTURE IN PICTURE BUTTON (tablet) ───────────────────────────────────────
// OFF for now, to be worked on later. false = the tablet option row has no
// Picture in Picture button. Only our button is hidden: nothing is blocked in
// the browser. The code behind it (togglePiP) stays, so true brings it back.
const PIP_BUTTON_ENABLED = false;

function MobilePlayerTablet({
  video, colors, currentTier, isPlaying, setIsPlaying, currentTime, setCurrentTime,
  videoUrl: rawVideoUrl, videoId, assetId, resumeFrom = 0,
  upNext = null, upNextLocked = false, onUpNext, onUnlock, endCard = null, refreshLink,
}) {
  // Muxed package swap: the FairPlay path plays the single-track SAMPLE-AES TS
  // package, not the demuxed cbcs fMP4 one. The desktop page also layers
  // ?forceVariant on top of this; that diagnostic hook is deliberately not
  // carried over here.
  // ?forceVariant test hook, same as the desktop Shaka page. There is no muxed
  // package swap here — that is a FairPlay-only concern (single-track SAMPLE-AES
  // TS); Widevine plays the demuxed cbcs fMP4 package directly.
  const videoUrl = useMemo(() => applyVariantOverrideTablet(rawVideoUrl), [rawVideoUrl]);

  const containerRef = useRef(null);   // fullscreen target
  const videoRef = useRef(null);       // REACT-owned <video> (Shaka attaches to it)
  const shakaRef = useRef(null);       // shaka.Player instance
  const cdnTokenRef = useRef(null);    // live CDN token, re-appended to every shaka request (refreshable)
  const guardRef = useRef(freshGuard());   // request guard (see the top of the file)
  const restartRef = useRef(null);         // restartFromLink, for the effects declared above it
  const [stopped, setStopped] = useState(0);   // bumps on every stop and restart, so the recovery effect re-reads the guard
  const [linkOverlay, setLinkOverlay] = useState(null);   // null | 'loading' | 'resuming' | 'ready' (step 2's overlay; 'resuming' = the Resume tap's own reload)
  const resumingRef = useRef(false);   // the Resume tap's reload is in progress: its own play() must not close the overlay
  const progressRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const { token, profile, api } = useAuth();
  const authTokenRef = useRef(token);
  authTokenRef.current = token;

  const API_CONTENT_BASE = process.env.REACT_APP_CONTENT_API_URL || process.env.REACT_APP_API_URL || '';
  const DRMTODAY_MERCHANT = process.env.REACT_APP_DRMTODAY_MERCHANT || 'f100090a-e116-42ba-b03a-1d157c6be3f3';

  const latestRef = useRef({ pos: 0, dur: 0 });
  const resumedRef = useRef(false);
  const resumeFromRef = useRef(resumeFrom);
  const videoUrlRef = useRef(videoUrl);
  videoUrlRef.current = videoUrl;

  // Heartbeats (the 10-second send while playing, and the final send on unmount) live in useHeartbeat.
  const heartbeatOnTime = useHeartbeat(videoId);

  const [showControls, setShowControls] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Saved speed (1.22): start from the profile's preferredPlaybackSpeed when it is one of our speeds, else 1×.
  const [playbackSpeed, setPlaybackSpeed] = useState(() => { const s = Number(profile && profile.preferredPlaybackSpeed); return PLAYBACK_SPEEDSTablet.includes(s) ? s : 1; });
  const [showSpeedSheet, setShowSpeedSheet] = useState(false);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(!!videoUrl);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!error) { setShowError(false); return; }
    const t = setTimeout(() => setShowError(true), 600);
    return () => clearTimeout(t);
  }, [error]);

  // Give up on the spinner rather than covering the player forever.
  useEffect(() => {
    if (!isLoading) return undefined;
    const t = setTimeout(() => setIsLoading(false), LOADING_GIVEUP_MSTablet);
    return () => clearTimeout(t);
  }, [isLoading]);

  useEffect(() => { resumedRef.current = false; resumeFromRef.current = resumeFrom; }, [videoUrl, resumeFrom]);

  const isHls = videoUrl && /\.m3u8($|\?)/.test(videoUrl);
  const videoUrlKey = videoUrl ? String(videoUrl).split('?')[0] : null;

  // Keep the CDN token current WITHOUT restarting playback.
  useEffect(() => {
    try { cdnTokenRef.current = new URL(videoUrl).searchParams.get('t'); }
    catch { cdnTokenRef.current = null; }
    // Request guard: the clock check (useCDNToken's rule) on the link that just arrived, then a fresh start if
    // the player is stopped. A fresh link resets the timed ladder.
    const guard = guardRef.current;
    const left = cdnTokenSecondsLeft(videoUrl);
    guard.clockOk = left === null || left >= CDN_TOKEN_VIDEO_MAX_SECONDS;
    if (guard.stopped) { guard.tries = 0; if (restartRef.current) restartRef.current(); }
  }, [videoUrl]);

  const totalSeconds = duration || 0;
  // Scrubbing (touch): while a finger drags the bar, the thumb and time follow it without
  // seeking; the video seeks once, on release (scrubTo / endScrub, beside seekTo).
  const [scrubTime, setScrubTime] = useState(null);
  const scrubRef = useRef(null);
  const pct = totalSeconds > 0 ? ((scrubTime ?? currentTime) / totalSeconds) * 100 : 0;
  const bufPct = totalSeconds > 0 ? (buffered / totalSeconds) * 100 : 0;
  const pStyles = getProgressStylesTablet(currentTier);

  useEffect(() => {
    if (!videoUrl || !videoRef.current) return;
    const vid = videoRef.current;

    // tear down any prior instance
    if (shakaRef.current) { shakaRef.current.destroy(); shakaRef.current = null; }
    if (guardRef.current.stopped) setStopped((n) => n + 1);   // request guard: the old lesson's recovery stands down
    guardRef.current = { ...freshGuard(), clockOk: guardRef.current.clockOk };   // request guard: a new player, a clean slate

    // Non-DRM / non-HLS source (e.g. a plain mp4 preview): just set src.
    if (!isHls) { vid.src = videoUrl; return; }

    shaka.polyfill.installAll();

    // ── MSE, NOT SAFARI'S NATIVE HLS ─────────────────────────────────────────
    // Shaka's README: "By default, FairPlay is handled using Apple's native HLS player,
    // when on Safari. We do support FairPlay through MSE/EME, however. See the
    // streaming.useNativeHlsForFairPlay configuration value."
    //
    // Shaka's FairPlay support matrix:
    //                    Modern EME   legacy Apple Media Keys
    //     src= (CMAF)    Y            Y
    //     MSE  (CMAF)    Y            -            ← MSE requires MODERN EME
    // Our content is CMAF (fMP4 .m4s), so MSE + com.apple.fps is a supported combo.
    //
    // WHY WE LEFT THE NATIVE HLS PATH — everything below was measured on it:
    //   1. Modern EME + native HLS: license 200, CKC accepted, ZERO frames, no error.
    //      Safari's NATIVE setMediaKeys() does not enable FairPlay decryption for
    //      native HLS; only webkitSetMediaKeys() does (shaka-player#9794).
    //   2. legacy Apple Media Keys + the legacy container: video DECRYPTS and renders.
    //      But the packager gives audio and video the SAME skd content-id
    //      (--hls_key_uri is a single global flag — shaka-packager#439/#470), so both
    //      raise webkitneedkey with identical init data, Shaka dedupes the second, and
    //      audio is never keyed. Safari's clock is AUDIO-driven → currentTime pinned at
    //      0.00 with 12s of video decoded → frozen first frame.
    //   3. Forcing a session per event → 6006: the CDM rejects a second session for a
    //      content-id it already holds.
    //   4. Giving audio a distinct content-id (Worker appends &trk=audio; DRMtoday
    //      ignores unknown params — measured 200) → TWO licenses, both 200, both
    //      sessions updated → STILL FROZEN.
    //
    // (4) is shaka-player#9268: "FairPlay multi-key content intermittently fails to
    // play (3016)... Shaka requests a license for only one of the two tracks... the
    // same assets play correctly in Video.js and other HTML5 players." Open, unfixed.
    // The native-HLS FairPlay path is broken for multi-track content.
    //
    // MSE sidesteps ALL of it. Shaka parses the manifest, fetches the segments, and
    // drives EME itself — exactly the path Widevine already uses successfully here.
    // No webkitneedkey, no session-per-track, no dedupe, no Safari native-HLS quirks.
    // Modern EME is required for MSE, so no PatchedMediaKeysApple.
    const FPS_KEYSYSTEM = 'com.apple.fps';

    if (!shaka.Player.isBrowserSupported()) { setError('This browser can’t play protected video'); return; }

    // The signed CDN needs ?t=<token> on EVERY request (manifest + segments). Shaka resolves
    // child playlists/segments relative to the manifest, dropping the query — so a request
    // filter re-appends the token (read live from a ref so a refresh can swap it).
    // cdnTokenRef is kept live by its own effect above — reading it here would race
    // with a re-mint and pin a stale token for the life of the player.

    const player = new shaka.Player();
    shakaRef.current = player;

    const licenseBase = (API_CONTENT_BASE || '').replace(/\/$/, '');
    const widevineLicense = `${licenseBase}/license/widevine?assetId=${encodeURIComponent(assetId || '')}`;
    // FairPlay (Safari/iOS) goes to our endpoint too; the route forwards to DRMtoday's
    // FairPlay license server and mints the same proven auth token server-side.
    const fairplayLicense = `${licenseBase}/license/fairplay?assetId=${encodeURIComponent(assetId || '')}`;
    // DRMtoday's FairPlay CERTIFICATE endpoint (public; keyed by merchant). Shaka fetches
    // this before it can build the SPC. Confirmed from DRMtoday integration docs.
    // ── FairPlay certificate ─────────────────────────────────────────────────
    // MEASURED 2026-07-16: the cert served at /cert/<merchant> is RSA-1024
    // (modulus 0x02 0x81 0x81 → 128 bytes), serial 2777B10FB9358DC3, notBefore
    // 2026-07-13. RSA-1024 is the OLD FPS generation; SDK 4.0+ certificates are
    // RSA-2048. The comment above says we need SDK 26 credentials — so the default
    // being served may be the wrong bundle entirely.
    //
    // WHY THAT PRODUCES EXACTLY OUR SYMPTOM:
    //   Apple ships certificate + private key + ASk as ONE bundle. Our HTTP 200
    //   proves the cert/private-key pair matches — the KSM could not decrypt the SPC
    //   otherwise. It proves NOTHING about the ASk: the ASk never touches SPC
    //   decryption, nothing validates it, and a mismatched one yields a well-formed,
    //   correctly-signed CKC carrying a GARBAGE content key. The CDM accepts it
    //   (session.update resolves), then decodes noise → MEDIA_ERR_DECODE, zero frames,
    //   no error. Which is what we have.
    //
    // TESTING ANOTHER BUNDLE WITHOUT TOUCHING THE DASHBOARD:
    //   The SPC carries a hash identifying which certificate the client used, so a KSM
    //   holding several bundles picks the matching private key + ASk from it. Swapping
    //   the cert here therefore swaps which bundle DRMtoday answers with — no default
    //   change, no risk to other sessions.
    //
    //   Drop the .cer files (DER, as Apple issued them) into public/ — same-origin, so
    //   no CORS — then:
    //       ?certUrl=/fps-2048.cer      ?certUrl=/fps-1024.cer      (no param = default)
    //   Whichever one PLAYS is the bundle whose ASk DRMtoday actually holds.
    //
    //   FairPlay certificates have NOTHING to do with Widevine — different DRM,
    //   different license server, no Apple cert in that path. This cannot affect Chrome.
    // HOW TO USE — from the Safari console, no URL, no file, no deploy of the cert:
    //
    //   1. terminal:   base64 -i cert_2.der | tr -d '\n' | pbcopy
    //   2. console:    sessionStorage.setItem('fpsCertB64', '<paste>'); location.reload()
    //   3. to revert:  sessionStorage.removeItem('fpsCert64'); location.reload()
    //
    // sessionStorage survives the reload and dies with the tab. The certificate is
    // PUBLIC — every Safari downloads it before playback, and an unauthenticated curl
    // fetches it — so this leaks nothing. The private key and ASk are never involved.
    //
    // ?certUrl=<url> also works if you'd rather point at a hosted .cer.
    let certOverride = null;
    try {
      const b64 = window.sessionStorage.getItem('fpsCertB64');
      if (b64) { certOverride = 'data:application/octet-stream;base64,' + b64.trim(); }
      else {
        const u = new URLSearchParams(window.location.search).get('certUrl');
        if (u) { certOverride = u; }
      }
    } catch { /* fall through to the default */ }
    const DRMTODAY_FPS_CERT = certOverride || `https://lic.drmtoday.com/license-server-fairplay/cert/${DRMTODAY_MERCHANT}`;

    player.configure({
      drm: {
        // TRIED AND REVERTED 2026-07-17: ignoreDuplicateInitData:false.
        // Reasoning was that initDataTransform fires twice (video + audio) but only one
        // license request goes out, so the second track never gets keyed. Forcing a
        // session per webkitneedkey instead produced
        //   6006 FAILED_TO_GENERATE_LICENSE_REQUEST — "EME PatchedMediaKeysApple key error"
        // i.e. the CDM rejects a SECOND session for the same content-id outright. So the
        // duplicate suppression is correct here and the stall has another cause.
        servers: {
          'com.widevine.alpha': widevineLicense,
          // FPS_KEYSYSTEM is 'com.apple.fps.1_0' — legacy Apple Media Keys. See the
          // block above for why. (Was 'com.apple.fps' / Modern EME, which never
          // enabled decryption for native HLS.)
          [FPS_KEYSYSTEM]: fairplayLicense,
        },
        advanced: {
          'com.widevine.alpha': { persistentStateRequired: false },
          [FPS_KEYSYSTEM]: {
            persistentStateRequired: false,
            distinctiveIdentifierRequired: false,
            serverCertificateUri: DRMTODAY_FPS_CERT,
          },
        },
      },
      streaming: {
        // THE CHANGE. Default is true → Safari's native HLS player → every FairPlay
        // bug we spent this investigation on. false → Shaka drives MSE/EME itself.
        //
        // Side effect worth knowing: in MSE mode SHAKA fetches the segments, so the
        // request filter below re-appends ?t= to each one. The Worker's manifest
        // rewrite becomes belt-and-braces rather than load-bearing (it only mattered
        // because NATIVE players drop the query when resolving relative URLs).
        useNativeHlsForFairPlay: false,
        rebufferingGoal: 4,
        bufferingGoal: 30,
      },
    });

    // ── initDataTransform — MODERN EME ───────────────────────────────────────
    // Modern EME takes the content-id as plain bytes. It must NOT be wrapped in
    // Apple's legacy container ([len][initData][len][contentId UTF-16][len][cert]) —
    // that is what legacy Apple Media Keys requires, and feeding it to Modern EME
    // produced SPC_CONTAINER_HEADER_MALFORMED. The container code is gone with the
    // legacy path; if you ever go back to com.apple.fps.1_0, it must come back too.
    //
    // CONTENT-ID: measured against DRMtoday, only one form returns 200:
    //     skd://drmtoday?assetId=X&variantId=hd  → 400 (x-dt-resp-code 10003, 10001)
    //     skd://drmtoday?assetId=X               → 200   ← this one
    //     ?assetId=X&variantId=hd                → 400
    //     ?assetId=X                             → 400
    // The keys were ingested under assetId alone, so we strip the variantId the
    // packager wrote. DRMtoday ignores unknown params (measured), so the Worker's
    // &trk=audio on the audio playlist survives this strip harmlessly — [^&]* stops
    // at the next '&'.
    player.configure('drm.initDataTransform', (initData, initDataType) => {
      if (initDataType !== 'skd') return initData;
      const { uri } = parseSkdInitDataTablet(initData);
      const contentId = uri.replace(/[?&]variantId=[^&]*/i, '');
      return shaka.util.StringUtils.toUTF8(contentId);
    });

    // Attach auth + DRM-system-specific request/response handling.
    const net = player.getNetworkingEngine();
    net.registerRequestFilter((type, request) => {
      const RT = shaka.net.NetworkingEngine.RequestType;
      if (type === RT.LICENSE) {
        // license call goes to OUR content-api → attach the user's bearer token so the
        // endpoint can check entitlement before minting the DRMtoday token.
        // Read from the ref, NOT the closure: a refreshed JWT must reach the next
        // license request without the effect (and the player) being rebuilt.
        const authToken = authTokenRef.current;
        if (authToken) request.headers['Authorization'] = `Bearer ${authToken}`;
        // FairPlay: the CDM message must be sent as spc=<base64> (form-urlencoded).
        const isFairPlay = request.uris.some((u) => u.indexOf('/license/fairplay') > -1);
        if (isFairPlay && request.body) {
          const payload = new Uint8Array(request.body);
          const b64 = shaka.util.Uint8ArrayUtils.toStandardBase64(payload);
          request.headers['Content-Type'] = 'application/x-www-form-urlencoded';
          request.body = shaka.util.StringUtils.toUTF8('spc=' + encodeURIComponent(b64));
        }
      } else {
        // manifest + segments → CDN, re-append ?t=<token>
        const t = cdnTokenRef.current;
        // Request guard: a link that has run out is never sent (the CDN would only refuse it), nor anything once
        // the player is stopped. Shaka turns the throw into a failed request (not retried); during a load() that
        // rejects the load, during playback it reaches the error listener below.
        if (guardRef.current.stopped || (guardRef.current.clockOk && cdnTokenExpired(t))) throw new Error('cdn link expired');
        if (t) {
          request.uris = request.uris.map((u) => {
            try { const url = new URL(u); url.searchParams.set('t', t); return url.toString(); }
            catch { return u; }
          });
        }
      }
    });

    // FairPlay response: DRMtoday returns the CKC (sometimes wrapped). Unwrap it into the
    // raw base64 the CDM expects. Widevine responses are handled by our endpoint (raw bytes).
    net.registerResponseFilter((type, response) => {
      const RT = shaka.net.NetworkingEngine.RequestType;
      if (type !== RT.LICENSE) return;
      const isFairPlay = response.uri && response.uri.indexOf('/license/fairplay') > -1;
      if (!isFairPlay) return;
      let txt = shaka.util.StringUtils.fromUTF8(response.data).trim();
      // strip a <ckc>…</ckc> wrapper if present
      const wrapped = txt.startsWith('<ckc>') && txt.endsWith('</ckc>');
      if (wrapped) txt = txt.slice(5, -6);
      // A throw here propagates as a load failure, which is the correct outcome:
      // a CKC that isn't base64 is not something the CDM can be handed.
      response.data = shaka.util.Uint8ArrayUtils.fromBase64(txt).buffer;
    });

    // Request guard: every failed CDN request counts, the ones shaka means to retry included (it reports those
    // as recoverable). Past the budget, or on a link that has run out, the player is unloaded and holds its spot;
    // restartFromLink (below) is the only way on from there.
    const noteFailedRequest = () => {
      const guard = guardRef.current;
      const now = Date.now();
      guard.failures = guard.failures.filter((at) => now - at < CDN_GUARD_WINDOW_SECONDS * 1000);
      guard.failures.push(now);
      const expired = guard.clockOk && cdnTokenExpired(cdnTokenRef.current);
      if (!expired && guard.failures.length < CDN_GUARD_MAX_FAILURES) return false;
      if (guard.stopped) return true;
      const cur = (videoRef.current && videoRef.current.currentTime) || 0;
      guard.pos = cur > 0 ? cur : (latestRef.current.pos > 0 ? latestRef.current.pos : guard.pos);   // the spot survives a stop during a restart
      guard.key = videoUrlKey;
      guard.stopped = true;
      guard.failures = [];
      guard.loading = false;   // unload() interrupts any load in flight (it rejects with 7000, which is ignored)
      player.unload().catch(() => {});
      setIsPlaying(false);   // unload() pauses the element without a pause event: the page must know
      // The overlay is the catch-all (October 30, 2026): with a spot to come back to, a stop never shows the error
      // screen. A dead link has nothing the player can try, so it is "Resume lesson" at once; a live link reloads
      // ("Reloading…") and the watchdog falls back to "Resume lesson". The tap refreshes the session, the link and
      // the video itself; only a tap that fails shows the error screen.
      setError(null); setLinkOverlay(expired ? 'ready' : 'loading');   // never the error screen: the overlay, and the tap recovers
      setIsLoading(false);
      setStopped((n) => n + 1);
      return true;
    };
    player.addEventListener('error', (e) => {
      const detail = e && e.detail ? e.detail : e;

      // LOAD_INTERRUPTED means WE cancelled the load (teardown/replacement). It is
      // bookkeeping, not a fault, and must never reach the user.
      if (detail && detail.code === 7000) return;
      if (detail && detail.category === shaka.util.Error.Category.NETWORK && noteFailedRequest()) return;

      // Shaka fires this for BOTH recoverable (severity 1) and critical (severity 2)
      // errors. Recoverable ones self-heal (buffering, a retried segment) and must NOT
      // put up the failure overlay — doing so left a stuck "Failed to load" over a video
      // that was actually playing. Only surface CRITICAL errors.
      const severity = detail && detail.severity;
      if (severity && severity !== shaka.util.Error.Severity.CRITICAL) return;
      const code = detail && detail.code;
      if (code === 403 || code === 6007 || code === 6001) setError('You don’t have access to this video');
      else {
        // The overlay, not the error screen, wherever there is a spot to come back to (October 30, 2026): the tap
        // reloads the video from a fresh session token and a fresh link.
        const v = videoRef.current; const at = (v && v.currentTime > 0) ? v.currentTime : (latestRef.current.pos || 0);
        guardRef.current.pos = at; setIsPlaying(false); setLinkOverlay('ready');
      }
      setIsLoading(false);
    });

    // Guards every async continuation below. Without this, a player torn down
    // mid-load still runs its catch and calls setError() — clobbering the player
    // that REPLACED it. The fetch effect has always had this; this one did not.
    let cancelled = false;

    (async () => {
      try {
        await player.attach(vid);
        if (cancelled) return;
        await player.load(videoUrlRef.current, resumeFromRef.current > 0 ? resumeFromRef.current : undefined);
        if (cancelled) return;

        setError(null);        // success → clear any stale/recoverable error state
        setIsLoading(false);
      } catch (err) {
        // A superseded player is EXPECTED to reject. It is not a user-visible failure
        // and must not touch shared state — the player that replaced it owns that now.
        if (cancelled || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        const code = err && err.code;
        const spot = resumeFromRef.current > 0 ? resumeFromRef.current : 0;
        if (code === 403 || code === 6007 || code === 6001) setError('You don’t have access to this video');
        else { guardRef.current.pos = spot; setLinkOverlay('ready'); }   // the overlay, never the error screen (October 30, 2026)
        setIsLoading(false);
        // Request guard: a load that failed on the network (a refused manifest, a dead link) is a stopped player
        // too: it holds the resume spot and tries again on a link, a gesture or the ladder, never on its own.
        if (err && err.category === shaka.util.Error.Category.NETWORK) {
          const guard = guardRef.current;
          guard.stopped = true; guard.key = videoUrlKey; guard.pos = spot;
          const dead = guard.clockOk && cdnTokenExpired(cdnTokenRef.current);
          setError(null); setLinkOverlay(dead ? 'ready' : 'loading');
          setStopped((n) => n + 1);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (shakaRef.current) { shakaRef.current.destroy(); shakaRef.current = null; }
    };
  }, [videoUrlKey, isHls, assetId, API_CONTENT_BASE]);

  // -- Play/pause: driven on the RAW <video>, not a player object --
  // This is the Shaka page's own effect, copied verbatim. Note the deliberate
  // difference from the FairPlay mobile page: there, play() is called on the
  // Video.js player and an AbortError is left alone because a late FairPlay key
  // can reject it. Widevine over MSE does not exhibit that, so the simpler
  // retry-muted-on-any-rejection behaviour is kept as-is rather than "improved".
  // -- Play/pause with muted fallback --
  // A rejected play() falls back to a muted retry (autoplay policy); if that also
  // fails the intent is cleared so the UI returns to a play button.
  // ── Starting playback (fix: "stuck after the next lesson", "the next lesson is muted") ─────────────────
  // A refused autoplay and an interrupted play are different things:
  //   NotAllowedError · the browser refuses sound without a tap (always so on iOS after an automatic lesson change).
  //     Start muted, which every browser allows, but only temporarily: "Tap for sound" shows, and the first tap,
  //     click or key press anywhere restores the sound, inside that tap (what iOS requires).
  //   anything else (AbortError: interrupted, e.g. by the stream still loading or a late FairPlay key) · never mute;
  //     show paused at once, with the play button; the student taps again (October 11: the retries are removed).
  const [autoMuted, setAutoMuted] = useState(false);
  useEffect(() => {
    if(videoUrl&&videoRef.current){
      if(isPlaying){
        let cancelled = false;
        const attempt = () => {
          const v = videoRef.current; if (cancelled || !v) return;
          const pr = v.play(); if (!pr || !pr.catch) return;
          pr.catch((err) => {
            if (cancelled || !videoRef.current) return;
            if (err && err.name === 'NotAllowedError') {
              if (videoRef.current.muted) { setIsPlaying(false); return; }   // refused even muted: show paused
              videoRef.current.muted = true; setIsMuted(true); setAutoMuted(true);
              const p2 = videoRef.current.play(); if (p2 && p2.catch) p2.catch(() => { if (!cancelled) setIsPlaying(false); });
            } else setIsPlaying(false);   // AbortError (interrupted): no retry; show paused, never mute
          });
        };
        attempt();
        return () => { cancelled = true; };
      }
      else videoRef.current.pause();
    }
    return undefined;
  }, [isPlaying, videoUrl, setIsPlaying]);
  useEffect(() => {
    if (!autoMuted) return undefined;
    // a tap on the player itself only restores the sound (it must not also pause or play); elsewhere it carries on.
    // M also means "restore the sound" here: the player's own M toggle would otherwise flip it straight back to muted.
    const restore = (e) => { const v = videoRef.current; if (v) v.muted = false; setIsMuted(false); setAutoMuted(false); if (e && e.type !== 'keydown' && containerRef.current && e.target && containerRef.current.contains(e.target)) { e.stopPropagation(); e.preventDefault(); } if (e && e.type === 'keydown' && (e.key === 'm' || e.key === 'M')) { e.stopImmediatePropagation(); e.preventDefault(); } };
    const o = { capture: true };
    window.addEventListener('touchend', restore, o); window.addEventListener('click', restore, o); window.addEventListener('keydown', restore, o);
    return () => { window.removeEventListener('touchend', restore, o); window.removeEventListener('click', restore, o); window.removeEventListener('keydown', restore, o); };
  }, [autoMuted]);
  useEffect(() => { if (!isMuted) setAutoMuted(false); }, [isMuted]);
  // "Tap for sound": big and centred for SOUND_PROMPT_BIG_MS, then the small corner pill until the sound is on.
  // Display only: a tap anywhere restores the sound either way (the effect above).
  const [soundPromptBig, setSoundPromptBig] = useState(false);
  useEffect(() => {
    if (!autoMuted) { setSoundPromptBig(false); return undefined; }
    setSoundPromptBig(true);
    const t = setTimeout(() => setSoundPromptBig(false), SOUND_PROMPT_BIG_MS);
    return () => clearTimeout(t);
  }, [autoMuted]);

  // TOUCH/CLICK DEDUPE — PER CONTROL.
  // iOS fires `touchend` and then synthesises a `click` for the same tap, so any
  // element with both handlers ran its action twice.
  //
  // The first version of this used ONE shared timestamp for every control, which
  // created a worse bug than the one it fixed: while the video plays the controls
  // auto-hide, so reaching any button takes TWO taps in quick succession — one on
  // the tap layer to reveal, one on the button. With a shared guard the second tap
  // fell inside the window and was discarded. Fullscreen and PiP looked completely
  // dead; play/pause only worked if you happened to pause between taps.
  //
  // The guard is now keyed per control, so a tap on the reveal layer can never
  // suppress the next tap on a different button.
  const tapGuards = useRef({});
  const once = useCallback((key, fn) => (e) => {
    // A handled touch cancels the browser's follow-up click (preventDefault), so one tap is
    // one action, and that click can never land on whatever just opened under the finger
    // (a "ghost click": it used to close the speed sheet the moment a tap opened it).
    // Quick repeated taps now all count. Safety net, still keyed per control: a click on
    // the same control within 400 ms of a handled touch is ignored, in case a browser sends one anyway.
    const now = Date.now();
    if (e && e.type === 'touchend') {
      if (e.cancelable && e.preventDefault) e.preventDefault();
      tapGuards.current[key] = now;
    } else if (now - (tapGuards.current[key] || 0) < 400) {
      return;
    }
    if (e && e.stopPropagation) e.stopPropagation();
    fn(e);
  }, []);

  // ── controls auto-hide ───────────────────────────────────────────────────
  const bump = useCallback(() => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (isPlaying) hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) { setShowControls(true); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
    return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); };
  }, [isPlaying]);

  useEffect(() => { if (isPlaying) setHasStarted(true); }, [isPlaying]);

  // WHY A DEDICATED TAP LAYER, AND NOT onClick ON THE CONTAINER:
  // On iOS a tap on a <video> element does not reliably emit a `click` that
  // bubbles to an ancestor — the media element swallows it. Relying on bubbling
  // meant that once the controls hid themselves (pointerEvents:'none'), the tap
  // landed on the video, nothing fired, and the controls could never be brought
  // back. This transparent sibling sits ABOVE the video and BELOW the controls,
  // and its pointerEvents are ALWAYS 'auto'.
  const toggleControls = useCallback(() => {
    if (showControls) { setShowControls(false); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else bump();
  }, [showControls, bump]);

  useEffect(() => { const v = videoRef.current; if (v) { v.muted = isMuted; v.volume = isMuted ? 0 : 1; } }, [isMuted]);
  // Hold for 2× (touch): while the finger is held on the video, play at 2×; on release, back to the chosen speed.
  const [hold, setHold] = useState(false);
  const holdRef = useRef({ timer: null, active: false, x: 0, y: 0 });
  useEffect(() => () => clearTimeout(holdRef.current.timer), []);
  // Media ready: counts each time the media reports its metadata (first load, a new lesson). The
  // speed and the subtitle tracks apply from this, because the <video> may not exist yet when the
  // player first appears, and isLoading can stay false if the player mounts before its URL.
  const [mediaReady, setMediaReady] = useState(0);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onReady = () => setMediaReady((n) => n + 1);
    v.addEventListener('loadedmetadata', onReady);
    return () => v.removeEventListener('loadedmetadata', onReady);
  }, [videoUrl]);

  // ── End of lesson (1.23) ──────────────────────────────────────────────────
  // The page passes upNext / upNextLocked / onUpNext / onUnlock to the LESSON player only, so the
  // answer-key player never shows the card. The video's end is detected by these new listeners;
  // the existing end handler is untouched.
  const endCardEnabled = typeof onUpNext === 'function' || typeof onUnlock === 'function';
  const [ended, setEnded] = useState(false);
  // Option row (tablet): the player's width decides whether the written labels fit, the two
  // menu buttons are remembered so each menu can open right above the button that was tapped,
  // and Picture in Picture shows only where the browser offers it for this video.
  const speedBtnRef = useRef(null);
  const subsBtnRef = useRef(null);
  const [playerWidth, setPlayerWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => setPlayerWidth(Math.round(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [pipAvailable, setPipAvailable] = useState(false);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) { setPipAvailable(false); return; }
    const webkitOk = typeof el.webkitSupportsPresentationMode === 'function' && typeof el.webkitSetPresentationMode === 'function'
      && el.webkitSupportsPresentationMode('picture-in-picture');
    const standardOk = !!document.pictureInPictureEnabled && typeof el.requestPictureInPicture === 'function' && !el.disablePictureInPicture;
    setPipAvailable(PIP_BUTTON_ENABLED && !!(webkitOk || standardOk));
  }, [videoUrl, mediaReady]);
  const [nativeFs, setNativeFs] = useState(false);     // iPhone's own full-screen player hides our card
  useEffect(() => {
    setEnded(false);
    const v = videoRef.current;
    if (!v) return undefined;
    const onEnd = () => setEnded(true);
    const onAgain = () => setEnded(false);
    v.addEventListener('ended', onEnd); v.addEventListener('play', onAgain); v.addEventListener('seeking', onAgain);
    return () => { v.removeEventListener('ended', onEnd); v.removeEventListener('play', onAgain); v.removeEventListener('seeking', onAgain); };
  }, [videoUrlKey]);   // a new video, not a new link (link refresh): a fresh link must not reset the end card
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const on = () => setNativeFs(true); const off = () => setNativeFs(false);
    el.addEventListener('webkitbeginfullscreen', on); el.addEventListener('webkitendfullscreen', off);
    return () => { el.removeEventListener('webkitbeginfullscreen', on); el.removeEventListener('webkitendfullscreen', off); };
  }, [videoUrl]);
  // No automatic advance: the next lesson starts only when the student taps "Play next lesson" (or Up Next),
  // so it always starts from a tap, the way every browser (iPad and iPhone included) lets a new video start.
  const replay = () => {
    setEnded(false);
    const v = videoRef.current;
    if (v) { v.currentTime = 0; setCurrentTime(0); }
    setIsPlaying(true);
  };
  useEffect(() => { const v = videoRef.current; if (v) v.playbackRate = hold ? 2 : playbackSpeed; }, [playbackSpeed, hold, mediaReady]);   // also once the media is ready: a speed chosen before it existed (e.g. a saved one) must still apply

  // ── Subtitles (1.21) ──────────────────────────────────────────────────────
  // Tracks come from the lesson data: video.subtitles = [{ lang, label, url, kind }], kind
  // 'subtitles' or 'captions' (English (CC) keeps its own key, 'en-cc'). With none, the
  // Subtitles control doesn't appear. Tracks are added once per player instance, after
  // the media is ready (mediaReady), by the effect below; never inside the load effect above.
  const subtitleOptions = useMemo(() => (Array.isArray(video && video.subtitles) ? video.subtitles : [])
    .filter((s) => s && s.url && s.lang)
    .map((s) => ({ ...s, kind: s.kind === 'captions' ? 'captions' : 'subtitles', key: s.kind === 'captions' ? s.lang + '-cc' : s.lang })), [video]);
  // Saved subtitles (1.22): start from the profile's preferredSubtitles, else Off. A lesson without
  // that language shows Off (activeSubtitleKey) without changing the saved value.
  const [subtitleKey, setSubtitleKey] = useState(() => (profile && typeof profile.preferredSubtitles === 'string' ? profile.preferredSubtitles : 'off'));
  const activeSubtitleKey = subtitleOptions.some((s) => s.key === subtitleKey) ? subtitleKey : 'off';
  // Saving (1.22) switches itself on: it only saves a field the profile already has (the sign the
  // backend supports it), so no request goes to an endpoint that doesn't exist yet. The in-memory
  // profile is updated too, so the next lesson starts with the choice.
  const savePreference = (field, value) => {
    if (!api || !profile || !Object.prototype.hasOwnProperty.call(profile, field)) return;
    if (profile[field] === value) return;   // unchanged: nothing to save (also keeps React's double-run in development quiet)
    profile[field] = value;
    try {
      const req = api.fetch('/profile/preferences', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      if (req && req.catch) req.catch(() => { /* a failed save changes nothing on screen */ });
    } catch (err) { /* a failed save changes nothing on screen */ }
  };
  const prefsReadyRef = useRef(false);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredPlaybackSpeed', playbackSpeed); }, [playbackSpeed]);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredSubtitles', subtitleKey); }, [subtitleKey]);
  useEffect(() => { prefsReadyRef.current = true; }, []);
  const [tracksReady, setTracksReady] = useState(0);
  const subsAddedFor = useRef(null);
  const [showSubtitleSheet, setShowSubtitleSheet] = useState(false);
  useEffect(() => {
    const player = shakaRef.current;
    if (!mediaReady || !player || !subtitleOptions.length || subsAddedFor.current === player) return;
    subsAddedFor.current = player;
    (async () => {
      for (const s of subtitleOptions) {
        try { await player.addTextTrackAsync(s.url, s.lang, s.kind === 'captions' ? 'caption' : 'subtitle', 'text/vtt', undefined, s.label); }
        catch (err) { /* one missing file must not block the others */ }
        if (shakaRef.current !== player) return;   // replaced by a new lesson's player
      }
      setTracksReady((n) => n + 1);
    })();
  }, [mediaReady, subtitleOptions]);
  // ── Request guard: the only way on from a stop. One load() of the current link at the remembered spot. ──────
  // Nothing happens on a link that has run out (useCDNToken brings a fresher one), or for another lesson (the
  // load effect builds a new player for it). The subtitle tracks are added again on the new load.
  const restartFromLink = () => {
    const guard = guardRef.current; const p = shakaRef.current; const url = videoUrlRef.current;
    if (!guard.stopped || !p || !url || String(url).split('?')[0] !== guard.key) return false;
    if (guard.clockOk && cdnTokenExpired(cdnTokenRef.current)) return false;
    guard.stopped = false; guard.failures = [];
    setStopped((n) => n + 1);   // the recovery effect re-reads the guard: not stopped → it stands down; a failed load arms it again
    resumedRef.current = true;   // the spot goes to load() as its start time; loadedmetadata must not seek elsewhere
    subsAddedFor.current = null; setTracksReady(0);
    const withOverlay = guard.pos >= 1;   // step 2's rule: a lesson never played reloads quietly
    guard.loading = true;
    setError(null); setIsLoading(!withOverlay); setLinkOverlay(withOverlay ? 'loading' : null);
    p.load(url, guard.pos > 0 ? guard.pos : undefined)
      .then(() => {
        if (shakaRef.current !== p || guard.stopped) return;   // stopped again meanwhile: the stop owns the state
        guard.loading = false;
        setError(null); setIsLoading(false);
        setLinkOverlay(withOverlay && !guard.playAfter ? 'ready' : null);
        if (guard.playAfter) { guard.playAfter = false; setIsPlaying(true); }
      })
      .catch((err) => {
        if (shakaRef.current !== p || guard.stopped || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        guard.loading = false;
        guard.stopped = true; guard.playAfter = false;   // still stopped; key and pos kept; no play without a new press
        setLinkOverlay('ready');   // the overlay, never the error screen (October 30, 2026)
        setIsLoading(false);
        setStopped((n) => n + 1);
      });
    return true;
  };
  restartRef.current = restartFromLink;
  // While stopped: back online or the page shown → one try; the timed ladder → one try per rung, then no more.
  useEffect(() => {
    const guard = guardRef.current;
    if (!stopped || !guard.stopped) return undefined;
    const again = () => { if (restartRef.current) restartRef.current(); };
    const onVisible = () => { if (document.visibilityState === 'visible') again(); };
    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', onVisible);
    let timer = null;
    if (guard.tries < CDN_GUARD_RETRY_SECONDS.length) {
      timer = window.setTimeout(() => { guard.tries += 1; again(); }, CDN_GUARD_RETRY_SECONDS[guard.tries] * 1000);
    }
    return () => { window.removeEventListener('online', again); document.removeEventListener('visibilitychange', onVisible); if (timer) window.clearTimeout(timer); };
  }, [stopped]);
  // A play press on a stopped player: one try, and play once it has loaded (the press is the gesture).
  useEffect(() => {
    if (!isPlaying) { guardRef.current.playAfter = false; return; }   // paused meanwhile: a later restart stays paused
    if (!guardRef.current.stopped) return;
    guardRef.current.tries = 0; guardRef.current.playAfter = true;
    if (restartRef.current) restartRef.current();
  }, [isPlaying]);
  // The overlay (step 2's rules): gone once the video plays another way; gone on a new lesson; and "Reloading…"
  // with no load in sight for CDN_GUARD_OVERLAY_WAIT_SECONDS becomes "Resume lesson" (the tap refreshes everything).
  useEffect(() => { if (isPlaying && linkOverlay && !resumingRef.current) setLinkOverlay(null); }, [isPlaying, linkOverlay]);
  useEffect(() => { setLinkOverlay(null); }, [videoUrlKey]);
  useEffect(() => {
    if (linkOverlay !== 'loading') return undefined;
    let timer = null;
    // Waits at least CDN_GUARD_OVERLAY_WAIT_SECONDS, and past the next ladder rung while one can still try (a link
    // that has run out cannot be tried, so then only the wait counts).
    const delay = () => {
      const guard = guardRef.current;
      const canTry = guard.tries < CDN_GUARD_RETRY_SECONDS.length && !(guard.clockOk && cdnTokenExpired(cdnTokenRef.current));
      return Math.max(CDN_GUARD_OVERLAY_WAIT_SECONDS, canTry ? CDN_GUARD_RETRY_SECONDS[guard.tries] + 5 : 0) * 1000;
    };
    const check = () => {
      const guard = guardRef.current;
      if (guard.loading) { timer = window.setTimeout(check, delay()); return; }   // a slow load: keep waiting
      if (guard.stopped) setLinkOverlay('ready');   // the tap takes it from here (October 30, 2026)
    };
    timer = window.setTimeout(check, delay());
    return () => window.clearTimeout(timer);
  }, [linkOverlay, stopped]);
  // Resume (October 20, 2026): the overlay may have said "Resume lesson" for hours. The tap re-establishes everything
  // at tap time instead of trusting the player prepared back then: 1. a fresh session token, 2. a fresh link into page
  // state (through the page's hook, so both players and the PDF links see it), 3. a reload from it at the spot (a new
  // DRM session whose license carries the fresh token, fresh buffers), 4. play. A play-then-pause inside the tap
  // captures the activation without playing the old stream; the overlay stays up ('resuming') until the reload plays.
  const continueFromLink = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const v = videoRef.current; const p = shakaRef.current; const guard = guardRef.current;
    if (!v || !p) return;
    resumingRef.current = true;
    const first = v.play(); if (first && first.catch) first.catch(() => {});
    v.pause();   // play-then-pause inside the tap: the activation is captured, nothing plays until the reload
    setLinkOverlay('resuming');
    const pos = v.currentTime > 0 ? v.currentTime : (guard.pos || 0);
    (async () => {
      try {
        // offline: nothing to refresh; the guard's own 'online' trigger reloads later
        if (navigator.onLine !== false) await api.refreshToken();
        if (typeof refreshLink === 'function') {
          const before = cdnTokenRef.current;
          const r = await refreshLink();
          // the fresh link reaches this player through a render and its [videoUrl] effect, which writes the token
          // the request filter sends (cdnTokenRef); wait for that write (normally one tick, at most 1 s)
          if (r === 'fresh') for (let i = 0; i < 40 && cdnTokenRef.current === before; i++) await new Promise((res) => setTimeout(res, 25));
        }
        if (shakaRef.current !== p) return;   // the lesson changed meanwhile
        if (guard.stopped || guard.loading) {   // stopped again while waiting, or a restart load already in flight
          guard.playAfter = true;                // (the fresh link's own): that load is the reload; play after it
          if (guard.stopped) {
            guard.tries = 0;
            const started = restartRef.current ? restartRef.current() : false;
            if (!started) { guard.playAfter = false; setLinkOverlay('ready'); }   // still no usable link: Resume lesson again
          }
          return;
        }
        const at = v.currentTime > 0 ? v.currentTime : pos;   // the spot now (the tap's play() may have moved on)
        guard.loading = true;
        resumedRef.current = true;   // the spot is load()'s start time; loadedmetadata must not seek elsewhere
        subsAddedFor.current = null; setTracksReady(0);   // the subtitle tracks are added again on the new load
        await p.load(videoUrlRef.current, at > 0 ? at : undefined);
        if (shakaRef.current !== p || guard.stopped) return;   // stopped again meanwhile: the stop owns the state
        guard.loading = false;
        setLinkOverlay(null); setError(null); setIsLoading(false);
        const again = v.play(); if (again && again.catch) again.catch(() => {});
        setIsPlaying(true);
      } catch (err) {
        if (shakaRef.current !== p || guard.stopped || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        // the same state as a failed restart load: stopped, with the key and the spot kept, so the next link, press,
        // online, visibility or ladder rung tries again; no play without a new press
        guard.loading = false;
        guard.stopped = true; guard.key = String(videoUrlRef.current).split('?')[0]; guard.pos = v.currentTime > 0 ? v.currentTime : pos; guard.playAfter = false;
        setLinkOverlay('ready'); setIsLoading(false); setIsPlaying(false);   // back to Resume lesson, never the error screen (October 30, 2026)
        setStopped((n) => n + 1);
      } finally {
        resumingRef.current = false;
      }
    })();
  };
  useEffect(() => {
    const player = shakaRef.current;
    if (!player || !tracksReady) return;
    const opt = subtitleOptions.find((s) => s.key === subtitleKey);
    try {
      if (!opt) { player.setTextTrackVisibility(false); return; }
      const kind = opt.kind === 'captions' ? 'caption' : 'subtitle';
      const all = player.getTextTracks();
      const tr = all.find((t) => t.language === opt.lang && t.kind === kind && (!opt.label || t.label === opt.label))
        || all.find((t) => t.language === opt.lang && t.kind === kind);
      if (tr) player.selectTextTrack(tr);
      player.setTextTrackVisibility(true);
    } catch (err) { /* subtitles are optional: never disturb playback */ }
  }, [subtitleKey, tracksReady, subtitleOptions]);

  // Read duration/position from the PLAYER, not React state. `duration` only
  // populates on loadedmetadata and `currentTime` lags a timeupdate behind, so
  // seeking off React state silently no-ops before metadata lands — which is
  // exactly when someone taps skip on a fresh lesson.
  // Read duration/position from the ELEMENT, not React state. `duration` only
  // populates on loadedmetadata and `currentTime` lags a timeupdate behind, so
  // seeking off React state silently no-ops before metadata lands — exactly when
  // someone taps skip on a fresh lesson.
  const liveDuration = () => {
    const v = videoRef.current;
    const d = v ? v.duration : 0;
    return Number.isFinite(d) && d > 0 ? d : (totalSeconds || 0);
  };
  const livePosition = () => {
    const v = videoRef.current;
    const t = v ? v.currentTime : 0;
    return Number.isFinite(t) ? t : (currentTime || 0);
  };

  const seekTo = (clientX) => {
    const v = videoRef.current;
    const d = liveDuration();
    if (!progressRef.current || !v || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    v.currentTime = t;
    setCurrentTime(t);
  };

  // Drag on the bar: move the thumb only (no seek), and keep the controls up.
  const scrubTo = (clientX) => {
    const d = liveDuration();
    if (!progressRef.current || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    scrubRef.current = t;
    setScrubTime(t);
    bump();
  };

  // Release: one seek, to where the finger let go.
  const endScrub = () => {
    const t = scrubRef.current;
    scrubRef.current = null;
    setScrubTime(null);
    if (t === null) return;
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    setCurrentTime(t);
  };

  const skip = (s) => {
    const v = videoRef.current;
    if (!v) return;
    const d = liveDuration();
    const target = livePosition() + s;
    const t = Math.max(0, d > 0 ? Math.min(d - 0.25, target) : target);
    v.currentTime = t;
    setCurrentTime(t);
    bump();
  };

  // ── Lock-screen and headphone controls (1.24) ───────────────────────────
  // The Media Session API. Registered when this player starts playing, and kept after a pause so the
  // lock-screen play button still works; cleared when this player unmounts, if it is still the one
  // registered. Handlers go through a ref, so they always call this render's functions.
  const mediaSessionRef = useRef({});
  mediaSessionRef.current = {
    play: () => setIsPlaying(true),
    pause: () => setIsPlaying(false),
    skip: (s) => skip(s),
    seekTo: (t) => { const v = videoRef.current; if (v) { v.currentTime = t; setCurrentTime(t); } },
  };
  const mediaSessionOwner = useRef({});
  useEffect(() => {
    if (!isPlaying || typeof navigator === 'undefined' || !navigator.mediaSession) return;
    const ms = navigator.mediaSession;
    try {
      if (typeof window.MediaMetadata === 'function') ms.metadata = new window.MediaMetadata({ title: (video && video.title) || '', artist: (video && video.series && video.series.name) || '', album: 'RoastBrew' });
    } catch (err) { /* metadata is optional */ }
    const set = (a, h) => { try { ms.setActionHandler(a, h); } catch (err) { /* this action isn't supported here */ } };
    set('play', () => mediaSessionRef.current.play());
    set('pause', () => mediaSessionRef.current.pause());
    set('seekbackward', () => mediaSessionRef.current.skip(-10));
    set('seekforward', () => mediaSessionRef.current.skip(10));
    set('seekto', (d) => { if (d && typeof d.seekTime === 'number') mediaSessionRef.current.seekTo(d.seekTime); });
    window.__rbMediaSessionOwner = mediaSessionOwner.current;
  }, [isPlaying, video]);
  useEffect(() => () => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession || window.__rbMediaSessionOwner !== mediaSessionOwner.current) return;
    ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'].forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch (err) { /* not supported */ } });
    window.__rbMediaSessionOwner = null;
  }, []);

  // Tablet menus open as popovers under the top band (1.28), not as bottom sheets.
  // A menu opens right ABOVE the option button that was tapped (the buttons now live in the
  // bottom row), kept inside the player's left and right edges and no taller than the space
  // above the button. position:fixed, so nothing can clip it.
  const popoverStyle = (anchorRef) => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const r = containerRef.current ? containerRef.current.getBoundingClientRect() : { top: 0, left: 0, right: vw, bottom: vh };
    const b = anchorRef && anchorRef.current ? anchorRef.current.getBoundingClientRect() : { top: r.bottom - 56, left: r.left, width: r.right - r.left };
    const W = 220;
    const left = Math.max(r.left + 8, Math.min(b.left + b.width / 2 - W / 2, r.right - W - 8));
    return {
      position: 'fixed', bottom: `${Math.round(vh - b.top + 10)}px`, left: `${Math.round(left)}px`,
      width: `${W}px`, maxHeight: `${Math.max(150, Math.round(b.top - r.top - 18))}px`, overflowY: 'auto', boxSizing: 'border-box',
      padding: '8px', borderRadius: '14px',
      background: colors.cardBg || '#141414',
      border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)'}`,
      boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
    };
  };

  // Double-tap to skip (touch). A second tap on the same side third within 300 ms skips
  // ±10 s, and further taps keep adding (the ripple shows the running total). A first tap
  // on a side waits 300 ms before showing or hiding the controls, so a second tap can cancel
  // that; a tap in the centre shows or hides them at once.
  const tapRef = useRef({ side: null, at: 0, total: 0, timer: null });
  const [ripple, setRipple] = useState(null);
  const rippleTimerRef = useRef(null);
  useEffect(() => () => { clearTimeout(tapRef.current.timer); clearTimeout(rippleTimerRef.current); }, []);
  const handleSurfaceTap = (e) => {
    const h = holdRef.current;
    clearTimeout(h.timer);
    if (h.active) { h.active = false; setHold(false); return; }   // releasing a hold is not a tap
    const el = e && e.currentTarget;
    const pt = e && e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : e;
    const tr = tapRef.current;
    if (!el || !pt || pt.clientX == null) { toggleControls(); return; }
    const r = el.getBoundingClientRect();
    const x = (pt.clientX - r.left) / r.width;
    const side = x < 1 / 3 ? 'back' : x > 2 / 3 ? 'fwd' : null;
    const now = Date.now();
    if (!side) { clearTimeout(tr.timer); tr.side = null; toggleControls(); return; }
    if (tr.side === side && now - tr.at < 300) {
      clearTimeout(tr.timer);
      tr.at = now;
      tr.total += SKIP_SECONDSTablet;
      skip(side === 'back' ? -SKIP_SECONDSTablet : SKIP_SECONDSTablet);
      try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ }   // haptic tick (Android; iPhones ignore it)
      setRipple({ side, total: tr.total });
      clearTimeout(rippleTimerRef.current);
      rippleTimerRef.current = setTimeout(() => setRipple(null), 700);
      return;
    }
    clearTimeout(tr.timer);
    tr.side = side; tr.at = now; tr.total = 0;
    tr.timer = setTimeout(() => { tr.side = null; toggleControls(); }, 300);
  };

  // ── iOS FULLSCREEN ────────────────────────────────────────────────────────
  // Two things were wrong before, both confirmed against Video.js's own
  // tech/html5.js source:
  //
  // 1. CASING. Video.js tests `typeof el.webkitEnterFullScreen === 'function'`
  //    — capital S in "FullScreen". I was calling `webkitEnterFullscreen` and
  //    gating on `webkitSupportsFullscreen`. Safari has exposed both spellings
  //    historically, but the capital-S one is what Video.js relies on, so try
  //    both rather than betting on either.
  //
  // 2. THE PRIMING SEQUENCE. iOS refuses programmatic fullscreen on a video that
  //    has not been primed. Video.js handles it: when the video is paused and
  //    networkState <= HAVE_METADATA it calls play(), then on a setTimeout(0)
  //    pauses and only THEN enters fullscreen — split across a tick, because
  //    doing both synchronously can trap older iOS in a play/pause loop. My
  //    version just called enter and gave up, which is exactly the "nothing
  //    happens" on a fresh lesson.
  //
  // So stop hand-rolling it: player.requestFullscreen() routes through the
  // tech's enterFullScreen() and inherits all of the above. The manual path
  // below is only a fallback if the Video.js player is not available.
  // ── FULLSCREEN ────────────────────────────────────────────────────────────
  // Simpler than the FairPlay page's version, because the platforms are simpler.
  // This page serves Android and narrow desktop windows, which support the
  // standard Fullscreen API on an ELEMENT — so the container goes fullscreen and
  // the custom controls stay visible and usable inside it.
  //
  // The video-element fallback (webkitEnterFullScreen, both spellings, plus the
  // paused/networkState priming dance Video.js performs) is kept for any WebKit
  // browser that somehow lands here, since that path needs it and costs nothing.
  //
  // NO screen.orientation.lock. The desktop Shaka page locks landscape on enter
  // and portrait on exit; on Android that fights whatever orientation the user is
  // actually holding, and on iOS the API does not exist at all so both calls sat
  // in empty catch blocks. Dropped on purpose.
  const nativeEnterName = (el) => {
    if (!el) return null;
    if (typeof el.webkitEnterFullScreen === 'function') return 'webkitEnterFullScreen';
    if (typeof el.webkitEnterFullscreen === 'function') return 'webkitEnterFullscreen';
    return null;
  };

  const goFullscreen = useCallback(() => {
    const el = videoRef.current;
    const box = containerRef.current;

    const inFs = !!(document.fullscreenElement || document.webkitFullscreenElement ||
                    (el && el.webkitDisplayingFullscreen));
    if (inFs) {
      if (el && typeof el.webkitExitFullScreen === 'function') { el.webkitExitFullScreen(); return; }
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }

    // Preferred: element fullscreen on the CONTAINER, so our controls come along.
    if (box && typeof box.requestFullscreen === 'function') {
      try { box.requestFullscreen().catch(() => {}); return; } catch (e) { /* fall through */ }
    }
    if (box && typeof box.webkitRequestFullscreen === 'function') {
      try { box.webkitRequestFullscreen(); return; } catch (e) { /* fall through */ }
    }

    // WebKit fallback: the platform player, with the priming sequence.
    if (!el) return;
    const method = nativeEnterName(el);
    if (!method) return;
    if (el.paused && el.networkState <= 1 /* HAVE_METADATA */) {
      const pr = el.play();
      if (pr && pr.catch) pr.catch(() => {});
      setTimeout(() => {
        el.pause();
        try { el[method](); } catch (e) { /* refused */ }
      }, 0);
    } else {
      try { el[method](); } catch (e) { /* refused */ }
    }
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', h);
    document.addEventListener('webkitfullscreenchange', h);
    const v = videoRef.current;
    const ib = () => setIsFullscreen(true); const ie = () => setIsFullscreen(false);
    if (v) { v.addEventListener('webkitbeginfullscreen', ib); v.addEventListener('webkitendfullscreen', ie); }
    return () => {
      document.removeEventListener('fullscreenchange', h);
      document.removeEventListener('webkitfullscreenchange', h);
      if (v) { v.removeEventListener('webkitbeginfullscreen', ib); v.removeEventListener('webkitendfullscreen', ie); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey]);


  // ── OPTION BUTTONS ────────────────────────────────────────────────────────
  // An icon with its name written out, 44px tall: the bottom row (Mute, Speed,
  // Subtitles, Picture in Picture, Full Screen) and AirPlay in the top corner.
  // When the player is too narrow for every label in the row, the row drops to
  // icons only (the name stays as the accessible label). `always` keeps the label.
  // Do the labels fit? Judged on the LONGEST wording each button can show ("Unmute", "1.25x"),
  // so the row never flips between words and icons because of a mute or a speed change.
  // Per button: icon 20 + gap 6 + padding 20, and about 7px a letter at 13px.
  const labelW = (t) => 46 + 7 * t.length;
  const labelsNeed = 24 + labelW('Unmute') + labelW('Speed (1.25x)')
    + (subtitleOptions.length > 0 ? 6 + labelW('Subtitles') : 0)
    + (pipAvailable ? 6 + labelW('Picture in Picture') : 0)
    + labelW(isFullscreen ? 'Exit Full Screen' : 'Full Screen');
  const showLabels = playerWidth === 0 || playerWidth >= labelsNeed;
  const barBtn = (icon, label, onClick, key, opts = {}) => {
    const withLabel = opts.always || showLabels;
    return (
      <button type="button" ref={opts.btnRef || null} aria-label={label}
        onClick={once(key, onClick)} onTouchEnd={once(key, onClick)}
        style={{
          height: '44px', minWidth: '44px', padding: withLabel ? '0 10px' : '0 6px', flexShrink: 0,
          background: opts.active ? 'rgba(255,255,255,0.16)' : 'none', border: 'none', borderRadius: '10px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          color: opts.tint || '#fff', fontSize: '13px', fontWeight: 600, fontFamily: '"DM Sans",sans-serif', whiteSpace: 'nowrap',
          WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
        }}>
        {withLabel ? <>{icon}<span>{label}</span></> : (opts.compact || icon)}
      </button>
    );
  };
  const barIcon = (children) => (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
  );

  // ── PICTURE IN PICTURE ────────────────────────────────────────────────────
  // Always from the tap itself (browsers only grant it inside a user gesture).
  // Safari on iPad uses its own webkitSetPresentationMode and wants the video
  // playing, so a paused lesson is started by the same tap. Everything else
  // uses the standard requestPictureInPicture. A refusal is silent: the button
  // simply does nothing, and nothing else changes.
  const togglePiP = () => {
    const el = videoRef.current;
    if (!el) return;
    try {
      if (typeof el.webkitSetPresentationMode === 'function' && typeof el.webkitSupportsPresentationMode === 'function'
        && el.webkitSupportsPresentationMode('picture-in-picture')) {
        if (el.webkitPresentationMode === 'picture-in-picture') { el.webkitSetPresentationMode('inline'); return; }
        if (el.paused) { const pr = el.play(); if (pr && pr.catch) pr.catch(() => {}); setIsPlaying(true); }
        el.webkitSetPresentationMode('picture-in-picture');
        return;
      }
      if (document.pictureInPictureElement) { const r = document.exitPictureInPicture(); if (r && r.catch) r.catch(() => {}); }
      else if (typeof el.requestPictureInPicture === 'function') { const r = el.requestPictureInPicture(); if (r && r.catch) r.catch(() => {}); }
    } catch (err) { /* the browser refused */ }
  };


  return (
    <div ref={containerRef} className="video-player-container"
      style={{
        position: 'relative', aspectRatio: '16/9', width: '100%',
        borderRadius: isFullscreen ? 0 : '10px', overflow: 'hidden', background: '#000',
      }}>
      {autoMuted && (soundPromptBig ? (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 22px) clamp(22px, 5vw, 34px)', borderRadius: '18px', border: 'none', background: '#111111', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 'clamp(16px, 2.4vw, 22px)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: 'clamp(34px, 7vw, 48px)', height: 'clamp(34px, 7vw, 48px)' }}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ) : (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 40, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '999px', border: 'none', background: 'rgba(0,0,0,0.72)', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ))}
      <div className="video-player-inner" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit' }}>

        {/* z0 — media */}
      {videoUrl && <video ref={videoRef} muted={isMuted} style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'contain',zIndex:0}}
          onLoadedMetadata={()=>{if(videoRef.current){setDuration(videoRef.current.duration);setIsLoading(false);setError(null);if(!resumedRef.current){const r=resumeFromRef.current;if(r>0&&r<videoRef.current.duration){videoRef.current.currentTime=r;setCurrentTime(r);}resumedRef.current=true;}}}}
          onTimeUpdate={()=>{if(videoRef.current&&!guardRef.current.stopped){const t=videoRef.current.currentTime;setCurrentTime(t);const d=videoRef.current.duration||duration||0;latestRef.current={pos:t,dur:d};heartbeatOnTime(t,d,videoRef.current.playbackRate,!videoRef.current.paused&&!videoRef.current.seeking);}}}
          onSeeking={()=>heartbeatOnTime.seeking()}
          onProgress={()=>{if(videoRef.current?.buffered.length>0)setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length-1));}}
          onWaiting={()=>setIsLoading(true)}
          onCanPlay={()=>{setIsLoading(false);setError(null);if(videoRef.current)videoRef.current.volume=isMuted?0:1;}}
          onEnded={()=>setIsPlaying(false)} onPlay={reportPlaying(setIsPlaying)} onPause={reportPlaying(setIsPlaying)}
          playsInline preload="metadata" />}
        {!videoUrl && <div style={{ position: 'absolute', inset: 0, background: '#0a0a0a' }} />}

        {/* z1 — cinematic vignette (mobile only) */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)',
        }} />

        {/* z2 — paused dim */}
        <div style={{ position: 'absolute', inset: 0, background: '#000', opacity: isPlaying ? 0 : 0.3, zIndex: 2, pointerEvents: 'none', transition: 'opacity 0.4s ease' }} />

        {/* z3 — spinner. pointerEvents NONE: an overlay above the controls without
            this is what made them unclickable in the first place. */}
        {isLoading && !showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.25)', borderTopColor: colors.accent, borderRadius: '50%', animation: 'rbspin 0.8s linear infinite' }} />
          </div>
        )}

        {/* z3 — error */}
        {showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'rgba(0,0,0,0.7)', zIndex: 3, pointerEvents: 'none', padding: '16px', textAlign: 'center' }}>
            <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
            <p style={{ margin: 0, color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>{error}</p>
          </div>
        )}
        <LinkOverlay state={linkOverlay} at={guardRef.current.pos} colors={colors} onContinue={continueFromLink} small />

        {/* z15 — TAP LAYER. Always interactive. iOS does not reliably bubble a
            click off a <video>, so the reveal tap needs a real element above it.
            Single logical handler via once(): touchend + synthesised click both
            firing is what made this toggle twice and appear dead. */}
        <div
          onTouchStart={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            clearTimeout(h.timer); h.active = false;
            if (!t || e.touches.length > 1) return;
            h.x = t.clientX; h.y = t.clientY;
            h.timer = setTimeout(() => { h.active = true; setHold(true); try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ } }, 450);
          }}
          onTouchMove={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            if (t && !h.active && Math.hypot(t.clientX - h.x, t.clientY - h.y) > 10) clearTimeout(h.timer);
          }}
          onTouchCancel={() => { const h = holdRef.current; clearTimeout(h.timer); if (h.active) { h.active = false; setHold(false); } }}
          onTouchEnd={once('tap', (e) => handleSurfaceTap(e))}
          onClick={once('tap', (e) => handleSurfaceTap(e))}
          style={{ position: 'absolute', inset: 0, zIndex: 15, pointerEvents: 'auto', background: 'transparent', touchAction: 'manipulation', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
        />
        {ripple && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, [ripple.side === 'back' ? 'left' : 'right']: 0, width: '38%', zIndex: 16, pointerEvents: 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', background: 'rgba(255,255,255,0.12)',
            borderRadius: ripple.side === 'back' ? '0 50% 50% 0 / 0 50% 50% 0' : '50% 0 0 50% / 50% 0 0 50%',
            color: '#fff', fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '14px', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            <span>{ripple.side === 'back' ? '«' : '»'}</span>
            <span>{ripple.side === 'back' ? '−' : '+'}{ripple.total} s</span>
          </div>
        )}
        {hold && (
          <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 17, pointerEvents: 'none',
            padding: '6px 12px', borderRadius: '999px', background: 'rgba(0,0,0,0.62)', color: '#fff',
            fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>
            2× ▸▸
          </div>
        )}

        {/* ── z16 CONTROLS — title on top, skip / play / skip in the centre, and one
            row of written-out options under the scrubber ─────────────────────── */}

        {/* TOP: the lesson's title. The band itself lets taps through to the tap layer. */}
        <div data-controls="true"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, padding: '10px 10px 24px 20px',
            background: 'linear-gradient(to bottom,rgba(0,0,0,0.6) 0%,rgba(0,0,0,0.3) 60%,transparent 100%)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: 'none',
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingTop: autoMuted && !soundPromptBig ? '52px' : '6px', minWidth: 0, fontFamily: '"DM Sans",sans-serif' }}>
            {/* The course and lesson names are no longer drawn on the video (October 15, 2026): the title stack above it has them. The box stays so the layout does not shift. */}
          </div>
        </div>

        {/* CENTRE: skip 10s / play / skip 10s — original SkipButton, top 45%. */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)',
            display: 'flex', alignItems: 'center', gap: '18px', zIndex: 16,
            opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          <SkipButtonTablet direction="back" onClick={once('skipback', () => skip(-SKIP_SECONDSTablet))} currentTier={currentTier} isMobile big seconds={SKIP_SECONDSTablet} />
          <button aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            onTouchEnd={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            style={{
              width: '60px', height: '60px', borderRadius: '50%', border: 'none', flexShrink: 0,
              background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', cursor: 'pointer',
              animation: !isPlaying ? 'rbpulse 2.2s ease-in-out infinite' : 'none',
              WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
            }}>
            <svg width={26} height={26} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: !isPlaying ? '3px' : '0' }}>
              {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
            </svg>
          </button>
          <SkipButtonTablet direction="forward" onClick={once('skipfwd', () => skip(SKIP_SECONDSTablet))} currentTier={currentTier} isMobile big seconds={SKIP_SECONDSTablet} />
        </div>

        {/* While a menu is open the picture dims a little, so the menu reads as the thing to answer. */}
        {(showSpeedSheet || showSubtitleSheet) && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 16, background: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }} />
        )}

        {/* BOTTOM: scrubber with the time at its end, then the option row. */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, padding: '18px 8px 6px',
            background: 'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.55) 55%,transparent 100%)',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '0 10px' }}>
          {/* A 4px bar inside a 24px hit area, so a thumb can actually land on it. */}
          <div ref={progressRef}
            onClick={(e) => { e.stopPropagation(); seekTo(e.clientX); }}
            onTouchStart={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchMove={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchEnd={(e) => { e.stopPropagation(); if (e.cancelable) e.preventDefault(); endScrub(); bump(); }}
            onTouchCancel={() => { scrubRef.current = null; setScrubTime(null); }}
            style={{ position: 'relative', height: '24px', display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1, minWidth: 0, touchAction: 'none' }}>
            <div style={{ position: 'relative', height: '4px', width: '100%', background: 'rgba(255,255,255,0.2)', borderRadius: '3px' }}>
              <div style={{ position: 'absolute', height: '100%', width: `${bufPct}%`, background: 'rgba(255,255,255,0.3)', borderRadius: '3px', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', height: '100%', width: `${pct}%`, borderRadius: '3px', pointerEvents: 'none', ...pStyles.played }} />
              <div style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%,-50%)', width: '12px', height: '12px', borderRadius: '50%', pointerEvents: 'none', ...pStyles.thumb }} />
            </div>
            {scrubTime !== null && (
              <div style={{ position: 'absolute', left: `${pct}%`, bottom: '26px', transform: 'translateX(-50%)', padding: '3px 8px', borderRadius: '6px', background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                {fmtTimeTablet(scrubTime)}
              </div>
            )}
          </div>
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.92)', fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {fmtTimeTablet(scrubTime ?? currentTime)} / {fmtTimeTablet(totalSeconds)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}>
            {barBtn(
              barIcon(isMuted
                ? <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M23 9l-6 6M17 9l6 6" /></>
                : <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" /></>),
              isMuted ? 'Unmute' : 'Mute', () => { setIsMuted(!isMuted); bump(); }, 'mute')}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: showLabels ? '6px' : '4px', flex: 1, minWidth: 0 }}>
              {barBtn(
                barIcon(<><path d="M5.6 18a9 9 0 1112.8 0" /><path d="M12 13l4-4" /></>),
                `Speed (${playbackSpeed}x)`, () => { setShowSpeedSheet(true); bump(); }, 'speed',
                { btnRef: speedBtnRef, active: showSpeedSheet, compact: <span>{playbackSpeed}x</span> })}
              {/* Subtitles: only when this lesson has subtitle tracks. */}
              {subtitleOptions.length > 0 && barBtn(
                barIcon(<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 12h4M13 12h4M7 16h10" /></>),
                'Subtitles', () => { setShowSubtitleSheet(true); bump(); }, 'subs',
                { btnRef: subsBtnRef, active: showSubtitleSheet, tint: activeSubtitleKey !== 'off' ? colors.accent : null })}
              {pipAvailable && barBtn(
                barIcon(<><rect x="2" y="3" width="20" height="14" rx="2" /><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor" /></>),
                'Picture in Picture', () => { togglePiP(); bump(); }, 'pip')}
            </div>
            {barBtn(
              barIcon(isFullscreen
                ? <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" />
                : <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />),
              isFullscreen ? 'Exit Full Screen' : 'Full Screen', () => goFullscreen(), 'fs')}
          </div>
        </div>

        <style>{`
          @keyframes rbspin { to { transform: rotate(360deg); } }
          @keyframes rbpulse { 0%,100% { transform: scale(1); box-shadow: 0 8px 32px rgba(0,0,0,0.4); } 50% { transform: scale(1.06); box-shadow: 0 10px 40px rgba(0,0,0,0.5); } }
          @keyframes rbsheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .video-js { background-color: transparent !important; }
          .video-js video, .video-js .vjs-tech { object-fit: contain; }
          .video-js .vjs-control-bar, .video-js .vjs-big-play-button, .video-js .vjs-loading-spinner,
          .video-js .vjs-error-display, .video-js .vjs-poster, .video-js .vjs-text-track-display { display: none !important; }
        `}</style>
      </div>

      {/* ── SPEED MENU ── opens above the Speed button (see popoverStyle). position:fixed with a
          full-screen backdrop: a tap anywhere outside closes it, and no ancestor can clip it. */}
      {showSpeedSheet && (
        <div
          onClick={once('sheetbg', () => setShowSpeedSheet(false))}
          onTouchEnd={once('sheetbg', () => setShowSpeedSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'transparent' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
            style={popoverStyle(speedBtnRef)}>
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Playback Speed
            </div>
            {PLAYBACK_SPEEDSTablet.map((spd) => (
              <button key={spd}
                onClick={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                onTouchEnd={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '44px', padding: '0 12px',
                  background: playbackSpeed === spd ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: playbackSpeed === spd ? colors.accent : colors.text,
                  fontSize: '15px', fontWeight: playbackSpeed === spd ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{`${spd}x`}</span>
                {playbackSpeed === spd && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* End of lesson (1.23): Up next / Unlock / Lesson complete, over the video. */}
      {ended && endCardEnabled && !nativeFs && <EndOfLessonCard colors={colors} upNext={upNext} upNextLocked={upNextLocked} info={endCard} onUpNext={onUpNext} onUnlock={onUnlock} onReplay={replay} />}
      {/* Subtitles sheet (1.21): built like the speed sheet. */}
      {showSubtitleSheet && (
        <div
          onClick={once('subbg', () => setShowSubtitleSheet(false))}
          onTouchEnd={once('subbg', () => setShowSubtitleSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'transparent' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()} role="dialog" aria-label="Subtitles"
            style={popoverStyle(subsBtnRef)}>
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Subtitles
            </div>
            {[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((s) => (
              <button key={s.key} role="menuitemradio" aria-checked={activeSubtitleKey === s.key}
                onClick={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                onTouchEnd={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '44px', padding: '0 12px',
                  background: activeSubtitleKey === s.key ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: activeSubtitleKey === s.key ? colors.accent : colors.text,
                  fontSize: '15px', fontWeight: activeSubtitleKey === s.key ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif', textAlign: 'left',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{s.label}</span>
                {activeSubtitleKey === s.key && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
            <div style={{ fontSize: '12px', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '10px 16px 0' }}>
              Only languages available for this lesson are listed.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════ Phone player · moved as is from VideoViewPageMobile.jsx (MobilePlayer) ════════════════

const PLAYBACK_SPEEDSPhone = [0.5, 0.75, 1, 1.25, 1.5, 2];

// If the media never reports ready, stop showing a spinner forever and let the
// user press play themselves. A stuck `isLoading` used to hide the controls
// behind an overlay with no way out.
const LOADING_GIVEUP_MSPhone = 15000;

// +/-10s on mobile (the desktop page uses 15).
const SKIP_SECONDSPhone = 10;

// ============================================================================
// DRM ENGINE — COPIED VERBATIM FROM VideoViewPage.jsx. DO NOT EDIT HERE.
// ============================================================================

function applyVariantOverridePhone(url) {
  if (!url) return url;
  let variant = null;
  try { variant = new URLSearchParams(window.location.search).get('forceVariant'); } catch { return url; }
  if (!variant || !/^[a-z0-9_-]+$/i.test(variant)) return url;
  const out = url.replace(/\/master\.m3u8(\?|$)/, '/' + variant + '/video.m3u8$1');
  return out;
}

function parseSkdInitDataPhone(initData) {
  const u8 = initData instanceof Uint8Array ? initData : new Uint8Array(initData);
  if (u8.byteLength >= 6) {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const byteLen = dv.getUint32(0, true);
    if (byteLen + 4 === u8.byteLength && byteLen % 2 === 0) {
      const uri = new TextDecoder('utf-16le').decode(new Uint8Array(u8.buffer, u8.byteOffset + 4, byteLen));
      if (uri.startsWith('skd:')) return { uri, shape: 'legacy_len_utf16le' };
    }
  }
  return { uri: shaka.util.StringUtils.fromBytesAutoDetect(initData), shape: 'plain' };
}

const getSkipButtonStylesPhone = (currentTier, isHovered, isMobile) => {
  const base = { position:'relative', borderRadius:isMobile?'16px':'20px', cursor:'pointer', display:'flex', alignItems:'center', gap:isMobile?'5px':'8px', padding:isMobile?'7px 12px':'12px 20px', transition:'all 0.4s cubic-bezier(0.4,0,0.2,1)', overflow:'hidden', border:'none', fontFamily:'"DM Sans",sans-serif', WebkitTapHighlightColor:'transparent' };
  if (tierRank(currentTier) === 0) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(26,26,26,0.5),rgba(42,42,42,0.45))':'linear-gradient(135deg,rgba(26,26,26,0.4),rgba(42,42,42,0.35))', border:`1px solid ${isHovered?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.2)'}`, boxShadow:isHovered?'0 4px 16px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.2)':'0 2px 8px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.15)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  if (tierRank(currentTier) === 1) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(201,169,98,0.35),rgba(201,169,98,0.25))':'linear-gradient(135deg,rgba(201,169,98,0.25),rgba(201,169,98,0.15))', border:`1px solid ${isHovered?'rgba(201,169,98,0.6)':'rgba(201,169,98,0.4)'}`, boxShadow:isHovered?'0 6px 24px rgba(0,0,0,0.4),0 0 36px rgba(201,169,98,0.4),inset 0 1px 0 rgba(201,169,98,0.5)':'0 3px 12px rgba(0,0,0,0.3),0 0 20px rgba(201,169,98,0.18),inset 0 1px 0 rgba(201,169,98,0.3)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  return { ...base, background:isHovered?'linear-gradient(135deg,rgba(232,213,163,0.4),rgba(232,213,163,0.3))':'linear-gradient(135deg,rgba(232,213,163,0.3),rgba(232,213,163,0.2))', border:`1px solid ${isHovered?'rgba(232,213,163,0.7)':'rgba(232,213,163,0.5)'}`, boxShadow:isHovered?'0 8px 32px rgba(0,0,0,0.5),0 0 44px rgba(232,213,163,0.45),inset 0 1px 0 rgba(232,213,163,0.6)':'0 4px 16px rgba(0,0,0,0.4),0 0 24px rgba(232,213,163,0.24),inset 0 1px 0 rgba(232,213,163,0.4)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
};
const getSkipIconColorPhone = (t,h) => tierRank(t)===0?'#FFF':tierRank(t)===1?(h?'#E8D5A3':'#C9A962'):(h?'#FFF8E7':'#E8D5A3');
const getSkipNumberColorPhone = (t) => tierRank(t)===0?'#FFF':tierRank(t)===1?'#C9A962':'#E8D5A3';
const getProgressStylesPhone = (t) => {
  if (tierRank(t)===0) return { played:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.4)'}, thumb:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.5)'} };
  if (tierRank(t)===1) return { played:{background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 12px rgba(201,169,98,0.5)'}, thumb:{background:'linear-gradient(135deg,#E8D5A3,#C9A962)',boxShadow:'0 0 12px rgba(201,169,98,0.6)'} };
  return { played:{background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 16px rgba(232,213,163,0.6)'}, thumb:{background:'linear-gradient(135deg,#FFF8E7,#E8D5A3)',boxShadow:'0 0 16px rgba(232,213,163,0.7)'} };
};
function SkipButtonPhone({ direction, onClick, currentTier, isMobile, seconds=15 }) {
  const [isHovered, setIsHovered] = useState(false);
  const styles = getSkipButtonStylesPhone(currentTier, isHovered, isMobile);
  const iconColor = getSkipIconColorPhone(currentTier, isHovered);
  const numberColor = getSkipNumberColorPhone(currentTier);
  const iconSize = isMobile?13:18, numberSize = isMobile?12:16, unitSize = isMobile?9:12;
  const iconFilter = tierRank(currentTier)===0?'drop-shadow(0 1px 3px rgba(0,0,0,0.5))':tierRank(currentTier)===1?(isHovered?'drop-shadow(0 0 8px rgba(201,169,98,0.5))':'drop-shadow(0 0 4px rgba(201,169,98,0.3))'):(isHovered?'drop-shadow(0 0 12px rgba(232,213,163,0.6))':'drop-shadow(0 0 6px rgba(232,213,163,0.4))');
  return (
    <button onClick={onClick} onMouseEnter={()=>setIsHovered(true)} onMouseLeave={()=>setIsHovered(false)} style={styles}>
      {direction==='back' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M19 12H5M12 5l-7 7 7 7"/></svg>}
      <span style={{display:'flex',alignItems:'baseline',gap:'2px',color:'#F5F5F0',textShadow:isLightTheme(currentTier)?'0 1px 3px rgba(0,0,0,0.5)':'0 1px 2px rgba(0,0,0,0.3)'}}>
        <span style={{fontSize:`${numberSize}px`,fontWeight:'700',color:numberColor,letterSpacing:'-0.02em'}}>{seconds}</span>
        <span style={{fontSize:`${unitSize}px`,fontWeight:'500',opacity:0.8}}>s</span>
      </span>
      {direction==='forward' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
    </button>
  );
}

const fmtTimePhone = (s) => {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

// ============================================================================
// MOBILE PLAYER
// ============================================================================
// Layer order inside the player box (all children of .video-player-inner):
//   z0  the <video> Video.js mounts
//   z1  paused dim            pointerEvents:none
//   z2  spinner / error       pointerEvents:none  ← MUST stay none, see header
//   z16 controls              pointerEvents follows showControls
// Nothing that covers the player is clickable except the controls themselves.

function MobilePlayerPhone({
  video, colors, currentTier, isPlaying, setIsPlaying, currentTime, setCurrentTime,
  videoUrl: rawVideoUrl, videoId, assetId, resumeFrom = 0,
  upNext = null, upNextLocked = false, onUpNext, onUnlock, endCard = null, refreshLink,
}) {
  // Muxed package swap: the FairPlay path plays the single-track SAMPLE-AES TS
  // package, not the demuxed cbcs fMP4 one. The desktop page also layers
  // ?forceVariant on top of this; that diagnostic hook is deliberately not
  // carried over here.
  // ?forceVariant test hook, same as the desktop Shaka page. There is no muxed
  // package swap here — that is a FairPlay-only concern (single-track SAMPLE-AES
  // TS); Widevine plays the demuxed cbcs fMP4 package directly.
  const videoUrl = useMemo(() => applyVariantOverridePhone(rawVideoUrl), [rawVideoUrl]);

  const containerRef = useRef(null);   // fullscreen target
  const videoRef = useRef(null);       // REACT-owned <video> (Shaka attaches to it)
  const shakaRef = useRef(null);       // shaka.Player instance
  const cdnTokenRef = useRef(null);    // live CDN token, re-appended to every shaka request (refreshable)
  const guardRef = useRef(freshGuard());   // request guard (see the top of the file)
  const restartRef = useRef(null);         // restartFromLink, for the effects declared above it
  const [stopped, setStopped] = useState(0);   // bumps on every stop and restart, so the recovery effect re-reads the guard
  const [linkOverlay, setLinkOverlay] = useState(null);   // null | 'loading' | 'resuming' | 'ready' (step 2's overlay; 'resuming' = the Resume tap's own reload)
  const resumingRef = useRef(false);   // the Resume tap's reload is in progress: its own play() must not close the overlay
  const progressRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const { token, profile, api } = useAuth();
  const authTokenRef = useRef(token);
  authTokenRef.current = token;

  const API_CONTENT_BASE = process.env.REACT_APP_CONTENT_API_URL || process.env.REACT_APP_API_URL || '';
  const DRMTODAY_MERCHANT = process.env.REACT_APP_DRMTODAY_MERCHANT || 'f100090a-e116-42ba-b03a-1d157c6be3f3';

  const latestRef = useRef({ pos: 0, dur: 0 });
  const resumedRef = useRef(false);
  const resumeFromRef = useRef(resumeFrom);
  const videoUrlRef = useRef(videoUrl);
  videoUrlRef.current = videoUrl;

  // Heartbeats (the 10-second send while playing, and the final send on unmount) live in useHeartbeat.
  const heartbeatOnTime = useHeartbeat(videoId);

  const [showControls, setShowControls] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Saved speed (1.22): start from the profile's preferredPlaybackSpeed when it is one of our speeds, else 1×.
  const [playbackSpeed, setPlaybackSpeed] = useState(() => { const s = Number(profile && profile.preferredPlaybackSpeed); return PLAYBACK_SPEEDSPhone.includes(s) ? s : 1; });
  const [showSpeedSheet, setShowSpeedSheet] = useState(false);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(!!videoUrl);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!error) { setShowError(false); return; }
    const t = setTimeout(() => setShowError(true), 600);
    return () => clearTimeout(t);
  }, [error]);

  // Give up on the spinner rather than covering the player forever.
  useEffect(() => {
    if (!isLoading) return undefined;
    const t = setTimeout(() => setIsLoading(false), LOADING_GIVEUP_MSPhone);
    return () => clearTimeout(t);
  }, [isLoading]);

  useEffect(() => { resumedRef.current = false; resumeFromRef.current = resumeFrom; }, [videoUrl, resumeFrom]);

  const isHls = videoUrl && /\.m3u8($|\?)/.test(videoUrl);
  const videoUrlKey = videoUrl ? String(videoUrl).split('?')[0] : null;

  // Keep the CDN token current WITHOUT restarting playback.
  useEffect(() => {
    try { cdnTokenRef.current = new URL(videoUrl).searchParams.get('t'); }
    catch { cdnTokenRef.current = null; }
    // Request guard: the clock check (useCDNToken's rule) on the link that just arrived, then a fresh start if
    // the player is stopped. A fresh link resets the timed ladder.
    const guard = guardRef.current;
    const left = cdnTokenSecondsLeft(videoUrl);
    guard.clockOk = left === null || left >= CDN_TOKEN_VIDEO_MAX_SECONDS;
    if (guard.stopped) { guard.tries = 0; if (restartRef.current) restartRef.current(); }
  }, [videoUrl]);

  const totalSeconds = duration || 0;
  // Scrubbing (touch): while a finger drags the bar, the thumb and time follow it without
  // seeking; the video seeks once, on release (scrubTo / endScrub, beside seekTo).
  const [scrubTime, setScrubTime] = useState(null);
  const scrubRef = useRef(null);
  const pct = totalSeconds > 0 ? ((scrubTime ?? currentTime) / totalSeconds) * 100 : 0;
  const bufPct = totalSeconds > 0 ? (buffered / totalSeconds) * 100 : 0;
  const pStyles = getProgressStylesPhone(currentTier);

  useEffect(() => {
    if (!videoUrl || !videoRef.current) return;
    const vid = videoRef.current;

    // tear down any prior instance
    if (shakaRef.current) { shakaRef.current.destroy(); shakaRef.current = null; }
    if (guardRef.current.stopped) setStopped((n) => n + 1);   // request guard: the old lesson's recovery stands down
    guardRef.current = { ...freshGuard(), clockOk: guardRef.current.clockOk };   // request guard: a new player, a clean slate

    // Non-DRM / non-HLS source (e.g. a plain mp4 preview): just set src.
    if (!isHls) { vid.src = videoUrl; return; }

    shaka.polyfill.installAll();

    // ── MSE, NOT SAFARI'S NATIVE HLS ─────────────────────────────────────────
    // Shaka's README: "By default, FairPlay is handled using Apple's native HLS player,
    // when on Safari. We do support FairPlay through MSE/EME, however. See the
    // streaming.useNativeHlsForFairPlay configuration value."
    //
    // Shaka's FairPlay support matrix:
    //                    Modern EME   legacy Apple Media Keys
    //     src= (CMAF)    Y            Y
    //     MSE  (CMAF)    Y            -            ← MSE requires MODERN EME
    // Our content is CMAF (fMP4 .m4s), so MSE + com.apple.fps is a supported combo.
    //
    // WHY WE LEFT THE NATIVE HLS PATH — everything below was measured on it:
    //   1. Modern EME + native HLS: license 200, CKC accepted, ZERO frames, no error.
    //      Safari's NATIVE setMediaKeys() does not enable FairPlay decryption for
    //      native HLS; only webkitSetMediaKeys() does (shaka-player#9794).
    //   2. legacy Apple Media Keys + the legacy container: video DECRYPTS and renders.
    //      But the packager gives audio and video the SAME skd content-id
    //      (--hls_key_uri is a single global flag — shaka-packager#439/#470), so both
    //      raise webkitneedkey with identical init data, Shaka dedupes the second, and
    //      audio is never keyed. Safari's clock is AUDIO-driven → currentTime pinned at
    //      0.00 with 12s of video decoded → frozen first frame.
    //   3. Forcing a session per event → 6006: the CDM rejects a second session for a
    //      content-id it already holds.
    //   4. Giving audio a distinct content-id (Worker appends &trk=audio; DRMtoday
    //      ignores unknown params — measured 200) → TWO licenses, both 200, both
    //      sessions updated → STILL FROZEN.
    //
    // (4) is shaka-player#9268: "FairPlay multi-key content intermittently fails to
    // play (3016)... Shaka requests a license for only one of the two tracks... the
    // same assets play correctly in Video.js and other HTML5 players." Open, unfixed.
    // The native-HLS FairPlay path is broken for multi-track content.
    //
    // MSE sidesteps ALL of it. Shaka parses the manifest, fetches the segments, and
    // drives EME itself — exactly the path Widevine already uses successfully here.
    // No webkitneedkey, no session-per-track, no dedupe, no Safari native-HLS quirks.
    // Modern EME is required for MSE, so no PatchedMediaKeysApple.
    const FPS_KEYSYSTEM = 'com.apple.fps';

    if (!shaka.Player.isBrowserSupported()) { setError('This browser can’t play protected video'); return; }

    // The signed CDN needs ?t=<token> on EVERY request (manifest + segments). Shaka resolves
    // child playlists/segments relative to the manifest, dropping the query — so a request
    // filter re-appends the token (read live from a ref so a refresh can swap it).
    // cdnTokenRef is kept live by its own effect above — reading it here would race
    // with a re-mint and pin a stale token for the life of the player.

    const player = new shaka.Player();
    shakaRef.current = player;

    const licenseBase = (API_CONTENT_BASE || '').replace(/\/$/, '');
    const widevineLicense = `${licenseBase}/license/widevine?assetId=${encodeURIComponent(assetId || '')}`;
    // FairPlay (Safari/iOS) goes to our endpoint too; the route forwards to DRMtoday's
    // FairPlay license server and mints the same proven auth token server-side.
    const fairplayLicense = `${licenseBase}/license/fairplay?assetId=${encodeURIComponent(assetId || '')}`;
    // DRMtoday's FairPlay CERTIFICATE endpoint (public; keyed by merchant). Shaka fetches
    // this before it can build the SPC. Confirmed from DRMtoday integration docs.
    // ── FairPlay certificate ─────────────────────────────────────────────────
    // MEASURED 2026-07-16: the cert served at /cert/<merchant> is RSA-1024
    // (modulus 0x02 0x81 0x81 → 128 bytes), serial 2777B10FB9358DC3, notBefore
    // 2026-07-13. RSA-1024 is the OLD FPS generation; SDK 4.0+ certificates are
    // RSA-2048. The comment above says we need SDK 26 credentials — so the default
    // being served may be the wrong bundle entirely.
    //
    // WHY THAT PRODUCES EXACTLY OUR SYMPTOM:
    //   Apple ships certificate + private key + ASk as ONE bundle. Our HTTP 200
    //   proves the cert/private-key pair matches — the KSM could not decrypt the SPC
    //   otherwise. It proves NOTHING about the ASk: the ASk never touches SPC
    //   decryption, nothing validates it, and a mismatched one yields a well-formed,
    //   correctly-signed CKC carrying a GARBAGE content key. The CDM accepts it
    //   (session.update resolves), then decodes noise → MEDIA_ERR_DECODE, zero frames,
    //   no error. Which is what we have.
    //
    // TESTING ANOTHER BUNDLE WITHOUT TOUCHING THE DASHBOARD:
    //   The SPC carries a hash identifying which certificate the client used, so a KSM
    //   holding several bundles picks the matching private key + ASk from it. Swapping
    //   the cert here therefore swaps which bundle DRMtoday answers with — no default
    //   change, no risk to other sessions.
    //
    //   Drop the .cer files (DER, as Apple issued them) into public/ — same-origin, so
    //   no CORS — then:
    //       ?certUrl=/fps-2048.cer      ?certUrl=/fps-1024.cer      (no param = default)
    //   Whichever one PLAYS is the bundle whose ASk DRMtoday actually holds.
    //
    //   FairPlay certificates have NOTHING to do with Widevine — different DRM,
    //   different license server, no Apple cert in that path. This cannot affect Chrome.
    // HOW TO USE — from the Safari console, no URL, no file, no deploy of the cert:
    //
    //   1. terminal:   base64 -i cert_2.der | tr -d '\n' | pbcopy
    //   2. console:    sessionStorage.setItem('fpsCertB64', '<paste>'); location.reload()
    //   3. to revert:  sessionStorage.removeItem('fpsCert64'); location.reload()
    //
    // sessionStorage survives the reload and dies with the tab. The certificate is
    // PUBLIC — every Safari downloads it before playback, and an unauthenticated curl
    // fetches it — so this leaks nothing. The private key and ASk are never involved.
    //
    // ?certUrl=<url> also works if you'd rather point at a hosted .cer.
    let certOverride = null;
    try {
      const b64 = window.sessionStorage.getItem('fpsCertB64');
      if (b64) { certOverride = 'data:application/octet-stream;base64,' + b64.trim(); }
      else {
        const u = new URLSearchParams(window.location.search).get('certUrl');
        if (u) { certOverride = u; }
      }
    } catch { /* fall through to the default */ }
    const DRMTODAY_FPS_CERT = certOverride || `https://lic.drmtoday.com/license-server-fairplay/cert/${DRMTODAY_MERCHANT}`;

    player.configure({
      drm: {
        // TRIED AND REVERTED 2026-07-17: ignoreDuplicateInitData:false.
        // Reasoning was that initDataTransform fires twice (video + audio) but only one
        // license request goes out, so the second track never gets keyed. Forcing a
        // session per webkitneedkey instead produced
        //   6006 FAILED_TO_GENERATE_LICENSE_REQUEST — "EME PatchedMediaKeysApple key error"
        // i.e. the CDM rejects a SECOND session for the same content-id outright. So the
        // duplicate suppression is correct here and the stall has another cause.
        servers: {
          'com.widevine.alpha': widevineLicense,
          // FPS_KEYSYSTEM is 'com.apple.fps.1_0' — legacy Apple Media Keys. See the
          // block above for why. (Was 'com.apple.fps' / Modern EME, which never
          // enabled decryption for native HLS.)
          [FPS_KEYSYSTEM]: fairplayLicense,
        },
        advanced: {
          'com.widevine.alpha': { persistentStateRequired: false },
          [FPS_KEYSYSTEM]: {
            persistentStateRequired: false,
            distinctiveIdentifierRequired: false,
            serverCertificateUri: DRMTODAY_FPS_CERT,
          },
        },
      },
      streaming: {
        // THE CHANGE. Default is true → Safari's native HLS player → every FairPlay
        // bug we spent this investigation on. false → Shaka drives MSE/EME itself.
        //
        // Side effect worth knowing: in MSE mode SHAKA fetches the segments, so the
        // request filter below re-appends ?t= to each one. The Worker's manifest
        // rewrite becomes belt-and-braces rather than load-bearing (it only mattered
        // because NATIVE players drop the query when resolving relative URLs).
        useNativeHlsForFairPlay: false,
        rebufferingGoal: 4,
        bufferingGoal: 30,
      },
    });

    // ── initDataTransform — MODERN EME ───────────────────────────────────────
    // Modern EME takes the content-id as plain bytes. It must NOT be wrapped in
    // Apple's legacy container ([len][initData][len][contentId UTF-16][len][cert]) —
    // that is what legacy Apple Media Keys requires, and feeding it to Modern EME
    // produced SPC_CONTAINER_HEADER_MALFORMED. The container code is gone with the
    // legacy path; if you ever go back to com.apple.fps.1_0, it must come back too.
    //
    // CONTENT-ID: measured against DRMtoday, only one form returns 200:
    //     skd://drmtoday?assetId=X&variantId=hd  → 400 (x-dt-resp-code 10003, 10001)
    //     skd://drmtoday?assetId=X               → 200   ← this one
    //     ?assetId=X&variantId=hd                → 400
    //     ?assetId=X                             → 400
    // The keys were ingested under assetId alone, so we strip the variantId the
    // packager wrote. DRMtoday ignores unknown params (measured), so the Worker's
    // &trk=audio on the audio playlist survives this strip harmlessly — [^&]* stops
    // at the next '&'.
    player.configure('drm.initDataTransform', (initData, initDataType) => {
      if (initDataType !== 'skd') return initData;
      const { uri } = parseSkdInitDataPhone(initData);
      const contentId = uri.replace(/[?&]variantId=[^&]*/i, '');
      return shaka.util.StringUtils.toUTF8(contentId);
    });

    // Attach auth + DRM-system-specific request/response handling.
    const net = player.getNetworkingEngine();
    net.registerRequestFilter((type, request) => {
      const RT = shaka.net.NetworkingEngine.RequestType;
      if (type === RT.LICENSE) {
        // license call goes to OUR content-api → attach the user's bearer token so the
        // endpoint can check entitlement before minting the DRMtoday token.
        // Read from the ref, NOT the closure: a refreshed JWT must reach the next
        // license request without the effect (and the player) being rebuilt.
        const authToken = authTokenRef.current;
        if (authToken) request.headers['Authorization'] = `Bearer ${authToken}`;
        // FairPlay: the CDM message must be sent as spc=<base64> (form-urlencoded).
        const isFairPlay = request.uris.some((u) => u.indexOf('/license/fairplay') > -1);
        if (isFairPlay && request.body) {
          const payload = new Uint8Array(request.body);
          const b64 = shaka.util.Uint8ArrayUtils.toStandardBase64(payload);
          request.headers['Content-Type'] = 'application/x-www-form-urlencoded';
          request.body = shaka.util.StringUtils.toUTF8('spc=' + encodeURIComponent(b64));
        }
      } else {
        // manifest + segments → CDN, re-append ?t=<token>
        const t = cdnTokenRef.current;
        // Request guard: a link that has run out is never sent (the CDN would only refuse it), nor anything once
        // the player is stopped. Shaka turns the throw into a failed request (not retried); during a load() that
        // rejects the load, during playback it reaches the error listener below.
        if (guardRef.current.stopped || (guardRef.current.clockOk && cdnTokenExpired(t))) throw new Error('cdn link expired');
        if (t) {
          request.uris = request.uris.map((u) => {
            try { const url = new URL(u); url.searchParams.set('t', t); return url.toString(); }
            catch { return u; }
          });
        }
      }
    });

    // FairPlay response: DRMtoday returns the CKC (sometimes wrapped). Unwrap it into the
    // raw base64 the CDM expects. Widevine responses are handled by our endpoint (raw bytes).
    net.registerResponseFilter((type, response) => {
      const RT = shaka.net.NetworkingEngine.RequestType;
      if (type !== RT.LICENSE) return;
      const isFairPlay = response.uri && response.uri.indexOf('/license/fairplay') > -1;
      if (!isFairPlay) return;
      let txt = shaka.util.StringUtils.fromUTF8(response.data).trim();
      // strip a <ckc>…</ckc> wrapper if present
      const wrapped = txt.startsWith('<ckc>') && txt.endsWith('</ckc>');
      if (wrapped) txt = txt.slice(5, -6);
      // A throw here propagates as a load failure, which is the correct outcome:
      // a CKC that isn't base64 is not something the CDM can be handed.
      response.data = shaka.util.Uint8ArrayUtils.fromBase64(txt).buffer;
    });

    // Request guard: every failed CDN request counts, the ones shaka means to retry included (it reports those
    // as recoverable). Past the budget, or on a link that has run out, the player is unloaded and holds its spot;
    // restartFromLink (below) is the only way on from there.
    const noteFailedRequest = () => {
      const guard = guardRef.current;
      const now = Date.now();
      guard.failures = guard.failures.filter((at) => now - at < CDN_GUARD_WINDOW_SECONDS * 1000);
      guard.failures.push(now);
      const expired = guard.clockOk && cdnTokenExpired(cdnTokenRef.current);
      if (!expired && guard.failures.length < CDN_GUARD_MAX_FAILURES) return false;
      if (guard.stopped) return true;
      const cur = (videoRef.current && videoRef.current.currentTime) || 0;
      guard.pos = cur > 0 ? cur : (latestRef.current.pos > 0 ? latestRef.current.pos : guard.pos);   // the spot survives a stop during a restart
      guard.key = videoUrlKey;
      guard.stopped = true;
      guard.failures = [];
      guard.loading = false;   // unload() interrupts any load in flight (it rejects with 7000, which is ignored)
      player.unload().catch(() => {});
      setIsPlaying(false);   // unload() pauses the element without a pause event: the page must know
      // The overlay is the catch-all (October 30, 2026): with a spot to come back to, a stop never shows the error
      // screen. A dead link has nothing the player can try, so it is "Resume lesson" at once; a live link reloads
      // ("Reloading…") and the watchdog falls back to "Resume lesson". The tap refreshes the session, the link and
      // the video itself; only a tap that fails shows the error screen.
      setError(null); setLinkOverlay(expired ? 'ready' : 'loading');   // never the error screen: the overlay, and the tap recovers
      setIsLoading(false);
      setStopped((n) => n + 1);
      return true;
    };
    player.addEventListener('error', (e) => {
      const detail = e && e.detail ? e.detail : e;

      // LOAD_INTERRUPTED means WE cancelled the load (teardown/replacement). It is
      // bookkeeping, not a fault, and must never reach the user.
      if (detail && detail.code === 7000) return;
      if (detail && detail.category === shaka.util.Error.Category.NETWORK && noteFailedRequest()) return;

      // Shaka fires this for BOTH recoverable (severity 1) and critical (severity 2)
      // errors. Recoverable ones self-heal (buffering, a retried segment) and must NOT
      // put up the failure overlay — doing so left a stuck "Failed to load" over a video
      // that was actually playing. Only surface CRITICAL errors.
      const severity = detail && detail.severity;
      if (severity && severity !== shaka.util.Error.Severity.CRITICAL) return;
      const code = detail && detail.code;
      if (code === 403 || code === 6007 || code === 6001) setError('You don’t have access to this video');
      else {
        // The overlay, not the error screen, wherever there is a spot to come back to (October 30, 2026): the tap
        // reloads the video from a fresh session token and a fresh link.
        const v = videoRef.current; const at = (v && v.currentTime > 0) ? v.currentTime : (latestRef.current.pos || 0);
        guardRef.current.pos = at; setIsPlaying(false); setLinkOverlay('ready');
      }
      setIsLoading(false);
    });

    // Guards every async continuation below. Without this, a player torn down
    // mid-load still runs its catch and calls setError() — clobbering the player
    // that REPLACED it. The fetch effect has always had this; this one did not.
    let cancelled = false;

    (async () => {
      try {
        await player.attach(vid);
        if (cancelled) return;
        await player.load(videoUrlRef.current, resumeFromRef.current > 0 ? resumeFromRef.current : undefined);
        if (cancelled) return;

        setError(null);        // success → clear any stale/recoverable error state
        setIsLoading(false);
      } catch (err) {
        // A superseded player is EXPECTED to reject. It is not a user-visible failure
        // and must not touch shared state — the player that replaced it owns that now.
        if (cancelled || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        const code = err && err.code;
        const spot = resumeFromRef.current > 0 ? resumeFromRef.current : 0;
        if (code === 403 || code === 6007 || code === 6001) setError('You don’t have access to this video');
        else { guardRef.current.pos = spot; setLinkOverlay('ready'); }   // the overlay, never the error screen (October 30, 2026)
        setIsLoading(false);
        // Request guard: a load that failed on the network (a refused manifest, a dead link) is a stopped player
        // too: it holds the resume spot and tries again on a link, a gesture or the ladder, never on its own.
        if (err && err.category === shaka.util.Error.Category.NETWORK) {
          const guard = guardRef.current;
          guard.stopped = true; guard.key = videoUrlKey; guard.pos = spot;
          const dead = guard.clockOk && cdnTokenExpired(cdnTokenRef.current);
          setError(null); setLinkOverlay(dead ? 'ready' : 'loading');
          setStopped((n) => n + 1);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (shakaRef.current) { shakaRef.current.destroy(); shakaRef.current = null; }
    };
  }, [videoUrlKey, isHls, assetId, API_CONTENT_BASE]);

  // -- Play/pause: driven on the RAW <video>, not a player object --
  // This is the Shaka page's own effect, copied verbatim. Note the deliberate
  // difference from the FairPlay mobile page: there, play() is called on the
  // Video.js player and an AbortError is left alone because a late FairPlay key
  // can reject it. Widevine over MSE does not exhibit that, so the simpler
  // retry-muted-on-any-rejection behaviour is kept as-is rather than "improved".
  // -- Play/pause with muted fallback --
  // A rejected play() falls back to a muted retry (autoplay policy); if that also
  // fails the intent is cleared so the UI returns to a play button.
  // ── Starting playback (fix: "stuck after the next lesson", "the next lesson is muted") ─────────────────
  // A refused autoplay and an interrupted play are different things:
  //   NotAllowedError · the browser refuses sound without a tap (always so on iOS after an automatic lesson change).
  //     Start muted, which every browser allows, but only temporarily: "Tap for sound" shows, and the first tap,
  //     click or key press anywhere restores the sound, inside that tap (what iOS requires).
  //   anything else (AbortError: interrupted, e.g. by the stream still loading or a late FairPlay key) · never mute;
  //     show paused at once, with the play button; the student taps again (October 11: the retries are removed).
  const [autoMuted, setAutoMuted] = useState(false);
  useEffect(() => {
    if(videoUrl&&videoRef.current){
      if(isPlaying){
        let cancelled = false;
        const attempt = () => {
          const v = videoRef.current; if (cancelled || !v) return;
          const pr = v.play(); if (!pr || !pr.catch) return;
          pr.catch((err) => {
            if (cancelled || !videoRef.current) return;
            if (err && err.name === 'NotAllowedError') {
              if (videoRef.current.muted) { setIsPlaying(false); return; }   // refused even muted: show paused
              videoRef.current.muted = true; setIsMuted(true); setAutoMuted(true);
              const p2 = videoRef.current.play(); if (p2 && p2.catch) p2.catch(() => { if (!cancelled) setIsPlaying(false); });
            } else setIsPlaying(false);   // AbortError (interrupted): no retry; show paused, never mute
          });
        };
        attempt();
        return () => { cancelled = true; };
      }
      else videoRef.current.pause();
    }
    return undefined;
  }, [isPlaying, videoUrl, setIsPlaying]);
  useEffect(() => {
    if (!autoMuted) return undefined;
    // a tap on the player itself only restores the sound (it must not also pause or play); elsewhere it carries on.
    // M also means "restore the sound" here: the player's own M toggle would otherwise flip it straight back to muted.
    const restore = (e) => { const v = videoRef.current; if (v) v.muted = false; setIsMuted(false); setAutoMuted(false); if (e && e.type !== 'keydown' && containerRef.current && e.target && containerRef.current.contains(e.target)) { e.stopPropagation(); e.preventDefault(); } if (e && e.type === 'keydown' && (e.key === 'm' || e.key === 'M')) { e.stopImmediatePropagation(); e.preventDefault(); } };
    const o = { capture: true };
    window.addEventListener('touchend', restore, o); window.addEventListener('click', restore, o); window.addEventListener('keydown', restore, o);
    return () => { window.removeEventListener('touchend', restore, o); window.removeEventListener('click', restore, o); window.removeEventListener('keydown', restore, o); };
  }, [autoMuted]);
  useEffect(() => { if (!isMuted) setAutoMuted(false); }, [isMuted]);
  // "Tap for sound": big and centred for SOUND_PROMPT_BIG_MS, then the small corner pill until the sound is on.
  // Display only: a tap anywhere restores the sound either way (the effect above).
  const [soundPromptBig, setSoundPromptBig] = useState(false);
  useEffect(() => {
    if (!autoMuted) { setSoundPromptBig(false); return undefined; }
    setSoundPromptBig(true);
    const t = setTimeout(() => setSoundPromptBig(false), SOUND_PROMPT_BIG_MS);
    return () => clearTimeout(t);
  }, [autoMuted]);

  // TOUCH/CLICK DEDUPE — PER CONTROL.
  // iOS fires `touchend` and then synthesises a `click` for the same tap, so any
  // element with both handlers ran its action twice.
  //
  // The first version of this used ONE shared timestamp for every control, which
  // created a worse bug than the one it fixed: while the video plays the controls
  // auto-hide, so reaching any button takes TWO taps in quick succession — one on
  // the tap layer to reveal, one on the button. With a shared guard the second tap
  // fell inside the window and was discarded. Fullscreen and PiP looked completely
  // dead; play/pause only worked if you happened to pause between taps.
  //
  // The guard is now keyed per control, so a tap on the reveal layer can never
  // suppress the next tap on a different button.
  const tapGuards = useRef({});
  const once = useCallback((key, fn) => (e) => {
    // A handled touch cancels the browser's follow-up click (preventDefault), so one tap is
    // one action, and that click can never land on whatever just opened under the finger
    // (a "ghost click": it used to close the speed sheet the moment a tap opened it).
    // Quick repeated taps now all count. Safety net, still keyed per control: a click on
    // the same control within 400 ms of a handled touch is ignored, in case a browser sends one anyway.
    const now = Date.now();
    if (e && e.type === 'touchend') {
      if (e.cancelable && e.preventDefault) e.preventDefault();
      tapGuards.current[key] = now;
    } else if (now - (tapGuards.current[key] || 0) < 400) {
      return;
    }
    if (e && e.stopPropagation) e.stopPropagation();
    fn(e);
  }, []);

  // ── controls auto-hide ───────────────────────────────────────────────────
  const bump = useCallback(() => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (isPlaying) hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) { setShowControls(true); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
    return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); };
  }, [isPlaying]);

  useEffect(() => { if (isPlaying) setHasStarted(true); }, [isPlaying]);

  // WHY A DEDICATED TAP LAYER, AND NOT onClick ON THE CONTAINER:
  // On iOS a tap on a <video> element does not reliably emit a `click` that
  // bubbles to an ancestor — the media element swallows it. Relying on bubbling
  // meant that once the controls hid themselves (pointerEvents:'none'), the tap
  // landed on the video, nothing fired, and the controls could never be brought
  // back. This transparent sibling sits ABOVE the video and BELOW the controls,
  // and its pointerEvents are ALWAYS 'auto'.
  const toggleControls = useCallback(() => {
    if (showControls) { setShowControls(false); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else bump();
  }, [showControls, bump]);

  useEffect(() => { const v = videoRef.current; if (v) { v.muted = isMuted; v.volume = isMuted ? 0 : 1; } }, [isMuted]);
  // Hold for 2× (touch): while the finger is held on the video, play at 2×; on release, back to the chosen speed.
  const [hold, setHold] = useState(false);
  const holdRef = useRef({ timer: null, active: false, x: 0, y: 0 });
  useEffect(() => () => clearTimeout(holdRef.current.timer), []);
  // Media ready: counts each time the media reports its metadata (first load, a new lesson). The
  // speed and the subtitle tracks apply from this, because the <video> may not exist yet when the
  // player first appears, and isLoading can stay false if the player mounts before its URL.
  const [mediaReady, setMediaReady] = useState(0);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onReady = () => setMediaReady((n) => n + 1);
    v.addEventListener('loadedmetadata', onReady);
    return () => v.removeEventListener('loadedmetadata', onReady);
  }, [videoUrl]);

  // ── End of lesson (1.23) ──────────────────────────────────────────────────
  // The page passes upNext / upNextLocked / onUpNext / onUnlock to the LESSON player only, so the
  // answer-key player never shows the card. The video's end is detected by these new listeners;
  // the existing end handler is untouched.
  const endCardEnabled = typeof onUpNext === 'function' || typeof onUnlock === 'function';
  const [ended, setEnded] = useState(false);
  const [nativeFs, setNativeFs] = useState(false);     // iPhone's own full-screen player hides our card
  useEffect(() => {
    setEnded(false);
    const v = videoRef.current;
    if (!v) return undefined;
    const onEnd = () => setEnded(true);
    const onAgain = () => setEnded(false);
    v.addEventListener('ended', onEnd); v.addEventListener('play', onAgain); v.addEventListener('seeking', onAgain);
    return () => { v.removeEventListener('ended', onEnd); v.removeEventListener('play', onAgain); v.removeEventListener('seeking', onAgain); };
  }, [videoUrlKey]);   // a new video, not a new link (link refresh): a fresh link must not reset the end card
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const on = () => setNativeFs(true); const off = () => setNativeFs(false);
    el.addEventListener('webkitbeginfullscreen', on); el.addEventListener('webkitendfullscreen', off);
    return () => { el.removeEventListener('webkitbeginfullscreen', on); el.removeEventListener('webkitendfullscreen', off); };
  }, [videoUrl]);
  // No automatic advance: the next lesson starts only when the student taps "Play next lesson" (or Up Next),
  // so it always starts from a tap, the way every browser (iPad and iPhone included) lets a new video start.
  const replay = () => {
    setEnded(false);
    const v = videoRef.current;
    if (v) { v.currentTime = 0; setCurrentTime(0); }
    setIsPlaying(true);
  };
  useEffect(() => { const v = videoRef.current; if (v) v.playbackRate = hold ? 2 : playbackSpeed; }, [playbackSpeed, hold, mediaReady]);   // also once the media is ready: a speed chosen before it existed (e.g. a saved one) must still apply

  // ── Subtitles (1.21) ──────────────────────────────────────────────────────
  // Tracks come from the lesson data: video.subtitles = [{ lang, label, url, kind }], kind
  // 'subtitles' or 'captions' (English (CC) keeps its own key, 'en-cc'). With none, the
  // Subtitles control doesn't appear. Tracks are added once per player instance, after
  // the media is ready (mediaReady), by the effect below; never inside the load effect above.
  const subtitleOptions = useMemo(() => (Array.isArray(video && video.subtitles) ? video.subtitles : [])
    .filter((s) => s && s.url && s.lang)
    .map((s) => ({ ...s, kind: s.kind === 'captions' ? 'captions' : 'subtitles', key: s.kind === 'captions' ? s.lang + '-cc' : s.lang })), [video]);
  // Saved subtitles (1.22): start from the profile's preferredSubtitles, else Off. A lesson without
  // that language shows Off (activeSubtitleKey) without changing the saved value.
  const [subtitleKey, setSubtitleKey] = useState(() => (profile && typeof profile.preferredSubtitles === 'string' ? profile.preferredSubtitles : 'off'));
  const activeSubtitleKey = subtitleOptions.some((s) => s.key === subtitleKey) ? subtitleKey : 'off';
  // Saving (1.22) switches itself on: it only saves a field the profile already has (the sign the
  // backend supports it), so no request goes to an endpoint that doesn't exist yet. The in-memory
  // profile is updated too, so the next lesson starts with the choice.
  const savePreference = (field, value) => {
    if (!api || !profile || !Object.prototype.hasOwnProperty.call(profile, field)) return;
    if (profile[field] === value) return;   // unchanged: nothing to save (also keeps React's double-run in development quiet)
    profile[field] = value;
    try {
      const req = api.fetch('/profile/preferences', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      if (req && req.catch) req.catch(() => { /* a failed save changes nothing on screen */ });
    } catch (err) { /* a failed save changes nothing on screen */ }
  };
  const prefsReadyRef = useRef(false);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredPlaybackSpeed', playbackSpeed); }, [playbackSpeed]);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredSubtitles', subtitleKey); }, [subtitleKey]);
  useEffect(() => { prefsReadyRef.current = true; }, []);
  const [tracksReady, setTracksReady] = useState(0);
  const subsAddedFor = useRef(null);
  const [showSubtitleSheet, setShowSubtitleSheet] = useState(false);
  useEffect(() => {
    const player = shakaRef.current;
    if (!mediaReady || !player || !subtitleOptions.length || subsAddedFor.current === player) return;
    subsAddedFor.current = player;
    (async () => {
      for (const s of subtitleOptions) {
        try { await player.addTextTrackAsync(s.url, s.lang, s.kind === 'captions' ? 'caption' : 'subtitle', 'text/vtt', undefined, s.label); }
        catch (err) { /* one missing file must not block the others */ }
        if (shakaRef.current !== player) return;   // replaced by a new lesson's player
      }
      setTracksReady((n) => n + 1);
    })();
  }, [mediaReady, subtitleOptions]);
  // ── Request guard: the only way on from a stop. One load() of the current link at the remembered spot. ──────
  // Nothing happens on a link that has run out (useCDNToken brings a fresher one), or for another lesson (the
  // load effect builds a new player for it). The subtitle tracks are added again on the new load.
  const restartFromLink = () => {
    const guard = guardRef.current; const p = shakaRef.current; const url = videoUrlRef.current;
    if (!guard.stopped || !p || !url || String(url).split('?')[0] !== guard.key) return false;
    if (guard.clockOk && cdnTokenExpired(cdnTokenRef.current)) return false;
    guard.stopped = false; guard.failures = [];
    setStopped((n) => n + 1);   // the recovery effect re-reads the guard: not stopped → it stands down; a failed load arms it again
    resumedRef.current = true;   // the spot goes to load() as its start time; loadedmetadata must not seek elsewhere
    subsAddedFor.current = null; setTracksReady(0);
    const withOverlay = guard.pos >= 1;   // step 2's rule: a lesson never played reloads quietly
    guard.loading = true;
    setError(null); setIsLoading(!withOverlay); setLinkOverlay(withOverlay ? 'loading' : null);
    p.load(url, guard.pos > 0 ? guard.pos : undefined)
      .then(() => {
        if (shakaRef.current !== p || guard.stopped) return;   // stopped again meanwhile: the stop owns the state
        guard.loading = false;
        setError(null); setIsLoading(false);
        setLinkOverlay(withOverlay && !guard.playAfter ? 'ready' : null);
        if (guard.playAfter) { guard.playAfter = false; setIsPlaying(true); }
      })
      .catch((err) => {
        if (shakaRef.current !== p || guard.stopped || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        guard.loading = false;
        guard.stopped = true; guard.playAfter = false;   // still stopped; key and pos kept; no play without a new press
        setLinkOverlay('ready');   // the overlay, never the error screen (October 30, 2026)
        setIsLoading(false);
        setStopped((n) => n + 1);
      });
    return true;
  };
  restartRef.current = restartFromLink;
  // While stopped: back online or the page shown → one try; the timed ladder → one try per rung, then no more.
  useEffect(() => {
    const guard = guardRef.current;
    if (!stopped || !guard.stopped) return undefined;
    const again = () => { if (restartRef.current) restartRef.current(); };
    const onVisible = () => { if (document.visibilityState === 'visible') again(); };
    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', onVisible);
    let timer = null;
    if (guard.tries < CDN_GUARD_RETRY_SECONDS.length) {
      timer = window.setTimeout(() => { guard.tries += 1; again(); }, CDN_GUARD_RETRY_SECONDS[guard.tries] * 1000);
    }
    return () => { window.removeEventListener('online', again); document.removeEventListener('visibilitychange', onVisible); if (timer) window.clearTimeout(timer); };
  }, [stopped]);
  // A play press on a stopped player: one try, and play once it has loaded (the press is the gesture).
  useEffect(() => {
    if (!isPlaying) { guardRef.current.playAfter = false; return; }   // paused meanwhile: a later restart stays paused
    if (!guardRef.current.stopped) return;
    guardRef.current.tries = 0; guardRef.current.playAfter = true;
    if (restartRef.current) restartRef.current();
  }, [isPlaying]);
  // The overlay (step 2's rules): gone once the video plays another way; gone on a new lesson; and "Reloading…"
  // with no load in sight for CDN_GUARD_OVERLAY_WAIT_SECONDS becomes "Resume lesson" (the tap refreshes everything).
  useEffect(() => { if (isPlaying && linkOverlay && !resumingRef.current) setLinkOverlay(null); }, [isPlaying, linkOverlay]);
  useEffect(() => { setLinkOverlay(null); }, [videoUrlKey]);
  useEffect(() => {
    if (linkOverlay !== 'loading') return undefined;
    let timer = null;
    // Waits at least CDN_GUARD_OVERLAY_WAIT_SECONDS, and past the next ladder rung while one can still try (a link
    // that has run out cannot be tried, so then only the wait counts).
    const delay = () => {
      const guard = guardRef.current;
      const canTry = guard.tries < CDN_GUARD_RETRY_SECONDS.length && !(guard.clockOk && cdnTokenExpired(cdnTokenRef.current));
      return Math.max(CDN_GUARD_OVERLAY_WAIT_SECONDS, canTry ? CDN_GUARD_RETRY_SECONDS[guard.tries] + 5 : 0) * 1000;
    };
    const check = () => {
      const guard = guardRef.current;
      if (guard.loading) { timer = window.setTimeout(check, delay()); return; }   // a slow load: keep waiting
      if (guard.stopped) setLinkOverlay('ready');   // the tap takes it from here (October 30, 2026)
    };
    timer = window.setTimeout(check, delay());
    return () => window.clearTimeout(timer);
  }, [linkOverlay, stopped]);
  // Resume (October 20, 2026): the overlay may have said "Resume lesson" for hours. The tap re-establishes everything
  // at tap time instead of trusting the player prepared back then: 1. a fresh session token, 2. a fresh link into page
  // state (through the page's hook, so both players and the PDF links see it), 3. a reload from it at the spot (a new
  // DRM session whose license carries the fresh token, fresh buffers), 4. play. A play-then-pause inside the tap
  // captures the activation without playing the old stream; the overlay stays up ('resuming') until the reload plays.
  const continueFromLink = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const v = videoRef.current; const p = shakaRef.current; const guard = guardRef.current;
    if (!v || !p) return;
    resumingRef.current = true;
    const first = v.play(); if (first && first.catch) first.catch(() => {});
    v.pause();   // play-then-pause inside the tap: the activation is captured, nothing plays until the reload
    setLinkOverlay('resuming');
    const pos = v.currentTime > 0 ? v.currentTime : (guard.pos || 0);
    (async () => {
      try {
        // offline: nothing to refresh; the guard's own 'online' trigger reloads later
        if (navigator.onLine !== false) await api.refreshToken();
        if (typeof refreshLink === 'function') {
          const before = cdnTokenRef.current;
          const r = await refreshLink();
          // the fresh link reaches this player through a render and its [videoUrl] effect, which writes the token
          // the request filter sends (cdnTokenRef); wait for that write (normally one tick, at most 1 s)
          if (r === 'fresh') for (let i = 0; i < 40 && cdnTokenRef.current === before; i++) await new Promise((res) => setTimeout(res, 25));
        }
        if (shakaRef.current !== p) return;   // the lesson changed meanwhile
        if (guard.stopped || guard.loading) {   // stopped again while waiting, or a restart load already in flight
          guard.playAfter = true;                // (the fresh link's own): that load is the reload; play after it
          if (guard.stopped) {
            guard.tries = 0;
            const started = restartRef.current ? restartRef.current() : false;
            if (!started) { guard.playAfter = false; setLinkOverlay('ready'); }   // still no usable link: Resume lesson again
          }
          return;
        }
        const at = v.currentTime > 0 ? v.currentTime : pos;   // the spot now (the tap's play() may have moved on)
        guard.loading = true;
        resumedRef.current = true;   // the spot is load()'s start time; loadedmetadata must not seek elsewhere
        subsAddedFor.current = null; setTracksReady(0);   // the subtitle tracks are added again on the new load
        await p.load(videoUrlRef.current, at > 0 ? at : undefined);
        if (shakaRef.current !== p || guard.stopped) return;   // stopped again meanwhile: the stop owns the state
        guard.loading = false;
        setLinkOverlay(null); setError(null); setIsLoading(false);
        const again = v.play(); if (again && again.catch) again.catch(() => {});
        setIsPlaying(true);
      } catch (err) {
        if (shakaRef.current !== p || guard.stopped || (err && err.code === 7000 /* LOAD_INTERRUPTED */)) return;
        // the same state as a failed restart load: stopped, with the key and the spot kept, so the next link, press,
        // online, visibility or ladder rung tries again; no play without a new press
        guard.loading = false;
        guard.stopped = true; guard.key = String(videoUrlRef.current).split('?')[0]; guard.pos = v.currentTime > 0 ? v.currentTime : pos; guard.playAfter = false;
        setLinkOverlay('ready'); setIsLoading(false); setIsPlaying(false);   // back to Resume lesson, never the error screen (October 30, 2026)
        setStopped((n) => n + 1);
      } finally {
        resumingRef.current = false;
      }
    })();
  };
  useEffect(() => {
    const player = shakaRef.current;
    if (!player || !tracksReady) return;
    const opt = subtitleOptions.find((s) => s.key === subtitleKey);
    try {
      if (!opt) { player.setTextTrackVisibility(false); return; }
      const kind = opt.kind === 'captions' ? 'caption' : 'subtitle';
      const all = player.getTextTracks();
      const tr = all.find((t) => t.language === opt.lang && t.kind === kind && (!opt.label || t.label === opt.label))
        || all.find((t) => t.language === opt.lang && t.kind === kind);
      if (tr) player.selectTextTrack(tr);
      player.setTextTrackVisibility(true);
    } catch (err) { /* subtitles are optional: never disturb playback */ }
  }, [subtitleKey, tracksReady, subtitleOptions]);

  // Read duration/position from the PLAYER, not React state. `duration` only
  // populates on loadedmetadata and `currentTime` lags a timeupdate behind, so
  // seeking off React state silently no-ops before metadata lands — which is
  // exactly when someone taps skip on a fresh lesson.
  // Read duration/position from the ELEMENT, not React state. `duration` only
  // populates on loadedmetadata and `currentTime` lags a timeupdate behind, so
  // seeking off React state silently no-ops before metadata lands — exactly when
  // someone taps skip on a fresh lesson.
  const liveDuration = () => {
    const v = videoRef.current;
    const d = v ? v.duration : 0;
    return Number.isFinite(d) && d > 0 ? d : (totalSeconds || 0);
  };
  const livePosition = () => {
    const v = videoRef.current;
    const t = v ? v.currentTime : 0;
    return Number.isFinite(t) ? t : (currentTime || 0);
  };

  const seekTo = (clientX) => {
    const v = videoRef.current;
    const d = liveDuration();
    if (!progressRef.current || !v || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    v.currentTime = t;
    setCurrentTime(t);
  };

  // Drag on the bar: move the thumb only (no seek), and keep the controls up.
  const scrubTo = (clientX) => {
    const d = liveDuration();
    if (!progressRef.current || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    scrubRef.current = t;
    setScrubTime(t);
    bump();
  };

  // Release: one seek, to where the finger let go.
  const endScrub = () => {
    const t = scrubRef.current;
    scrubRef.current = null;
    setScrubTime(null);
    if (t === null) return;
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    setCurrentTime(t);
  };

  const skip = (s) => {
    const v = videoRef.current;
    if (!v) return;
    const d = liveDuration();
    const target = livePosition() + s;
    const t = Math.max(0, d > 0 ? Math.min(d - 0.25, target) : target);
    v.currentTime = t;
    setCurrentTime(t);
    bump();
  };

  // ── Lock-screen and headphone controls (1.24) ───────────────────────────
  // The Media Session API. Registered when this player starts playing, and kept after a pause so the
  // lock-screen play button still works; cleared when this player unmounts, if it is still the one
  // registered. Handlers go through a ref, so they always call this render's functions.
  const mediaSessionRef = useRef({});
  mediaSessionRef.current = {
    play: () => setIsPlaying(true),
    pause: () => setIsPlaying(false),
    skip: (s) => skip(s),
    seekTo: (t) => { const v = videoRef.current; if (v) { v.currentTime = t; setCurrentTime(t); } },
  };
  const mediaSessionOwner = useRef({});
  useEffect(() => {
    if (!isPlaying || typeof navigator === 'undefined' || !navigator.mediaSession) return;
    const ms = navigator.mediaSession;
    try {
      if (typeof window.MediaMetadata === 'function') ms.metadata = new window.MediaMetadata({ title: (video && video.title) || '', artist: (video && video.series && video.series.name) || '', album: 'RoastBrew' });
    } catch (err) { /* metadata is optional */ }
    const set = (a, h) => { try { ms.setActionHandler(a, h); } catch (err) { /* this action isn't supported here */ } };
    set('play', () => mediaSessionRef.current.play());
    set('pause', () => mediaSessionRef.current.pause());
    set('seekbackward', () => mediaSessionRef.current.skip(-10));
    set('seekforward', () => mediaSessionRef.current.skip(10));
    set('seekto', (d) => { if (d && typeof d.seekTime === 'number') mediaSessionRef.current.seekTo(d.seekTime); });
    window.__rbMediaSessionOwner = mediaSessionOwner.current;
  }, [isPlaying, video]);
  useEffect(() => () => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession || window.__rbMediaSessionOwner !== mediaSessionOwner.current) return;
    ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'].forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch (err) { /* not supported */ } });
    window.__rbMediaSessionOwner = null;
  }, []);

  // Double-tap to skip (touch). A second tap on the same side third within 300 ms skips
  // ±10 s, and further taps keep adding (the ripple shows the running total). A first tap
  // on a side waits 300 ms before showing or hiding the controls, so a second tap can cancel
  // that; a tap in the centre shows or hides them at once.
  const tapRef = useRef({ side: null, at: 0, total: 0, timer: null });
  const [ripple, setRipple] = useState(null);
  const rippleTimerRef = useRef(null);
  useEffect(() => () => { clearTimeout(tapRef.current.timer); clearTimeout(rippleTimerRef.current); }, []);
  const handleSurfaceTap = (e) => {
    const h = holdRef.current;
    clearTimeout(h.timer);
    if (h.active) { h.active = false; setHold(false); return; }   // releasing a hold is not a tap
    const el = e && e.currentTarget;
    const pt = e && e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : e;
    const tr = tapRef.current;
    if (!el || !pt || pt.clientX == null) { toggleControls(); return; }
    const r = el.getBoundingClientRect();
    const x = (pt.clientX - r.left) / r.width;
    const side = x < 1 / 3 ? 'back' : x > 2 / 3 ? 'fwd' : null;
    const now = Date.now();
    if (!side) { clearTimeout(tr.timer); tr.side = null; toggleControls(); return; }
    if (tr.side === side && now - tr.at < 300) {
      clearTimeout(tr.timer);
      tr.at = now;
      tr.total += SKIP_SECONDSPhone;
      skip(side === 'back' ? -SKIP_SECONDSPhone : SKIP_SECONDSPhone);
      try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ }   // haptic tick (Android; iPhones ignore it)
      setRipple({ side, total: tr.total });
      clearTimeout(rippleTimerRef.current);
      rippleTimerRef.current = setTimeout(() => setRipple(null), 700);
      return;
    }
    clearTimeout(tr.timer);
    tr.side = side; tr.at = now; tr.total = 0;
    tr.timer = setTimeout(() => { tr.side = null; toggleControls(); }, 300);
  };

  // ── iOS FULLSCREEN ────────────────────────────────────────────────────────
  // Two things were wrong before, both confirmed against Video.js's own
  // tech/html5.js source:
  //
  // 1. CASING. Video.js tests `typeof el.webkitEnterFullScreen === 'function'`
  //    — capital S in "FullScreen". I was calling `webkitEnterFullscreen` and
  //    gating on `webkitSupportsFullscreen`. Safari has exposed both spellings
  //    historically, but the capital-S one is what Video.js relies on, so try
  //    both rather than betting on either.
  //
  // 2. THE PRIMING SEQUENCE. iOS refuses programmatic fullscreen on a video that
  //    has not been primed. Video.js handles it: when the video is paused and
  //    networkState <= HAVE_METADATA it calls play(), then on a setTimeout(0)
  //    pauses and only THEN enters fullscreen — split across a tick, because
  //    doing both synchronously can trap older iOS in a play/pause loop. My
  //    version just called enter and gave up, which is exactly the "nothing
  //    happens" on a fresh lesson.
  //
  // So stop hand-rolling it: player.requestFullscreen() routes through the
  // tech's enterFullScreen() and inherits all of the above. The manual path
  // below is only a fallback if the Video.js player is not available.
  // ── FULLSCREEN ────────────────────────────────────────────────────────────
  // Simpler than the FairPlay page's version, because the platforms are simpler.
  // This page serves Android and narrow desktop windows, which support the
  // standard Fullscreen API on an ELEMENT — so the container goes fullscreen and
  // the custom controls stay visible and usable inside it.
  //
  // The video-element fallback (webkitEnterFullScreen, both spellings, plus the
  // paused/networkState priming dance Video.js performs) is kept for any WebKit
  // browser that somehow lands here, since that path needs it and costs nothing.
  //
  // NO screen.orientation.lock. The desktop Shaka page locks landscape on enter
  // and portrait on exit; on Android that fights whatever orientation the user is
  // actually holding, and on iOS the API does not exist at all so both calls sat
  // in empty catch blocks. Dropped on purpose.
  const nativeEnterName = (el) => {
    if (!el) return null;
    if (typeof el.webkitEnterFullScreen === 'function') return 'webkitEnterFullScreen';
    if (typeof el.webkitEnterFullscreen === 'function') return 'webkitEnterFullscreen';
    return null;
  };

  const goFullscreen = useCallback(() => {
    const el = videoRef.current;
    const box = containerRef.current;

    const inFs = !!(document.fullscreenElement || document.webkitFullscreenElement ||
                    (el && el.webkitDisplayingFullscreen));
    if (inFs) {
      if (el && typeof el.webkitExitFullScreen === 'function') { el.webkitExitFullScreen(); return; }
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }

    // Preferred: element fullscreen on the CONTAINER, so our controls come along.
    if (box && typeof box.requestFullscreen === 'function') {
      try { box.requestFullscreen().catch(() => {}); return; } catch (e) { /* fall through */ }
    }
    if (box && typeof box.webkitRequestFullscreen === 'function') {
      try { box.webkitRequestFullscreen(); return; } catch (e) { /* fall through */ }
    }

    // WebKit fallback: the platform player, with the priming sequence.
    if (!el) return;
    const method = nativeEnterName(el);
    if (!method) return;
    if (el.paused && el.networkState <= 1 /* HAVE_METADATA */) {
      const pr = el.play();
      if (pr && pr.catch) pr.catch(() => {});
      setTimeout(() => {
        el.pause();
        try { el[method](); } catch (e) { /* refused */ }
      }, 0);
    } else {
      try { el[method](); } catch (e) { /* refused */ }
    }
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', h);
    document.addEventListener('webkitfullscreenchange', h);
    const v = videoRef.current;
    const ib = () => setIsFullscreen(true); const ie = () => setIsFullscreen(false);
    if (v) { v.addEventListener('webkitbeginfullscreen', ib); v.addEventListener('webkitendfullscreen', ie); }
    return () => {
      document.removeEventListener('fullscreenchange', h);
      document.removeEventListener('webkitfullscreenchange', h);
      if (v) { v.removeEventListener('webkitbeginfullscreen', ib); v.removeEventListener('webkitendfullscreen', ie); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey]);


  // TOP BAR PILLS — the original mobile design, restored.
  // These are flex:1 pills spread across the bar, so the tap target is WIDE even
  // though it is not tall. The original set minHeight 32px and 9px text, which is
  // what made them fiddly; the layout was never the problem. Raised to 44px /
  // 11px and otherwise left as it was.
  const topBarBtn = (children, onClick, active = false, key = 'pill', disabled = false) => (
    <button
      disabled={disabled}
      onClick={!disabled && onClick ? once(key, onClick) : undefined}
      onTouchEnd={!disabled && onClick ? once(key, onClick) : undefined}
      style={{
        opacity: disabled ? 0.4 : 1,
        // flex:1 with a cap. With four pills flex:1 filled the bar naturally; with
        // two left, uncapped flex:1 stretched each to half the player width, which
        // reads as a banner rather than a button.
        flex: 1, maxWidth: '132px', minHeight: '44px', padding: '6px',
        background: active ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
        color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '600',
        fontFamily: '"DM Sans",sans-serif', backdropFilter: 'blur(10px)',
        WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
      }}>{children}</button>
  );

  // ── NO PiP BUTTON — BY DESIGN ─────────────────────────────────────────────
  // There used to be one here, and it was more trouble than it was worth on iOS.
  // Video.js is no help (its html5 tech implements PiP as a bare
  // el.requestPictureInPicture(), which does not exist on iOS Safari), Safari's
  // own webkitSetPresentationMode requires BOTH a live user gesture and a
  // currently-PLAYING video, and automatic PiP on page-leave is impossible —
  // WebKit only grants PiP inside a transient user activation, so requests from
  // unload / visibilitychange / pagehide report success while the window never
  // renders.
  //
  // The workaround is simply better: entering fullscreen hands off to Apple's
  // NATIVE player, which already has a PiP control, and fullscreen -> background
  // is the one automatic PiP transition WebKit does perform. So the user gets PiP
  // for free via the fullscreen button, with none of the gating to maintain.


  return (
    <div ref={containerRef} className="video-player-container"
      style={{
        position: 'relative', aspectRatio: '16/9', width: '100%',
        borderRadius: isFullscreen ? 0 : '10px', overflow: 'hidden', background: '#000',
      }}>
      {autoMuted && (soundPromptBig ? (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 22px) clamp(22px, 5vw, 34px)', borderRadius: '18px', border: 'none', background: '#111111', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 'clamp(16px, 2.4vw, 22px)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: 'clamp(34px, 7vw, 48px)', height: 'clamp(34px, 7vw, 48px)' }}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ) : (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 40, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '999px', border: 'none', background: 'rgba(0,0,0,0.72)', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ))}
      <div className="video-player-inner" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit' }}>

        {/* z0 — media */}
      {videoUrl && <video ref={videoRef} muted={isMuted} style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'contain',zIndex:0}}
          onLoadedMetadata={()=>{if(videoRef.current){setDuration(videoRef.current.duration);setIsLoading(false);setError(null);if(!resumedRef.current){const r=resumeFromRef.current;if(r>0&&r<videoRef.current.duration){videoRef.current.currentTime=r;setCurrentTime(r);}resumedRef.current=true;}}}}
          onTimeUpdate={()=>{if(videoRef.current&&!guardRef.current.stopped){const t=videoRef.current.currentTime;setCurrentTime(t);const d=videoRef.current.duration||duration||0;latestRef.current={pos:t,dur:d};heartbeatOnTime(t,d,videoRef.current.playbackRate,!videoRef.current.paused&&!videoRef.current.seeking);}}}
          onSeeking={()=>heartbeatOnTime.seeking()}
          onProgress={()=>{if(videoRef.current?.buffered.length>0)setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length-1));}}
          onWaiting={()=>setIsLoading(true)}
          onCanPlay={()=>{setIsLoading(false);setError(null);if(videoRef.current)videoRef.current.volume=isMuted?0:1;}}
          onEnded={()=>setIsPlaying(false)} onPlay={reportPlaying(setIsPlaying)} onPause={reportPlaying(setIsPlaying)}
          playsInline preload="metadata" />}
        {!videoUrl && <div style={{ position: 'absolute', inset: 0, background: '#0a0a0a' }} />}

        {/* z1 — cinematic vignette (mobile only) */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)',
        }} />

        {/* z2 — paused dim */}
        <div style={{ position: 'absolute', inset: 0, background: '#000', opacity: isPlaying ? 0 : 0.3, zIndex: 2, pointerEvents: 'none', transition: 'opacity 0.4s ease' }} />

        {/* z3 — spinner. pointerEvents NONE: an overlay above the controls without
            this is what made them unclickable in the first place. */}
        {isLoading && !showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.25)', borderTopColor: colors.accent, borderRadius: '50%', animation: 'rbspin 0.8s linear infinite' }} />
          </div>
        )}

        {/* z3 — error */}
        {showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'rgba(0,0,0,0.7)', zIndex: 3, pointerEvents: 'none', padding: '16px', textAlign: 'center' }}>
            <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
            <p style={{ margin: 0, color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>{error}</p>
          </div>
        )}
        <LinkOverlay state={linkOverlay} at={guardRef.current.pos} colors={colors} onContinue={continueFromLink} small />

        {/* z15 — TAP LAYER. Always interactive. iOS does not reliably bubble a
            click off a <video>, so the reveal tap needs a real element above it.
            Single logical handler via once(): touchend + synthesised click both
            firing is what made this toggle twice and appear dead. */}
        <div
          onTouchStart={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            clearTimeout(h.timer); h.active = false;
            if (!t || e.touches.length > 1) return;
            h.x = t.clientX; h.y = t.clientY;
            h.timer = setTimeout(() => { h.active = true; setHold(true); try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ } }, 450);
          }}
          onTouchMove={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            if (t && !h.active && Math.hypot(t.clientX - h.x, t.clientY - h.y) > 10) clearTimeout(h.timer);
          }}
          onTouchCancel={() => { const h = holdRef.current; clearTimeout(h.timer); if (h.active) { h.active = false; setHold(false); } }}
          onTouchEnd={once('tap', (e) => handleSurfaceTap(e))}
          onClick={once('tap', (e) => handleSurfaceTap(e))}
          style={{ position: 'absolute', inset: 0, zIndex: 15, pointerEvents: 'auto', background: 'transparent', touchAction: 'manipulation', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
        />
        {ripple && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, [ripple.side === 'back' ? 'left' : 'right']: 0, width: '38%', zIndex: 16, pointerEvents: 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', background: 'rgba(255,255,255,0.12)',
            borderRadius: ripple.side === 'back' ? '0 50% 50% 0 / 0 50% 50% 0' : '50% 0 0 50% / 50% 0 0 50%',
            color: '#fff', fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '14px', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            <span>{ripple.side === 'back' ? '«' : '»'}</span>
            <span>{ripple.side === 'back' ? '−' : '+'}{ripple.total} s</span>
          </div>
        )}
        {hold && (
          <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 17, pointerEvents: 'none',
            padding: '6px 12px', borderRadius: '999px', background: 'rgba(0,0,0,0.62)', color: '#fff',
            fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>
            2× ▸▸
          </div>
        )}

        {/* ── z16 CONTROLS — the original mobile three-band layout ──────────── */}

        {/* TOP: pills, space-evenly. CC removed (no caption tracks in the payload).
            Speed and Mute remain; PiP is reached via fullscreen (see above). */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, padding: '8px 10px',
            background: 'linear-gradient(to bottom,rgba(0,0,0,0.8) 0%,rgba(0,0,0,0.4) 70%,transparent 100%)',
            display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', gap: '8px',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          {/* Subtitles pill (1.21): shown only when this lesson has subtitle tracks. */}
          {subtitleOptions.length > 0 && topBarBtn(
            <><svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 12h4M13 12h4M7 16h10" /></svg><span>Subtitles</span></>,
            () => { setShowSubtitleSheet(true); bump(); }, showSubtitleSheet || activeSubtitleKey !== 'off', 'subs')}
          {topBarBtn(<span>{playbackSpeed === 1 ? '1x' : `${playbackSpeed}x`}</span>,
            () => { setShowSpeedSheet(true); bump(); }, showSpeedSheet, 'speed')}
          {topBarBtn(
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isMuted
                ? <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M23 9l-6 6M17 9l6 6" /></>
                : <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" /></>}
            </svg>, () => { setIsMuted(!isMuted); bump(); }, false, 'mute')}
        </div>

        {/* CENTRE: skip 10s / play / skip 10s — original SkipButton, top 45%. */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)',
            display: 'flex', alignItems: 'center', gap: '14px', zIndex: 16,
            opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          <SkipButtonPhone direction="back" onClick={once('skipback', () => skip(-SKIP_SECONDSPhone))} currentTier={currentTier} isMobile seconds={SKIP_SECONDSPhone} />
          <button aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            onTouchEnd={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            style={{
              width: '52px', height: '52px', borderRadius: '50%', border: 'none', flexShrink: 0,
              background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', cursor: 'pointer',
              animation: !isPlaying ? 'rbpulse 2.2s ease-in-out infinite' : 'none',
              WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
            }}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: !isPlaying ? '3px' : '0' }}>
              {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
            </svg>
          </button>
          <SkipButtonPhone direction="forward" onClick={once('skipfwd', () => skip(SKIP_SECONDSPhone))} currentTier={currentTier} isMobile seconds={SKIP_SECONDSPhone} />
        </div>

        {/* BOTTOM: progress + time + fullscreen (original composition). */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px',
            background: 'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.5) 50%,transparent 100%)',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          {/* The original was a bare 4px div — a 4px tall tap target. Same visual
              bar, wrapped in a 24px hit area so a thumb can actually land on it. */}
          <div ref={progressRef}
            onClick={(e) => { e.stopPropagation(); seekTo(e.clientX); }}
            onTouchStart={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchMove={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchEnd={(e) => { e.stopPropagation(); if (e.cancelable) e.preventDefault(); endScrub(); bump(); }}
            onTouchCancel={() => { scrubRef.current = null; setScrubTime(null); }}
            style={{ position: 'relative', height: '24px', display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '2px', touchAction: 'none' }}>
            <div style={{ position: 'relative', height: '4px', width: '100%', background: 'rgba(255,255,255,0.2)', borderRadius: '3px' }}>
              <div style={{ position: 'absolute', height: '100%', width: `${bufPct}%`, background: 'rgba(255,255,255,0.3)', borderRadius: '3px', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', height: '100%', width: `${pct}%`, borderRadius: '3px', pointerEvents: 'none', ...pStyles.played }} />
              <div style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%,-50%)', width: '12px', height: '12px', borderRadius: '50%', pointerEvents: 'none', ...pStyles.thumb }} />
            </div>
            {scrubTime !== null && (
              <div style={{ position: 'absolute', left: `${pct}%`, bottom: '26px', transform: 'translateX(-50%)', padding: '3px 8px', borderRadius: '6px', background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                {fmtTimePhone(scrubTime)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)', fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums' }}>
              {fmtTimePhone(scrubTime ?? currentTime)} / {fmtTimePhone(totalSeconds)}
            </span>
            <button aria-label="Fullscreen"
              onClick={once('fs', () => goFullscreen())} onTouchEnd={once('fs', () => goFullscreen())}
              style={{ width: '44px', height: '44px', padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isFullscreen
                  ? <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" />
                  : <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />}
              </svg>
            </button>
          </div>
        </div>

        <style>{`
          @keyframes rbspin { to { transform: rotate(360deg); } }
          @keyframes rbpulse { 0%,100% { transform: scale(1); box-shadow: 0 8px 32px rgba(0,0,0,0.4); } 50% { transform: scale(1.06); box-shadow: 0 10px 40px rgba(0,0,0,0.5); } }
          @keyframes rbsheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .video-js { background-color: transparent !important; }
          .video-js video, .video-js .vjs-tech { object-fit: contain; }
          .video-js .vjs-control-bar, .video-js .vjs-big-play-button, .video-js .vjs-loading-spinner,
          .video-js .vjs-error-display, .video-js .vjs-poster, .video-js .vjs-text-track-display { display: none !important; }
        `}</style>
      </div>

      {/* ── SPEED SHEET ───────────────────────────────────────────────────────
          Rises from the BOTTOM OF THE SCREEN. position:fixed, rendered as a
          SIBLING of the player box rather than inside it.
          WHY: the original menu was position:absolute with bottom:100% inside the
          TOP bar, so it drew above the top of the player — outside the container's
          overflow:hidden — and was clipped away. It looked like it opened and
          could never be tapped. A fixed-position sheet cannot be clipped by any
          ancestor, and full-width 54px rows are a far bigger target than a
          floating 120px menu over a ~190px-tall video. */}
      {showSpeedSheet && (
        <div
          onClick={once('sheetbg', () => setShowSpeedSheet(false))}
          onTouchEnd={once('sheetbg', () => setShowSpeedSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.5)' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
            style={{
              background: colors.cardBg || '#141414',
              borderTopLeftRadius: '18px', borderTopRightRadius: '18px',
              padding: '10px 12px calc(20px + env(safe-area-inset-bottom))',
              borderTop: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)'}`,
              boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
              animation: 'rbsheet 0.22s cubic-bezier(0.4,0,0.2,1)',
            }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: colors.muted, opacity: 0.5, margin: '4px auto 14px' }} />
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Playback speed
            </div>
            {PLAYBACK_SPEEDSPhone.map((spd) => (
              <button key={spd}
                onClick={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                onTouchEnd={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '54px', padding: '0 16px',
                  background: playbackSpeed === spd ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: playbackSpeed === spd ? colors.accent : colors.text,
                  fontSize: '16px', fontWeight: playbackSpeed === spd ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{spd === 1 ? 'Normal' : `${spd}x`}</span>
                {playbackSpeed === spd && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* End of lesson (1.23): Up next / Unlock / Lesson complete, over the video. */}
      {ended && endCardEnabled && !nativeFs && <EndOfLessonCard colors={colors} upNext={upNext} upNextLocked={upNextLocked} info={endCard} onUpNext={onUpNext} onUnlock={onUnlock} onReplay={replay} />}
      {/* Subtitles sheet (1.21): built like the speed sheet. */}
      {showSubtitleSheet && (
        <div
          onClick={once('subbg', () => setShowSubtitleSheet(false))}
          onTouchEnd={once('subbg', () => setShowSubtitleSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.5)' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()} role="dialog" aria-label="Subtitles"
            style={{
              background: colors.cardBg || '#141414',
              borderTopLeftRadius: '18px', borderTopRightRadius: '18px',
              padding: '10px 12px calc(20px + env(safe-area-inset-bottom))',
              borderTop: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)'}`,
              boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
            }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: colors.muted, opacity: 0.5, margin: '4px auto 14px' }} />
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Subtitles
            </div>
            {[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((s) => (
              <button key={s.key} role="menuitemradio" aria-checked={activeSubtitleKey === s.key}
                onClick={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                onTouchEnd={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '54px', padding: '0 16px',
                  background: activeSubtitleKey === s.key ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: activeSubtitleKey === s.key ? colors.accent : colors.text,
                  fontSize: '16px', fontWeight: activeSubtitleKey === s.key ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif', textAlign: 'left',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{s.label}</span>
                {activeSubtitleKey === s.key && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
            <div style={{ fontSize: '12px', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '10px 16px 0' }}>
              Only languages available for this lesson are listed.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════ The chooser ════════════════
export default function WidevinePlayer({ device, ...props }) {
  if (device === 'tablet') return <MobilePlayerTablet {...props} />;
  if (device === 'phone') return <MobilePlayerPhone {...props} />;
  return <VideoPlayer {...props} />;
}
